import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList, ReferenceDot } from 'recharts';
import MobileOrderCard from './components/orders/MobileOrderCard.js';
import CategoryTabs from './components/orders/CategoryTabs';
import StockAdjustButton from './components/stock/StockAdjustButton';
import SettingsView from './components/SettingsView';
import CustomerOrderHistoryModal from './components/CustomerOrderHistoryModal';
import PendingWooOrdersModal from './components/PendingWooOrdersModal';
import ProductImagePicker from './components/ProductImagePicker';
import CRMInvoice from './components/CRMInvoice';
import NetRevenueSparkline from './components/ui/NetRevenueSparkline';
import BottleSizesHorizontalChart from './components/ui/BottleSizesHorizontalChart';
import TopCustomersFunnel from './components/ui/TopCustomersFunnel';
import TopProductsGroupedChart from './components/ui/TopProductsGroupedChart';
import { useAuth } from './components/AuthProvider';
import { useLanguage } from './contexts/LanguageContext';
import Login from './pages/login';
import { usePushNotifications } from './hooks/usePushNotifications';
import { supabaseTracked as supabase, supabaseTracked } from './lib/api/supabaseWithTracking';
import { lilacvalleyClient } from './lib/api/lilacvalleyClient';
import { testSupabaseConnection } from './utils/testSupabaseConnection';
import {
    safeCoerceNumber,
    formatCurrency,
    getCurrencySymbol,
    getRecentMonths,
    monthLabel,
    isCountableOrderStatus,
    exportJson,
    exportCsv,
    generateColorVariants
} from './utils/helpers';

import {
    ICONS_MAP,
    ORDER_STATUS_FLOW,
    FOCUSED_INPUT_STYLE,
    DEFAULT_SETTINGS,
    DEFAULT_GENERAL_SETTINGS,
    DEFAULT_STOCK_SETTINGS,
    TAILWIND_COLORS,
    CUSTOM_ICONS,
    COLOR_PALETTE
} from './utils/constants';

const getCustomerRank = (customer, orders, t = (s) => s, primaryColor = 'indigo') => {
    if (!customer) return { type: 'None', text: '', color: 'gray' };
    const completedOrdersCount = (orders || []).filter(order => order.customerid === customer.id && order.status === 'completed').length;
    if (completedOrdersCount === 0) return { type: 'New', text: t('newRank') || 'New', color: 'blue' };
    if (completedOrdersCount >= 1 && completedOrdersCount <= 2) return { type: 'Returning', text: t('returningRank') || 'Returning', color: 'green' };
    if (completedOrdersCount >= 3 && completedOrdersCount <= 5) return { type: 'Regular', text: t('regularRank') || 'Regular', color: primaryColor };
    if (completedOrdersCount >= 6 && completedOrdersCount <= 10) return { type: 'VIP', text: t('vipRank') || 'VIP', color: 'purple' };
    const eliteText = t('eliteRank');
    return { type: 'Elite', text: eliteText && eliteText !== 'eliteRank' ? eliteText : 'Elite', color: 'pink' };
};

// Reusable AdminOnly component to wrap admin-only JSX
const AdminOnly = ({ children }) => {
    const { isAdmin } = useAuth();
    return isAdmin ? children : null;
};

// Helper: format currency short (e.g., 12.3k)
const formatCurrencyShort = (num) => {
    if (Math.abs(num) >= 1000) {
        return (num / 1000).toFixed(1) + 'k';
    }
    return num.toFixed(0);
};

// Helper: calculate per tick width for chart labels
// Helper: normalize string for searching (Arabic-aware)
const normalizeSearch = (str) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .trim()
        .replace(/[أإآ]/g, 'ا')
        .replace(/ة/g, 'ه')
        .replace(/ى/g, 'ي')
        .replace(/ـ/g, '')
        .replace(/[\u064B-\u065F]/g, '');
};

const calcPerTickWidth = (chartWidth, tickCount) => {
    return chartWidth / tickCount;
};

// Initial data structure
// Load branding from localStorage to prevent flash of default values
const getInitialData = () => {
    const storedLogoUrl = localStorage.getItem('logoUrl') || '';
    const storedPrimaryColor = localStorage.getItem('primaryColor') || 'indigo';
    const storedStoreName = localStorage.getItem('storeName') || '';

    return {
        customers: [],
        products: [],
        orders: [],
        vendors: [],
        bottles: [{ id: 'tester', size: 'Tester', gender: 'Testers', actualstock: 50, totalcostbottle: 0.05, totalordered: 0, imageurl: '', oilperbottle: 5 },
        { id: '50mlforher', size: '50ml', gender: 'For Her', actualstock: 100, totalcostbottle: 0.85, totalordered: 0, imageurl: '', oilperbottle: 16 },
        { id: '50mlforhim', size: '50ml', gender: 'For Him', actualstock: 100, totalcostbottle: 0.85, totalordered: 0, imageurl: '', oilperbottle: 16 },
        { id: '30mlforher', size: '30ml', gender: 'For Her', actualstock: 100, totalcostbottle: 0.65, totalordered: 0, imageurl: '', oilperbottle: 10 },
        { id: '30mlforhim', size: '30ml', gender: 'For Him', actualstock: 100, totalcostbottle: 0.65, totalordered: 0, imageurl: '', oilperbottle: 10 },
        { id: '20mlpockets', size: '20ml', gender: 'Pockets', actualstock: 100, totalcostbottle: 0.5, totalordered: 0, imageurl: '', oilperbottle: 5 },
        ],
        oils: [{
            oil: 'Oil A',
            price: 0.5,
            actualstock: 5000,
            totalordered: 0,
            oiltype: 'Women',
            vendor_id: null,
            oil_per_5ml: 1,
            oil_per_20ml: 5,
            oil_per_30ml: 9,
            oil_per_50ml: 15,
            oil_per_100ml: 30
        },
        {
            oil: 'Oil B',
            price: 0.45,
            actualstock: 7500,
            totalordered: 0,
            oiltype: 'Men',
            vendor_id: null,
            oil_per_5ml: 1,
            oil_per_20ml: 5,
            oil_per_30ml: 9,
            oil_per_50ml: 15,
            oil_per_100ml: 30
        },
        {
            oil: 'Oil C',
            price: 0.6,
            actualstock: 3000,
            totalordered: 0,
            oiltype: 'Unisex',
            vendor_id: null,
            oil_per_5ml: 1,
            oil_per_20ml: 5,
            oil_per_30ml: 9,
            oil_per_50ml: 15,
            oil_per_100ml: 30
        }
        ],
        returnedPool: { 'Product A 50ml For Her': 5, 'Product B 30ml For Him': 2 },
        shipping: [{ id: 's1', state: 'CA', city: 'Los Angeles', fees: 5.00 }, { id: 's2', state: 'CA', city: 'San Diego', fees: 6.50 }],
        settings: {
            logoUrl: storedLogoUrl,
            primaryColor: storedPrimaryColor,
            statusIcons: { ordered: 'ordered', shipped: 'shipped', delivered: 'delivered', completed: 'completed', cancelled: 'cancelled', returned: 'returned', deleted: 'trash', dashboard: 'dashboard', stock: 'stock', customers: 'customers', settings: 'settings', add: 'add', back: 'back', advance: 'advance', delete: 'trash', warning: 'warning', edit: 'edit', save: 'save', close: 'close', upload: 'upload', previous: 'back' },
            statusColors: { ordered: 'blue', shipped: 'purple', delivered: 'yellow', completed: 'green', cancelled: 'red', returned: 'orange', deleted: 'gray' },
        },
        adsLedger: {},
        stockSettings: {
            oilCategories: ["For Him", "For Her", "Unisex"],
            bottleCategories: ["For Him", "For Her", "Testers"],
            bottleVariants: {
                male: ["30ml", "50ml", "100ml", "tester"],
                female: ["30ml", "50ml", "100ml"],
                pockets: ["20ml"],
                testers: ["tester"]
            },

            lowStockThresholds: {
                oils: 50,
                bottles: 10
            },
            inventoryLabels: {
                bottles: "Bottles",
                oils: "Oils"
            }
        },
        generalSettings: {
            storeName: storedStoreName,
            currency: "GBP"
        }
    };
};

const initialData = getInitialData();

// Custom X-axis tick component for charts with consistent 45° angled labels
const CustomXAxisTick = ({ x, y, payload, maxWidth, fontSize, angle = -45, formatter }) => {
    // Use formatter if provided, otherwise use raw value
    const text = formatter ? formatter(payload.value) : payload.value;

    // Always use angled labels for consistency
    const actualAngle = angle;
    const actualTextAnchor = 'end';

    // Use the exact fontSize — no floor minimum so callers control size precisely
    const responsiveFontSize = fontSize;

    // For angled labels, wrap text if needed
    const effectiveMaxWidth = maxWidth || 999;
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const testWidth = testLine.length * (responsiveFontSize * 0.6);
        if (testWidth > effectiveMaxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
        } else {
            currentLine = testLine;
        }
    });
    if (currentLine) lines.push(currentLine);

    // Increase line height for better readability
    const lineHeight = responsiveFontSize * 1.3;

    return (
        <g>
            {lines.map((line, i) => (
                <text
                    key={i}
                    x={x}
                    y={y + i * lineHeight}
                    textAnchor={actualTextAnchor}
                    fontSize={responsiveFontSize}
                    fontWeight={600}
                    fill="#374151"
                    transform={`rotate(${actualAngle} ${x} ${y + i * lineHeight})`}
                >
                    {line}
                </text>
            ))}
        </g>
    );
};

// --- SVG Icons ---

const Icon = ({ path, className = 'w-6 h-6' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
        <path d={path} />
    </svg>
);

// --- UI Components ---

const Badge = ({ children, color, className }) => (<span className={`px-2 py-1 text-xs font-medium rounded-full ${color} ${className}`}>{children}</span>);
const Card = ({ children, className }) => (<div className={`bg-white rounded-lg shadow-md p-4 mb-4 ${className}`}>{children}</div>);

const Button = ({ children, onClick, color = 'indigo', className = '', type = 'button', disabled = false, size = 'md', tooltip }) => {
    const pColor = color === 'primary' ? 'indigo' : color; // Fallback if handled elsewhere
    const colorClasses = `bg-${pColor}-600 hover:bg-${pColor}-700 focus:ring-${pColor}-500 text-white`;

    const grayClasses = 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400';
    const finalClasses = pColor === 'gray' ? grayClasses : colorClasses;

    const sizeClasses = size === 'md' ? 'px-4 py-2' : 'px-3 py-1';
    return (
        <button type={type} onClick={onClick} disabled={disabled}
            className={`inline-flex items-center justify-center gap-2 ${sizeClasses} text-sm font-semibold rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${finalClasses} ${className} relative group`}
        >
            {children}
            {tooltip && <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block w-max bg-gray-700 text-white text-xs rounded py-1 px-2 z-10">{tooltip}</span>}
        </button>
    );
};
const IconButton = ({ icon, onClick, color, className, tooltip }) => {
    // Map color names to explicit Tailwind classes (Tailwind doesn't support dynamic class names)
    const colorMap = {
        'red': 'text-red-600 hover:text-red-700',
        'blue': 'text-blue-600 hover:text-blue-700',
        'green': 'text-green-600 hover:text-green-700',
        'yellow': 'text-yellow-600 hover:text-yellow-700',
        'purple': 'text-purple-600 hover:text-purple-700',
        'indigo': 'text-indigo-600 hover:text-indigo-700',
        'gray': 'text-gray-600 hover:text-gray-700',
        'orange': 'text-orange-600 hover:text-orange-700',
        'violet': 'text-violet-600 hover:text-violet-700',
        'pink': 'text-pink-600 hover:text-pink-700',
    };
    // Fallback allows for dynamic primary color string
    const colorClasses = colorMap[color] || `text-${color}-600 hover:text-${color}-700`;

    return (
        <button onClick={onClick} className={`p-2 rounded-full hover:bg-gray-200 transition-colors relative group ${className}`}>
            {/* Use ICONS_MAP for path lookup */}
            <Icon path={ICONS_MAP[icon]} className={`w-5 h-5 ${colorClasses}`} />
            {tooltip && <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block w-max bg-gray-700 text-white text-xs rounded py-1 px-2 z-[9999] pointer-events-none">{tooltip}</span>}
        </button>
    );
};

// --- View Components ---
function OrderDetailsModal({ order, customer, orders, onClose, products, onUpdate, returnedPool, generalSettings, onCustomerClick, shippingFees, settings }) {
    const { t } = useLanguage();
    const primaryColor = settings?.primaryColor || 'indigo';

    const getCustomerRank = (customer, orders) => {
        if (!customer) return { text: '', color: 'gray' };
        const completedOrdersCount = orders.filter(order => order.customerid === customer.id && order.status === 'completed').length;
        if (completedOrdersCount === 0) return { text: t('newRank'), color: 'blue' };
        if (completedOrdersCount >= 1 && completedOrdersCount <= 2) return { text: t('returningRank'), color: 'green' };
        if (completedOrdersCount >= 3 && completedOrdersCount <= 5) return { text: t('regularRank'), color: 'indigo' };
        if (completedOrdersCount >= 6 && completedOrdersCount <= 10) return { text: t('vipRank'), color: 'purple' };
        return { text: t('eliteRank', { count: completedOrdersCount }), color: 'pink' };
    };

    const [isEditing, setIsEditing] = useState(false);
    const [showInvoice, setShowInvoice] = useState(false);
    // CRITICAL FIX: Ensure a deep copy is used to prevent state mutations before save
    const [editedItems, setEditedItems] = useState(JSON.parse(JSON.stringify(order.items || [])));
    const [editedTotal, setEditedTotal] = useState(order.total);
    const [editedPayment, setEditedPayment] = useState(order.payment);
    const [editedDiscount, setEditedDiscount] = useState(safeCoerceNumber(order.discount));
    const [editedDate, setEditedDate] = useState(() => order.date ? new Date(order.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
    const [editedNotes, setEditedNotes] = useState(() => {
        const n = order.notes || '';
        return n.replace(/\[(?:WooCommerce|Lilacvalley) Order ID: #?\d+\]/gi, '').trim();
    });

    const wooOrderIdFromNotes = order.woo_order_id || order.notes?.match(/\[(?:WooCommerce|Lilacvalley) Order ID: #?(\d+)\]/i)?.[1];
    const wooOrderId = wooOrderIdFromNotes || order.woo_order_id || order.wooOrderData?.id;
    const [editedAttachment, setEditedAttachment] = useState(order.attachment || '');
    const [isUploading, setIsUploading] = useState(false);

    // Searchable dropdown state
    const [itemSearches, setItemSearches] = useState(order.items?.map(item => item.pid || '') || []);
    const [dropdownOpens, setDropdownOpens] = useState(new Array(order.items?.length || 0).fill(false));

    const availableProducts = useMemo(() => products.sort((a, b) => a.product.localeCompare(b.product)), [products]);

    const prodMap = useMemo(() => new Map(products.map(p => [p.product, p])), [products]);

    const handleItemChange = (index, field, value) => {
        const newItems = [...editedItems];
        newItems[index][field] = value;
        setEditedItems(newItems);
    };

    const handleRemoveItem = (index) => {
        setEditedItems(editedItems.filter((_, i) => i !== index));
    };


    const handleSave = () => {
        onUpdate(order.orderid, {
            ...order,
            items: editedItems.filter(item => item.pid), // filter out empty lines
            total: safeCoerceNumber(editedTotal),
            payment: editedPayment,
            discount: safeCoerceNumber(editedDiscount),
            date: new Date(editedDate).toISOString(),
            notes: editedNotes,
            attachment: editedAttachment
        });
        setIsEditing(false);
    };

    const handleCancel = () => {
        setIsEditing(false);
        // Reset local state from the original order data
        setEditedItems(JSON.parse(JSON.stringify(order.items || [])));
        setEditedTotal(order.total);
        setEditedPayment(order.payment);
        setEditedDiscount(safeCoerceNumber(order.discount));
        setEditedDate(order.date ? new Date(order.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
        setEditedNotes(order.notes || '');
        setEditedAttachment(order.attachment || '');
        setItemSearches(order.items?.map(item => item.pid || '') || []);
        setDropdownOpens(new Array(order.items?.length || 0).fill(false));
    }

    const subtotal = editedItems.reduce((sum, item) => {
        const product = prodMap.get(item.pid);
        return sum + (product ? safeCoerceNumber(product.price) * safeCoerceNumber(item.qty) : 0);
    }, 0);

    const netTotal = subtotal - safeCoerceNumber(editedDiscount);

    useEffect(() => {
        if (!isEditing) {
            setEditedTotal(order.total);
            setEditedDiscount(order.discount);
            setEditedNotes((order.notes || '').replace(/\[(?:WooCommerce|Lilacvalley) Order ID: #?\d+\]/gi, '').trim());
        } else if (order.discount === undefined || order.discount === null || safeCoerceNumber(order.discount) === 0) {
            // Suggest a total based on subtotal - discount if discount was not manually set in the database
            setEditedTotal(netTotal.toFixed(2));
        }
    }, [isEditing, order.total, order.discount, netTotal]);

    const fallbackImage = `https://placehold.co/40x40/4f46e5/ffffff?text=LV`;

    if (!order) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-[1.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex justify-between items-start p-4 sm:p-6 bg-gray-50 border-b border-gray-100 gap-2">
                    <div className="flex gap-3 items-center min-w-0">
                        <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-${primaryColor}-100 flex items-center justify-center text-${primaryColor}-600 text-lg sm:text-2xl font-bold flex-shrink-0 cursor-pointer`}
                            onClick={() => { if (customer && onCustomerClick) onCustomerClick(customer); }}>
                            {customer?.name ? customer.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div className="min-w-0">
                            <h2 className={`text-base sm:text-xl font-bold text-gray-900 cursor-pointer hover:text-${primaryColor}-600 transition-colors truncate`}
                                onClick={() => { if (customer && onCustomerClick) onCustomerClick(customer); }}>
                                {customer?.name || 'Unknown Customer'}
                            </h2>
                            <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="text-xs font-medium text-gray-500">{t('orderId')} {String(order.orderid || 'N/A').replace(/^#/, '')}</span>
                                <span className="text-gray-300">•</span>
                                {(() => {
                                    const statusBadgeColors = { ordered: { bg: '#DBEAFE', text: '#1E40AF', border: '#BFDBFE' }, shipped: { bg: '#EDE9FE', text: '#5B21B6', border: '#DDD6FE' }, delivered: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' }, completed: { bg: '#D1FAE5', text: '#065F46', border: '#A7F3D0' }, cancelled: { bg: '#FEE2E2', text: '#991B1B', border: '#FECACA' }, returned: { bg: '#FFEDD5', text: '#9A3412', border: '#FED7AA' } };
                                    const c = statusBadgeColors[order.status] || { bg: '#F3F4F6', text: '#374151', border: '#E5E7EB' };
                                    return <span className="capitalize text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}` }}>{t(order.status) || order.status}</span>;
                                })()}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                            onClick={() => setShowInvoice(true)}
                            className={`flex items-center gap-1 bg-${primaryColor}-50 hover:bg-${primaryColor}-100 text-${primaryColor}-700 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold transition-colors`}
                            title={t('generateInvoice')}
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> {t('viewInvoice')}
                        </button>
                        {!isEditing && (
                            <button onClick={() => setIsEditing(true)} className="flex items-center gap-1 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold transition-colors">
                                <Icon path={ICONS_MAP['edit']} className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t('editOrder')}</span>
                            </button>
                        )}
                        <IconButton icon="close" color="gray" onClick={onClose} tooltip={t('close')} />
                    </div>
                </div>
                <div className="p-4 sm:p-6 overflow-y-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6 p-3 sm:p-5 border border-gray-100 rounded-2xl bg-white shadow-sm">
                        {isEditing ? (
                            <>
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">{t('payment')}</label>
                                    <select value={editedPayment} onChange={e => setEditedPayment(e.target.value)} className={`w-full p-2 border border-gray-200 rounded-xl bg-gray-50 ${FOCUSED_INPUT_STYLE}`}>
                                        <option>{t('cashPayment')}</option><option>{t('visaPayment')}</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">{t('total')} ({getCurrencySymbol(generalSettings.currency)})</label>
                                    <input type="number" value={editedTotal} onChange={e => setEditedTotal(e.target.value)} className={`w-full p-2 border border-gray-200 rounded-xl bg-gray-50 font-bold text-indigo-600 ${FOCUSED_INPUT_STYLE}`} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">{t('discount')}</label>
                                    <input type="number" value={editedDiscount} onChange={e => setEditedDiscount(safeCoerceNumber(e.target.value))} className={`w-full p-2 border border-gray-200 rounded-xl bg-gray-50 ${FOCUSED_INPUT_STYLE}`} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">{t('date')}</label>
                                    <input type="date" value={editedDate} onChange={e => setEditedDate(e.target.value)} className={`w-full p-2 border border-gray-200 rounded-xl bg-gray-50 ${FOCUSED_INPUT_STYLE}`} />
                                </div>
                            </>
                        ) : (
                            <>
                                <div><p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('payment')}</p><p className="font-semibold text-gray-900 text-base sm:text-lg">{order.payment === 'Visa' ? t('visaPayment') : t('cashPayment')}</p></div>
                                <div><p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('total')}</p><p className={`font-bold text-${primaryColor}-600 text-base sm:text-lg`}>{formatCurrency(order.total, generalSettings.currency)}</p></div>
                                <div><p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('discount')}</p><p className="font-bold text-red-500 text-base sm:text-lg">- {formatCurrency(order.discount, generalSettings.currency)}</p></div>
                                <div><p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('date')}</p><p className="font-semibold text-gray-900 text-sm sm:text-lg">{order.date ? new Date(order.date).toLocaleDateString() : 'N/A'}</p></div>
                            </>
                        )}
                    </div>
                    {/* New Row for Location Details and Notes */}
                    <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-5 border border-gray-100 rounded-2xl bg-white shadow-sm flex flex-col gap-4">
                            <div className="flex items-start gap-3">
                                <div className={`p-2 bg-${primaryColor}-50 rounded-lg text-${primaryColor}-500`}>
                                    <Icon path={ICONS_MAP['location'] || ICONS_MAP['edit']} className="w-6 h-6" />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('shippingDetails')}</p>
                                    <p className="font-semibold text-gray-900 text-lg">{customer?.state} - {customer?.city}</p>
                                    {customer && <p className="text-sm text-gray-500 mt-1">Fee: {formatCurrency(safeCoerceNumber(shippingFees?.find(s => s.state === customer.state && s.city === customer.city)?.fees) || 0, generalSettings.currency)}</p>}
                                </div>
                            </div>
                            <div className="h-px bg-gray-100 w-full my-1"></div>
                            <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('customerRank')}</p>
                                {(() => {
                                    const r = getCustomerRank(customer, orders, t, primaryColor);
                                    return <span className={`bg-${r.color}-50 text-${r.color}-700 border border-${r.color}-200 font-bold px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 text-sm`}>
                                        {r.text}
                                    </span>;
                                })()}
                            </div>
                        </div>
                        {isEditing ? (
                            <div className="p-5 border border-yellow-100 rounded-2xl bg-yellow-50/50 space-y-4 shadow-sm">
                                <div>
                                    <label className="text-xs font-semibold text-yellow-800 uppercase tracking-wider block mb-1">{t('orderNotes')}</label>
                                    <textarea value={editedNotes} onChange={e => setEditedNotes(e.target.value)} rows="3" className="w-full p-3 border border-yellow-200 rounded-xl bg-white/80 focus:ring-yellow-400 focus:border-yellow-400" placeholder={t('addNotesPlaceholder')}></textarea>
                                    {wooOrderId && (
                                        <div className="mt-2 text-[11px] font-bold text-yellow-700 bg-yellow-100/50 px-3 py-1.5 rounded-lg border border-yellow-200/50 inline-block">
                                            Lilacvalley Order ID: #{wooOrderId}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-yellow-800 uppercase tracking-wider mb-2">{t('attachmentLabel')}</label>
                                    <div className="flex gap-2 flex-wrap">
                                        <label className="cursor-pointer bg-white border border-yellow-200 px-4 py-2 rounded-xl font-medium text-sm text-yellow-800 hover:bg-yellow-50 flex items-center gap-2 shadow-sm transition-colors">
                                            {isUploading ? t('loading') : <><Icon path={ICONS_MAP['export']} className="w-4 h-4 text-yellow-400" /> {t('chooseTakePhoto')}</>}
                                            <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                setIsUploading(true);
                                                try {
                                                    const ext = file.name.split('.').pop();
                                                    const path = `${Date.now()}-${Math.random().toString(36).substring(2, 7)}.${ext}`;
                                                    const { error } = await supabaseTracked.storage.from('attachment').upload(path, file);
                                                    if (error) throw error;
                                                    const { data } = supabaseTracked.storage.from('attachment').getPublicUrl(path);
                                                    setEditedAttachment(data.publicUrl);
                                                } catch (err) { alert('Upload failed: ' + err.message); }
                                                finally { setIsUploading(false); }
                                            }} />
                                        </label>
                                        {editedAttachment && (
                                            <button type="button" onClick={() => setEditedAttachment('')} className="bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 px-3 py-2 rounded-xl text-sm font-medium transition-colors shadow-sm">{t('remove')}</button>
                                        )}
                                    </div>
                                    {editedAttachment && (
                                        <a href={editedAttachment} target="_blank" rel="noopener noreferrer" className={`mt-3 text-sm font-medium text-${primaryColor}-700 hover:text-${primaryColor}-900 inline-flex items-center gap-1.5 bg-${primaryColor}-50 px-3 py-1.5 rounded-lg border border-${primaryColor}-100 transition-colors w-fit`}>
                                            <Icon path={ICONS_MAP['export']} className="w-4 h-4" /> {t('viewAttachment')}
                                        </a>
                                    )}
                                </div>
                            </div>
                        ) : (order.notes || order.attachment) ? (
                            <div className="p-5 border border-yellow-100 rounded-2xl bg-yellow-50/50 shadow-sm flex flex-col gap-4">
                                {editedNotes && (
                                    <div>
                                        <p className="text-xs font-semibold text-yellow-800 uppercase tracking-wider mb-1">{t('orderNotes')}</p>
                                        <div className="bg-white/80 p-3 rounded-xl border border-yellow-200 text-gray-800 text-sm whitespace-pre-wrap">{editedNotes}</div>
                                    </div>
                                )}
                                {wooOrderId && (
                                    <div className="text-[11px] font-bold text-yellow-800 bg-white/60 px-3 py-2 rounded-xl border border-yellow-200 inline-block self-start">
                                        Lilacvalley Order ID: #{wooOrderId}
                                    </div>
                                )}
                                {order.attachment && (
                                    <div>
                                        <p className="text-xs font-semibold text-yellow-800 uppercase tracking-wider mb-2">{t('attachmentLabel')}</p>
                                        <a href={order.attachment} target="_blank" rel="noopener noreferrer" className={`text-sm font-medium text-${primaryColor}-700 hover:text-${primaryColor}-900 inline-flex items-center gap-1.5 bg-white border border-${primaryColor}-100 shadow-sm px-4 py-2 rounded-xl transition-all hover:shadow`}>
                                            <Icon path={ICONS_MAP['export']} className="w-4 h-4" /> {t('viewDocument')}
                                        </a>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="p-5 border border-gray-100 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 text-sm shadow-sm">
                                <p>{t('noNotes')}</p>
                            </div>
                        )}
                    </div>

                    <h3 className="text-lg font-bold text-gray-700 mb-2">{t('items')}</h3>
                    {isEditing ? (
                        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden flex flex-col gap-[1px] bg-gray-100 p-[1px] mb-8">
                            {editedItems.map((item, index) => {
                                const productInfo = prodMap.get(item.pid);
                                const availableReturned = returnedPool[item.pid] || 0;
                                return (
                                    <div key={index} className="bg-white p-3 sm:p-4 relative">
                                        <div className="flex items-start gap-3">
                                            {productInfo && <img src={productInfo.imageurl || fallbackImage} alt={item.pid} className="w-12 h-12 rounded-xl object-cover border border-gray-100 shadow-sm flex-shrink-0 mt-1" onError={(e) => e.target.src = fallbackImage} />}
                                            <div className="flex-1 min-w-0">
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={itemSearches[index] ?? item.pid}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            setItemSearches(prev => { const n = [...prev]; n[index] = val; return n; });
                                                        }}
                                                        onFocus={() => setDropdownOpens(prev => { const n = [...prev]; n[index] = true; return n; })}
                                                        onBlur={() => setTimeout(() => setDropdownOpens(prev => { const n = [...prev]; n[index] = false; return n; }), 150)}
                                                        placeholder={t('selectProduct')}
                                                        className={`w-full p-2 text-sm font-semibold border rounded-lg bg-gray-50 ${FOCUSED_INPUT_STYLE}`}
                                                    />
                                                    {(dropdownOpens[index] || false) && (() => {
                                                        const search = (itemSearches[index] || '').toLowerCase();
                                                        const filtered = availableProducts.filter(p => p.product.toLowerCase().includes(search));
                                                        return filtered.length > 0 ? (
                                                            <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl max-h-48 overflow-y-auto">
                                                                {filtered.map(p => (
                                                                    <div key={p.product} onMouseDown={() => {
                                                                        handleItemChange(index, 'pid', p.product);
                                                                        setItemSearches(prev => { const n = [...prev]; n[index] = p.product; return n; });
                                                                        setDropdownOpens(prev => { const n = [...prev]; n[index] = false; return n; });
                                                                    }} className="p-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between text-sm">
                                                                        <span>{p.product}</span>
                                                                        <span className="text-xs text-gray-500">Avail: {p.actualstock || 0}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : null;
                                                    })()}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                                    <div className="flex items-center gap-1">
                                                        <button type="button" onClick={() => handleItemChange(index, 'qty', Math.max(1, (item.qty || 1) - 1))} className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold text-sm">−</button>
                                                        <input type="number" value={item.qty || ''} onChange={e => { const val = e.target.value; handleItemChange(index, 'qty', val === '' ? '' : Math.max(1, parseInt(val) || 1)); }} onBlur={e => { if (e.target.value === '' || parseInt(e.target.value) < 1) handleItemChange(index, 'qty', 1); }} min="1" className={`w-12 px-1 py-1 text-sm text-center border rounded-lg h-7 ${FOCUSED_INPUT_STYLE}`} />
                                                        <button type="button" onClick={() => handleItemChange(index, 'qty', (item.qty || 1) + 1)} className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold text-sm">+</button>
                                                    </div>
                                                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                                        <input type="checkbox" id={`used-${index}`} checked={item.used} onChange={e => handleItemChange(index, 'used', e.target.checked)} disabled={(returnedPool[item.pid] || 0) < item.qty && !item.used} className="h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:bg-gray-200" />
                                                        <span className={`${availableReturned > 0 ? 'font-bold text-yellow-700' : 'text-gray-600'}`}>Returned <span className="text-gray-400">({availableReturned})</span></span>
                                                    </label>
                                                </div>
                                            </div>
                                            <button type="button" onClick={() => handleRemoveItem(index)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors flex-shrink-0">
                                                <Icon path={ICONS_MAP['trash']} className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="bg-white p-3 flex justify-end">
                                <Button size="sm" color="gray" onClick={() => setEditedItems([...editedItems, { pid: '', qty: 1, used: false }])}>{t('addItem')}</Button>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden flex flex-col gap-[1px] bg-gray-100 p-[1px]">
                            {editedItems.map((item, index) => {
                                const productInfo = prodMap.get(item.pid);
                                const imageUrl = productInfo?.imageurl;
                                return (
                                    <div key={index} className="flex items-center justify-between p-3 sm:p-4 bg-white hover:bg-gray-50 transition-colors gap-3">
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            {productInfo ? (
                                                <img src={imageUrl || fallbackImage} alt={item.pid} className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl object-cover border border-gray-100 shadow-sm flex-shrink-0" onError={(e) => e.target.src = fallbackImage} />
                                            ) : (
                                                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center shadow-sm flex-shrink-0">
                                                    <Icon path={ICONS_MAP['stock'] || ICONS_MAP['edit']} className="w-5 h-5 text-gray-400" />
                                                </div>
                                            )}
                                            <div className="min-w-0">
                                                <p className="font-bold text-gray-900 text-sm sm:text-base leading-tight truncate">{item.pid}</p>
                                                {item.used ? <Badge color="bg-yellow-100 text-yellow-800 border border-yellow-200">{t('returned')}</Badge> : <Badge color="bg-blue-50 text-blue-700 border border-blue-200">{t('newPackage')}</Badge>}
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end flex-shrink-0">
                                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">{t('qty')}</p>
                                            <p className={`font-extrabold text-${primaryColor}-700 text-xl px-2 py-0.5 bg-${primaryColor}-50 rounded-lg`}>{item.qty}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="p-5 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 rounded-b-[1.5rem]">
                    {isEditing ? <>
                        <Button onClick={handleCancel} color="gray" className="px-5 py-2 rounded-xl font-medium border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 flex items-center gap-1.5 shadow-sm"><Icon path={ICONS_MAP['close'] || ICONS_MAP['edit']} className="w-4 h-4" /> {t('cancel')}</Button>
                        <Button onClick={handleSave} color="green" className="px-6 py-2 rounded-xl font-bold flex items-center gap-1.5 shadow-md hover:shadow-lg transition-transform hover:scale-105 active:scale-95 text-white bg-green-600 hover:bg-green-700 border-none"><Icon path={ICONS_MAP['save'] || ICONS_MAP['edit']} className="w-4 h-4" /> {t('saveChanges')}</Button>
                    </> : <Button onClick={onClose} color="gray" className="px-6 py-2 rounded-xl font-medium border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 shadow-sm">{t('close')}</Button>}
                </div>
            </div>
            {showInvoice && (
                <CRMInvoice
                    order={order}
                    customer={customer}
                    products={products}
                    generalSettings={generalSettings}
                    shippingFees={shippingFees}
                    settings={settings}
                    onClose={() => setShowInvoice(false)}
                />
            )}
        </div>
    );
}


// ───────────────────────────────────────────────────
// Website Orders Panel — read-only view of lilacvalley.orders
// ───────────────────────────────────────────────────
function WebsiteOrdersPanel({ onTriggerImport }) {
    const [wsOrders, setWsOrders] = useState([]);
    const [wsLoading, setWsLoading] = useState(true);
    const [wsError, setWsError] = useState(null);
    const [wsStatusFilter, setWsStatusFilter] = useState('processing');
    const [wsSearch, setWsSearch] = useState('');

    useEffect(() => {
        const fetchOrders = async () => {
            setWsLoading(true);
            try {
                const { data, error } = await lilacvalleyClient
                    .from('orders')
                    .select('*')
                    .order('created_at', { ascending: false });
                if (error) throw error;
                setWsOrders(data || []);
            } catch (err) {
                setWsError(err.message);
            } finally {
                setWsLoading(false);
            }
        };
        fetchOrders();
    }, []);

    const handleStatusChange = async (targetId, newStatus) => {
        setWsOrders(orders => orders.map(o => o.id === targetId ? { ...o, status: newStatus } : o));
        try {
            await lilacvalleyClient.from('orders').update({ status: newStatus }).eq('id', targetId);
        } catch (e) {
            console.error('Failed to update storefront order status', e);
        }
    };

    const STATUS_INFO = {
        processing: { bg: '#DCFCE7', text: '#166534', label: 'Processing' },
        pending_payment: { bg: '#FEF3C7', text: '#92400E', label: 'Pending Payment' },
        pending_instapay: { bg: '#FEF08A', text: '#854D0E', label: 'Instapay Pending' },
        shipped: { bg: '#E9D5FF', text: '#6B21A8', label: 'Shipped' },
        delivered: { bg: '#DCFCE7', text: '#166534', label: 'Delivered' },
        completed: { bg: '#D1FAE5', text: '#065F46', label: 'Completed' },
        cancelled: { bg: '#FEE2E2', text: '#991B1B', label: 'Cancelled' },
        refunded: { bg: '#FEE2E2', text: '#991B1B', label: 'Refunded' },
        imported: { bg: '#E0E7FF', text: '#3730A3', label: 'Imported (CRM)' },
    };

    // Define fixed sorting order for tabs
    const STATUS_ORDER = ['processing', 'pending_instapay', 'pending_payment', 'shipped', 'delivered', 'completed', 'cancelled', 'refunded', 'imported'];

    const derivedOrders = wsOrders.map(o => ({
        ...o,
        computedStatus: o.crm === 'imported' ? 'imported' : o.status
    }));

    const allStatuses = STATUS_ORDER.filter(s => derivedOrders.some(o => o.computedStatus === s));
    const wsFiltered = derivedOrders.filter(o => {
        const ms = wsStatusFilter === 'all' || o.computedStatus === wsStatusFilter;
        const mq = !wsSearch ||
            (o.customer || '').toLowerCase().includes(wsSearch.toLowerCase()) ||
            (o.phone || '').includes(wsSearch) ||
            (o.id || '').toLowerCase().includes(wsSearch.toLowerCase());
        return ms && mq;
    });

    if (wsLoading) return (
        <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
    );
    if (wsError) return (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-red-700 text-sm">
            Failed to load website orders: {wsError}
        </div>
    );

    return (
        <div>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder="Search by customer, phone or order ID..."
                        value={wsSearch}
                        onChange={e => setWsSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-300 transition-all"
                    />
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    {['all', ...allStatuses].map(s => {
                        const cfg = STATUS_INFO[s] || { bg: '#F3F4F6', text: '#374151', label: s };
                        const isActive = wsStatusFilter === s;
                        return (
                            <button
                                key={s}
                                onClick={() => setWsStatusFilter(s)}
                                className={`px-3 py-1.5 rounded-xl text-xs font-bold capitalize transition-all ${isActive ? 'shadow-md scale-105 ring-2 ring-offset-1' : 'opacity-70 hover:opacity-100'}`}
                                style={{ backgroundColor: isActive ? (cfg.bg || '#E0E7FF') : '#F3F4F6', color: isActive ? (cfg.text || '#3730A3') : '#374151', ringColor: cfg.text }}
                            >
                                {s === 'all' ? `All (${derivedOrders.length})` : `${cfg.label} (${derivedOrders.filter(o => o.computedStatus === s).length})`}
                            </button>
                        );
                    })}
                </div>
            </div>

            {wsFiltered.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                    <svg className="w-14 h-14 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="font-medium">No website orders found</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {wsFiltered.map(order => {
                        const cfg = STATUS_INFO[order.computedStatus] || { bg: '#F3F4F6', text: '#374151', label: order.computedStatus };
                        const items = order.items || [];
                        const isProcessing = order.computedStatus === 'processing';
                        const isCash = (order.payment_method || '').toLowerCase().includes('cash');
                        return (
                            <div
                                key={order.id}
                                onClick={() => isProcessing && onTriggerImport && onTriggerImport(order)}
                                className={`bg-white rounded-2xl shadow-sm p-4 transition-all ${isProcessing ? 'border-2 border-green-300 cursor-pointer hover:shadow-lg hover:scale-[1.01]' : 'border border-gray-100 shadow-sm'
                                    }`}
                            >
                                {/* Header row */}
                                <div className="flex justify-between items-start gap-4 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className="font-bold text-gray-900 bg-gray-100 px-2.5 py-1 rounded-lg text-sm shadow-sm border border-gray-200">{order.id}</span>
                                            <select
                                                value={order.status || 'processing'}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                                className="text-[10px] font-bold px-3 py-1 rounded-full outline-none cursor-pointer appearance-none border border-transparent hover:border-gray-300 transition-colors uppercase tracking-wider"
                                                style={{ backgroundColor: cfg.bg, color: cfg.text }}
                                            >
                                                <option value="processing">Processing</option>
                                                <option value="pending_payment">Payment Pending</option>
                                                <option value="pending_instapay">Instapay Pending</option>
                                                <option value="shipped">Shipped</option>
                                                <option value="delivered">Delivered</option>
                                                <option value="cancelled">Cancelled</option>
                                                <option value="refunded">Refunded</option>
                                            </select>
                                            {isProcessing && (
                                                <span className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded-full font-bold animate-pulse">⚡ Ready for import</span>
                                            )}
                                        </div>
                                        <p className="font-semibold text-gray-800 text-sm">{order.customer}</p>
                                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
                                            {order.phone && <span>📞 {order.phone}</span>}
                                            {order.email && <span>✉️ {order.email}</span>}
                                        </div>
                                        {order.city && <p className="text-xs text-gray-400 mt-0.5">📍 {order.city}{order.address ? ` · ${order.address}` : ''}</p>}
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className="font-extrabold text-gray-900 text-lg">EGP {Number(order.total || 0).toLocaleString()}</p>
                                        {order.shipping > 0 && <p className="text-xs text-gray-400">+ EGP {order.shipping} shipping</p>}
                                        <p className={`text-xs font-semibold mt-1 ${isCash ? 'text-green-600' : 'text-blue-600'}`}>{order.payment_method}</p>
                                        <p className="text-xs text-gray-400 mt-1">{new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                    </div>
                                </div>
                                {/* Items */}
                                {items.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-gray-100">
                                        <div className="flex flex-wrap gap-2">
                                            {items.map((item, i) => (
                                                <div key={i} className="flex flex-col gap-1.5 items-start">
                                                    <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-lg">
                                                        <span className="font-bold text-indigo-600">{item.quantity}×</span>
                                                        {item.name}
                                                        {item.size && <span className="text-gray-400 text-[10px]">({item.size.split('/')[0].trim()})</span>}
                                                        {item.price !== undefined && <span className="text-gray-500">· EGP {item.price}</span>}
                                                    </span>
                                                    {item.bundleSelections && typeof item.bundleSelections === 'object' && (
                                                        <div className="flex flex-wrap gap-1.5 ml-4 pl-2 border-l-2 border-indigo-200">
                                                            {Object.values(item.bundleSelections).map((bundleItem, bIdx) => {
                                                                if (!bundleItem) return null;
                                                                return (
                                                                    <span key={bIdx} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded cursor-default border border-indigo-100">
                                                                        <span className="font-bold">{bundleItem.quantity || 1}×</span>
                                                                        {bundleItem.name}
                                                                        {bundleItem.selectedVariant?.size && <span className="text-indigo-400">({bundleItem.selectedVariant.size})</span>}
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {/* Notes */}
                                {order.notes && (
                                    <div className="mt-2 text-xs text-yellow-800 bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-1.5">
                                        📝 {order.notes}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

        </div>
    );
}

function OrdersBoard({ orders: rawOrders, customers, handleOrderAction, setView, setSelectedOrder, shippingFees, settings, generalSettings, handleFileUpload, setSelectedCustomerForHistory, pendingWebsiteOrders = [], onImportSingleOrder }) {
    const orders = useMemo(() => {
        return [...rawOrders].sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            if (dateB - dateA !== 0) return dateB - dateA;
            // Fallback to order ID if dates are identical
            const idA = parseInt(String(a.orderid).replace(/\D/g, '')) || 0;
            const idB = parseInt(String(b.orderid).replace(/\D/g, '')) || 0;
            return idB - idA;
        });
    }, [rawOrders]);

    const { t, language, isRTL } = useLanguage();
    const [openFileDialog, setOpenFileDialog] = useState(false);
    const custMap = useMemo(() => new Map(customers.map(c => [c.id, c])), [customers]);
    const shippingMap = useMemo(() => new Map(shippingFees.map(s => [`${s.state?.trim()}-${s.city?.trim()}`, s])), [shippingFees]);

    useEffect(() => {
        if (openFileDialog) {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.csv,.xlsx,.json';
            input.onchange = (e) => handleFileUpload(e, 'orders');
            input.click();
            setOpenFileDialog(false);
        }
    }, [openFileDialog, handleFileUpload]);

    // Status configuration uses colors/icons from settings
    const STATUS_CONFIG = useMemo(() => {
        return Object.keys(settings.statusColors).reduce((acc, status) => {
            acc[status] = {
                icon: settings.statusIcons[status],
                color: settings.statusColors[status]
            };
            return acc;
        }, {});
    }, [settings.statusColors, settings.statusIcons]);

    // Define custom order for status tabs: ordered-shipped-delivered-completed, then cancelled and returned
    const STATUS_ORDER = ['ordered', 'shipped', 'delivered', 'completed', 'cancelled', 'returned'];

    const [activeTab, setActiveTab] = useState('ordered'); // State for active tab
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('ordersViewMode') || 'auto'); // State for view mode
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [completedStateFilter, setCompletedStateFilter] = useState('All States');
    const [collapsedPaymentGroups, setCollapsedPaymentGroups] = useState({}); // Track collapsed payment groups
    const [collapsedDateGroups, setCollapsedDateGroups] = useState({}); // Track collapsed date groups
    const [orderSearch, setOrderSearch] = useState(''); // Search by customer name or phone
    const [showFilters, setShowFilters] = useState(false);
    const filterRef = useRef(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const effectiveViewMode = viewMode === 'auto' ? (isMobile ? 'cards' : 'table') : viewMode;

    const toggleViewMode = () => {
        const newMode = viewMode === 'table' ? 'cards' : viewMode === 'cards' ? 'auto' : 'table';
        setViewMode(newMode);
        localStorage.setItem('ordersViewMode', newMode);
    };

    const mostRecentCompletedDate = useMemo(() => {
        const completed = orders.find(o => (o.status || 'ordered').toLowerCase() === 'completed');
        return completed ? new Date(completed.date) : null;
    }, [orders]);

    const ordersByStatus = useMemo(() => {
        const grouped = {};
        const counts = {};

        // Initialize all statuses except deleted
        Object.keys(STATUS_CONFIG).filter(status => status !== 'deleted').forEach(status => {
            grouped[status] = {};
            counts[status] = 0;
        });

        orders.forEach(order => {
            const status = (order.status || 'ordered').toLowerCase();
            if (status === 'deleted') return;

            // Apply search filter (happens before status check so counts are accurate for search)
            if (orderSearch.trim()) {
                const customer = custMap.get(order.customerid);
                const searchNorm = normalizeSearch(orderSearch);
                const nameMatch = customer?.name && normalizeSearch(customer.name).includes(searchNorm);
                const phoneMatch = customer?.mobile && customer.mobile.includes(orderSearch.trim());
                if (!nameMatch && !phoneMatch) return;
            }

            // Important: Count only after search filter, but BEFORE state filter
            if (counts[status] !== undefined) counts[status]++;

            // Apply state filter for active tabs
            const tabsWithStateFilter = ['completed', 'ordered', 'shipped', 'delivered'];
            if (tabsWithStateFilter.includes(status) && completedStateFilter !== 'All States') {
                const customer = custMap.get(order.customerid);
                if (customer?.state !== completedStateFilter) return;
            }

            const paymentMethod = order.payment || 'N/A';
            const d = new Date(order.date);
            const orderDate = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`; // Simple YYYY-MM-DD for grouping

            if (!grouped[status]) grouped[status] = {};
            if (!grouped[status][paymentMethod]) grouped[status][paymentMethod] = {};
            if (!grouped[status][paymentMethod][orderDate]) grouped[status][paymentMethod][orderDate] = [];

            grouped[status][paymentMethod][orderDate].push(order);
        });

        // Attach pre-calculated counts as a non-enumerable property
        Object.defineProperty(grouped, '_counts', { value: counts, enumerable: false });

        return grouped;
    }, [orders, STATUS_CONFIG, completedStateFilter, custMap, orderSearch]);

    // TAB component for OrdersBoard
    const OrdersBoardTabs = ({ currentStatus, setStatus }) => {
        // Use custom status order, filter out deleted
        const visibleStatuses = STATUS_ORDER.filter(status => status !== 'deleted' && STATUS_CONFIG[status]);
        return (
            <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                    {visibleStatuses.map((status, index) => {
                        const config = STATUS_CONFIG[status];
                        // Calculate count by flattening all nested levels: Payment → Date → Orders
                        const paymentGroups = ordersByStatus[status] || {};
                        const count = Object.values(paymentGroups).flatMap(dateGroups =>
                            Object.values(dateGroups).flat()
                        ).length;
                        // Add spacing before cancelled
                        const addSpacing = status === 'cancelled';
                        const isActive = currentStatus === status;
                        return (
                            <button
                                key={status}
                                onClick={() => setStatus(status)}
                                className={`group relative px-3 py-2 rounded-xl font-semibold transition-all duration-200 ease-in-out flex-shrink-0
                                    ${addSpacing ? 'ml-2' : ''}
                                    ${isActive
                                        ? `bg-gradient-to-br from-${config.color}-500 to-${config.color}-600 text-white shadow-lg scale-105`
                                        : `bg-${config.color}-100 text-${config.color}-800 hover:bg-${config.color}-200 shadow-sm hover:shadow-md`}
                                    focus:outline-none focus:ring-2 focus:ring-${config.color}-500 focus:ring-offset-2`}
                            >
                                <div className="flex items-center gap-2">
                                    {/* Icon */}
                                    <div className={`p-1 rounded-lg transition-colors ${isActive ? 'bg-white/20' : `bg-${config.color}-200`}`}>
                                        <Icon path={ICONS_MAP[config.icon]} className={`w-4 h-4 ${isActive ? 'text-white' : `text-${config.color}-700`}`} />
                                    </div>
                                    {/* Status name - hidden on mobile when not active */}
                                    <span className={`text-xs font-bold capitalize ${isActive ? '' : 'hidden md:inline'}`}>{status}</span>
                                    {/* Count badge - matches icon color scheme */}
                                    <div className={`min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-xs font-bold
                                         ${isActive
                                            ? 'bg-white/20 text-white'
                                            : `bg-${config.color}-200 text-${config.color}-800`}`}>
                                        {count}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    const ordersInActiveTab = useMemo(() => {
        const paymentGroups = ordersByStatus[activeTab] || {};
        // Flatten the three-level structure: Payment → Date → Orders
        return Object.values(paymentGroups).flatMap(dateGroups => Object.values(dateGroups).flat());
    }, [ordersByStatus, activeTab]);

    const totalRevenue = useMemo(() => {
        return Object.entries(ordersByStatus[activeTab] || {}).reduce((sum, [paymentMethod, dateGroups]) => {
            const allOrders = Object.values(dateGroups).flat();
            const revenue = allOrders.reduce((subSum, order) => {
                const customer = custMap.get(order.customerid);
                const shippingFee = customer ? (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;
                return subSum + (safeCoerceNumber(order.total) - shippingFee);
            }, 0);
            return sum + revenue;
        }, 0);
    }, [ordersByStatus, activeTab, custMap, shippingMap]);

    const OrderRow = ({ order, onCustomerClick }) => {
        const customer = custMap.get(order.customerid);
        const total = safeCoerceNumber(order.total);
        const shippingFee = customer ? (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;
        const status = order.status;

        const statusColor = STATUS_CONFIG[status]?.color || 'gray';

        // Format products ordered for multi-line display
        const productsOrdered = (order.items || []).map(item =>
            `${item.pid} (x${item.qty})`
        ).join('\n');

        // Determine all possible actions based on the current status
        const getPossibleActions = (currentStatus) => {
            switch (currentStatus) {
                case 'ordered':
                    return [
                        { action: 'advance', label: t('moveToShipped'), color: STATUS_CONFIG.shipped.color, icon: 'shipped' },
                        { action: 'cancel', label: t('cancelOrder'), color: STATUS_CONFIG.cancelled.color, icon: 'cancelled' }
                    ];
                case 'shipped':
                    return [
                        { action: 'back', label: t('moveToOrdered'), color: STATUS_CONFIG.ordered.color, icon: 'ordered' },
                        { action: 'advance', label: t('moveToDelivered'), color: STATUS_CONFIG.delivered.color, icon: 'delivered' },
                        { action: 'cancel', label: t('cancelOrder'), color: STATUS_CONFIG.cancelled.color, icon: 'cancelled' }
                    ];
                case 'delivered':
                    return [
                        { action: 'back', label: t('moveToShipped'), color: STATUS_CONFIG.shipped.color, icon: 'shipped' },
                        { action: 'complete', label: t('completeSale'), color: STATUS_CONFIG.completed.color, icon: 'completed' }
                    ];
                case 'completed':
                    return [
                        { action: 'return', label: t('markReturned'), color: STATUS_CONFIG.returned.color, icon: 'returned' },
                        { action: 'cancel', label: t('markCancelled'), color: STATUS_CONFIG.cancelled.color, icon: 'cancelled' }
                    ];
                case 'cancelled':
                    return [
                        { action: 'reopen', label: t('reOrder'), color: STATUS_CONFIG.ordered.color, icon: 'ordered' },
                        { action: 'return', label: t('markReturned'), color: STATUS_CONFIG.returned.color, icon: 'returned' },
                    ];
                case 'returned':
                    return [
                        { action: 'reopen', label: t('reOrder'), color: STATUS_CONFIG.ordered.color, icon: 'ordered' },
                        { action: 'cancel', label: t('markCancelled'), color: STATUS_CONFIG.cancelled.color, icon: 'cancelled' },
                    ];
                case 'deleted':
                    return [
                        { action: 'reopen', label: t('restoreToCancelled'), color: STATUS_CONFIG.cancelled.color, icon: 'cancelled' },
                        { action: 'perm_delete', label: t('permanentlyDelete'), color: STATUS_CONFIG.deleted.color, icon: 'trash' }
                    ];
                default:
                    return [];
            }
        };

        const possibleActions = getPossibleActions(activeTab);

        return (
            // Make the entire row clickable to view details
            <tr key={order.orderid} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedOrder(order)}>
                <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                        <span className="font-bold text-gray-900 bg-gray-100 px-2.5 py-1 rounded-lg text-xs inline-block w-fit border border-gray-200">{String(order.orderid).replace(/^#/, '')}</span>
                        {order.woo_order_id && (
                            <span className="font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-lg text-[10px] inline-block w-fit border border-indigo-100 flex items-center gap-1">
                                {order.woo_order_id}
                            </span>
                        )}
                    </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                    <p
                        className="font-semibold text-indigo-600 hover:text-indigo-800 cursor-pointer hover:underline"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (customer) onCustomerClick(customer);
                        }}
                    >
                        {customer?.name || 'N/A'}
                    </p>
                    <p>{customer?.mobile || 'N/A'}</p>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                    <p>{customer?.state || 'N/A'}</p>
                    <p className="text-xs text-gray-400">{customer?.city || ''}</p>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <pre className="text-xs font-mono whitespace-pre-wrap">{productsOrdered}</pre>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-indigo-600">{formatCurrency(total, generalSettings?.currency || 'GBP')}<br /><span className="text-xs text-gray-500">{t('shipping')}: {getCurrencySymbol(generalSettings?.currency || 'GBP')}{Math.floor(shippingFee)}</span></td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                    <Badge color={order.payment === 'Visa' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>{order.payment === 'Visa' ? t('visaPayment') : t('cashPayment')}</Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end flex-wrap gap-1">

                        {possibleActions.map(act => {
                            // If the action is a single button (like Complete or Perm Delete), use the Button component
                            const isButton = ['complete', 'reopen', 'perm_delete'].includes(act.action);

                            if (isButton) {
                                return (
                                    <Button key={act.action} size="sm" color={act.color} onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                            await handleOrderAction(order.orderid, act.action);
                                        } catch (err) {
                                            console.error('Order action failed:', err);
                                        }
                                    }} tooltip={act.label}>
                                        <Icon path={ICONS_MAP[act.icon]} className="w-4 h-4" /> {act.label.split(' ')[0]}
                                    </Button>
                                );
                            } else {
                                // Otherwise, use the IconButton for smaller actions
                                return (
                                    <IconButton
                                        key={act.action}
                                        icon={act.icon}
                                        color={act.color}
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                                await handleOrderAction(order.orderid, act.action);
                                            } catch (err) {
                                                console.error('Order action failed:', err);
                                            }
                                        }}
                                        tooltip={act.label}
                                    />
                                );
                            }
                        })}

                        {/* Soft Delete is always available if not on the deleted tab */}
                        {activeTab !== 'deleted' &&
                            <IconButton icon="trash" color="gray" onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                    await handleOrderAction(order.orderid, 'delete');
                                } catch (err) {
                                    console.error('Order action failed:', err);
                                }
                            }} tooltip={t('softDelete')} />
                        }

                    </div>
                </td>
            </tr>
        );
    };


    return (
        <div>
            <div className="flex justify-between items-center gap-2 mb-4">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">{t('ordersBoard')}</h1>
                <div className="flex gap-2">
                    <button
                        onClick={() => setActiveTab('website')}
                        className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all duration-200 ${activeTab === 'website'
                            ? 'bg-indigo-600 text-white shadow-lg'
                            : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200'
                            }`}
                        title="Website Orders"
                    >
                        <span>🌐</span>
                        <span className="hidden sm:inline">Website Orders</span>
                        {pendingWebsiteOrders.length > 0 && (
                            <span className="bg-red-500 text-white text-[10px] sm:text-xs font-bold px-1.5 py-0.5 rounded-full animate-pulse">
                                {pendingWebsiteOrders.length}
                            </span>
                        )}
                    </button>
                    <button onClick={toggleViewMode} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors" title={viewMode === 'auto' ? t('auto') : viewMode === 'table' ? t('table') : t('cards')}>
                        {viewMode === 'table' ? (
                            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                        ) : (
                            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" /></svg>
                        )}
                    </button>
                    <Button onClick={() => setView('Deleted Orders')} color="gray" size="sm" className="px-2" title={t('deletedOrders')}>
                        <Icon path={ICONS_MAP['trash']} className="w-5 h-5 text-gray-600" />
                    </Button>
                    <AdminOnly>
                        <Button onClick={() => setOpenFileDialog(true)} color="gray" size="sm" className="px-2" title={t('uploadOrders')}>
                            <Icon path={ICONS_MAP['upload']} className="w-5 h-5 text-gray-600" />
                        </Button>
                    </AdminOnly>
                </div>
            </div>

            <div className="mb-6">
                <Button onClick={() => setView('New Order')} color={settings.primaryColor} className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl shadow-sm hover:shadow-md transition-all">
                    <Icon path={ICONS_MAP['add']} className="w-5 h-5" />
                    <span className="font-bold">{t('createNewOrder')}</span>
                </Button>
            </div>

            {/* Search and Filters (Moved Above CategoryTabs) */}
            <div className="mb-4 flex flex-col sm:flex-row gap-4 items-center justify-between bg-white p-3 rounded-2xl shadow-sm border border-gray-100">
                {/* Search Bar and Filter State Container */}
                <div className="flex items-center gap-3 w-full flex-1">
                    <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder={t('searchByCustomerNameOrPhone')}
                            value={orderSearch}
                            onChange={(e) => setOrderSearch(e.target.value)}
                            className={`w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 focus:bg-white rounded-xl text-sm transition-all duration-300 outline-none ${FOCUSED_INPUT_STYLE}`}
                        />
                        {orderSearch && (
                            <button
                                onClick={() => setOrderSearch('')}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                title="Clear search"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* Filter Popover for Orders Board */}
                    {['completed', 'ordered', 'shipped', 'delivered'].includes(activeTab) && (
                        <div className="relative" ref={filterRef}>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`p-2.5 rounded-xl border transition-all duration-200 ${showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-600 shadow-sm' : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300 hover:bg-gray-50'}`}
                                title={t('filters')}
                            >
                                <Icon path={ICONS_MAP['filter']} className="w-5 h-5" />
                            </button>

                            {showFilters && (() => {
                                // Efficiently calculate state counts
                                const stateCounts = {};
                                const tabsToCount = ['completed', 'ordered', 'shipped', 'delivered'];
                                orders.forEach(o => {
                                    if (tabsToCount.includes(o.status) && o.status === activeTab) {
                                        const cust = custMap.get(o.customerid);
                                        const state = cust?.state || 'Unknown';
                                        stateCounts[state] = (stateCounts[state] || 0) + 1;
                                    }
                                });

                                return (
                                    <div className={`absolute top-full mt-2 ${isRTL ? 'left-0' : 'right-0'} bg-white border border-gray-100 rounded-xl shadow-xl z-50 p-4 min-w-[200px] space-y-4`}>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{t('filterState') || 'Filter State'}</label>
                                            <select
                                                value={completedStateFilter}
                                                onChange={(e) => setCompletedStateFilter(e.target.value)}
                                                className={`w-full p-2 border border-gray-200 rounded-lg text-sm ${FOCUSED_INPUT_STYLE}`}
                                            >
                                                <option value="All States">All States</option>
                                                {Object.keys(stateCounts).sort().map(state => (
                                                    <option key={state} value={state}>{state} ({stateCounts[state]})</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="pt-2 border-t border-gray-100 flex justify-end">
                                            <button onClick={() => setShowFilters(false)} className="text-sm font-bold text-indigo-600 hover:underline">{t('done')}</button>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
            </div>

            {/* Consolidated Tabs Component */}
            <CategoryTabs
                currentStatus={activeTab}
                setActiveTab={setActiveTab}
                ordersByStatus={ordersByStatus}
                statusConfig={STATUS_CONFIG}
                isMobile={isMobile}
                totalRevenue={totalRevenue}
                visibleStatuses={STATUS_ORDER.filter(status => status !== 'deleted' && STATUS_CONFIG[status])}
                generalSettings={generalSettings}
            />

            {/* Content for Active Tab */}
            {activeTab === 'website' ? (
                <WebsiteOrdersPanel
                    onTriggerImport={(order) => {
                        if (onImportSingleOrder) onImportSingleOrder(order);
                    }}
                />
            ) : effectiveViewMode === 'table' ? (
                <Card className="w-full border-t-4 border-gray-200">
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                        <div className="inline-block min-w-full align-middle">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[80px]">{t('orderId')}</th>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[120px]">{t('customer')}</th>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[100px]">{t('location')}</th>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[200px]">{t('products')}</th>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[80px]">{t('total')}</th>
                                        <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[80px]">{t('payment')}</th>
                                        <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[140px]">{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {Object.entries(ordersByStatus[activeTab] || {})
                                        .sort(([a], [b]) => (b === 'Cash' ? 1 : 0) - (a === 'Cash' ? 1 : 0)) // Sort by payment method
                                        .map(([paymentMethod, dateGroups]) => {
                                            const allOrders = Object.values(dateGroups).flat();
                                            if (allOrders.length === 0) return null;
                                            const revenue = allOrders.reduce((sum, order) => {
                                                const customer = custMap.get(order.customerid);
                                                const shippingFee = customer ? (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;
                                                return sum + (safeCoerceNumber(order.total) - shippingFee);
                                            }, 0);
                                            const paymentGroupKey = `${activeTab}-${paymentMethod}`;
                                            const isPaymentCollapsed = !!collapsedPaymentGroups[paymentGroupKey];

                                            return (
                                                <React.Fragment key={paymentMethod}>
                                                    <tr
                                                        className={`${paymentMethod === 'Cash' ? 'bg-green-600' : 'bg-blue-600'} cursor-pointer hover:opacity-90 transition-opacity`}
                                                        onClick={() => {
                                                            setCollapsedPaymentGroups(prev => ({
                                                                ...prev,
                                                                [paymentGroupKey]: !prev[paymentGroupKey]
                                                            }));
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                setCollapsedPaymentGroups(prev => ({
                                                                    ...prev,
                                                                    [paymentGroupKey]: !prev[paymentGroupKey]
                                                                }));
                                                            }
                                                        }}
                                                        tabIndex={0}
                                                        role="button"
                                                        aria-expanded={!isPaymentCollapsed}
                                                    >
                                                        <td colSpan="7" className="px-6 py-2 text-md font-semibold text-white">
                                                            <div className="flex justify-between items-center">
                                                                <span className="flex items-center">
                                                                    <Icon
                                                                        path={isPaymentCollapsed ? ICONS_MAP['chevronright'] : ICONS_MAP['chevrondown']}
                                                                        className="w-5 h-5 mr-2 text-white"
                                                                    />
                                                                    <Icon path={ICONS_MAP[paymentMethod === 'Cash' ? 'cash' : 'creditcard']} className="w-5 h-5 mr-2 text-white" />
                                                                    {paymentMethod === 'Cash' ? t('cashLabel') : t('visaLabel')} ({allOrders.length} {t('orders')})
                                                                </span>
                                                                <span className="font-bold text-white">
                                                                    <span className="hidden sm:inline">{t('revenue')}: </span>
                                                                    {formatCurrency(revenue, generalSettings?.currency || 'GBP')}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {!isPaymentCollapsed && Object.entries(dateGroups)
                                                        .sort(([dateA, ordersA], [dateB, ordersB]) => new Date(ordersB[0]?.date) - new Date(ordersA[0]?.date)) // Sort by date descending using ISO timestamp
                                                        .map(([orderDate, ordersList]) => {
                                                            const dateGroupKey = `${paymentGroupKey}-${orderDate}`;
                                                            const isDateCollapsed = collapsedDateGroups[dateGroupKey] !== undefined
                                                                ? collapsedDateGroups[dateGroupKey]
                                                                : (activeTab === 'completed' && mostRecentCompletedDate && (mostRecentCompletedDate - new Date(orderDate)) > (30 * 24 * 60 * 60 * 1000));

                                                            return (
                                                                <React.Fragment key={`${paymentMethod}-${orderDate}`}>
                                                                    <tr
                                                                        className="bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors"
                                                                        onClick={() => {
                                                                            setCollapsedDateGroups(prev => ({
                                                                                ...prev,
                                                                                [dateGroupKey]: !prev[dateGroupKey]
                                                                            }));
                                                                        }}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                                e.preventDefault();
                                                                                setCollapsedDateGroups(prev => ({
                                                                                    ...prev,
                                                                                    [dateGroupKey]: !prev[dateGroupKey]
                                                                                }));
                                                                            }
                                                                        }}
                                                                        tabIndex={0}
                                                                        role="button"
                                                                        aria-expanded={!isDateCollapsed}
                                                                    >
                                                                        <td colSpan="7" className="px-6 py-1 text-sm font-medium text-blue-900">
                                                                            <span className="flex items-center">
                                                                                <Icon
                                                                                    path={isDateCollapsed ? ICONS_MAP['chevronright'] : ICONS_MAP['chevrondown']}
                                                                                    className="w-4 h-4 mr-2 text-blue-900"
                                                                                />
                                                                                📅 {t(orderDate) || orderDate} ({ordersList.length} {ordersList.length === 1 ? t('order') : t('orders')})
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                    {!isDateCollapsed && ordersList.map(order => <OrderRow key={order.orderid} order={order} onCustomerClick={setSelectedCustomerForHistory} />)}
                                                                </React.Fragment>
                                                            );
                                                        })
                                                    }
                                                </React.Fragment>
                                            )
                                        })}
                                    {ordersInActiveTab.length === 0 && (
                                        <tr><td colSpan="7" className="text-center py-6 text-gray-500 italic">{t('noOrdersFound')}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Card>
            ) : (
                <div className="space-y-4">
                    {Object.entries(ordersByStatus[activeTab] || {})
                        .sort(([a], [b]) => (b === 'Cash' ? 1 : 0) - (a === 'Cash' ? 1 : 0))
                        .map(([paymentMethod, dateGroups]) => {
                            const allOrders = Object.values(dateGroups).flat();
                            const paymentGroupKey = `${activeTab}-${paymentMethod}`;
                            const isPaymentCollapsed = !!collapsedPaymentGroups[paymentGroupKey];

                            return (
                                <div key={paymentMethod} className="space-y-2">
                                    <div
                                        className={`flex justify-between items-center px-4 py-2 rounded-lg cursor-pointer hover:opacity-90 transition-opacity ${paymentMethod === 'Cash' ? 'bg-green-600' : 'bg-blue-600'}`}
                                        onClick={() => {
                                            setCollapsedPaymentGroups(prev => ({
                                                ...prev,
                                                [paymentGroupKey]: !prev[paymentGroupKey]
                                            }));
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setCollapsedPaymentGroups(prev => ({
                                                    ...prev,
                                                    [paymentGroupKey]: !prev[paymentGroupKey]
                                                }));
                                            }
                                        }}
                                        tabIndex={0}
                                        role="button"
                                        aria-expanded={!isPaymentCollapsed}
                                    >
                                        <span className="flex items-center font-semibold text-white">
                                            <Icon
                                                path={isPaymentCollapsed ? ICONS_MAP['chevronright'] : ICONS_MAP['chevrondown']}
                                                className="w-5 h-5 mr-2 text-white"
                                            />
                                            <Icon path={ICONS_MAP[paymentMethod === 'Cash' ? 'cash' : 'creditcard']} className="w-5 h-5 mr-2 text-white" />
                                            {paymentMethod === 'Cash' ? t('cashLabel') : t('visaLabel')} ({allOrders.length} {t('orders')})
                                        </span>
                                        <span className="font-bold text-white">
                                            <span className="hidden sm:inline">{t('revenue')}: </span>
                                            {formatCurrency(allOrders.reduce((sum, order) => {
                                                const customer = custMap.get(order.customerid);
                                                const shippingFee = customer ? (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;
                                                return sum + (safeCoerceNumber(order.total) - shippingFee);
                                            }, 0), generalSettings?.currency || 'GBP')}
                                        </span>
                                    </div>
                                    {!isPaymentCollapsed && Object.entries(dateGroups)
                                        .sort(([dateA, ordersA], [dateB, ordersB]) => new Date(ordersB[0]?.date) - new Date(ordersA[0]?.date))
                                        .map(([orderDate, ordersList]) => {
                                            const dateGroupKey = `${paymentGroupKey}-${orderDate}`;
                                            const isDateCollapsed = collapsedDateGroups[dateGroupKey] !== undefined
                                                ? collapsedDateGroups[dateGroupKey]
                                                : (activeTab === 'completed' && mostRecentCompletedDate && (mostRecentCompletedDate - new Date(orderDate)) > (30 * 24 * 60 * 60 * 1000));

                                            return (
                                                <div key={`${paymentMethod}-${orderDate}`} className="space-y-2">
                                                    <div
                                                        className="px-4 py-1 bg-blue-50 rounded text-sm font-medium text-blue-900 cursor-pointer hover:bg-blue-100 transition-colors flex items-center"
                                                        onClick={() => {
                                                            setCollapsedDateGroups(prev => ({
                                                                ...prev,
                                                                [dateGroupKey]: !prev[dateGroupKey]
                                                            }));
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                setCollapsedDateGroups(prev => ({
                                                                    ...prev,
                                                                    [dateGroupKey]: !prev[dateGroupKey]
                                                                }));
                                                            }
                                                        }}
                                                        tabIndex={0}
                                                        role="button"
                                                        aria-expanded={!isDateCollapsed}
                                                    >
                                                        <Icon
                                                            path={isDateCollapsed ? ICONS_MAP['chevronright'] : ICONS_MAP['chevrondown']}
                                                            className="w-4 h-4 mr-2 text-blue-900"
                                                        />
                                                        📅 {t(orderDate) || orderDate} ({ordersList.length} {ordersList.length === 1 ? t('order') : t('orders')})
                                                    </div>
                                                    {!isDateCollapsed && (
                                                        <div className="grid grid-cols-1 gap-4">
                                                            {ordersList.map(order => (
                                                                <MobileOrderCard
                                                                    key={order.orderid}
                                                                    order={order}
                                                                    customer={custMap.get(order.customerid)}
                                                                    onClick={() => setSelectedOrder(order)}
                                                                    handleOrderAction={handleOrderAction}
                                                                    statusConfig={STATUS_CONFIG}
                                                                    settings={settings}
                                                                    generalSettings={generalSettings}
                                                                    formatCurrency={formatCurrency}
                                                                    shippingFees={shippingFees}
                                                                    shippingMap={shippingMap}
                                                                    onCustomerClick={setSelectedCustomerForHistory}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            );
                        })}
                    {ordersInActiveTab.length === 0 && (
                        <div className="text-center py-6 text-gray-500 italic">No {activeTab} orders found.</div>
                    )}
                </div>
            )}
        </div>
    );
}

function DeletedOrdersBoard({ orders, customers, handleOrderAction, setView, setSelectedOrder, shippingFees, settings, generalSettings, handleFileUpload, setSelectedCustomerForHistory }) {
    const { t, language, isRTL } = useLanguage();
    const custMap = useMemo(() => new Map(customers.map(c => [c.id, c])), [customers]);
    const shippingMap = useMemo(() => new Map(shippingFees.map(s => [`${s.state?.trim()}-${s.city?.trim()}`, s])), [shippingFees]);

    const STATUS_CONFIG = {
        deleted: { icon: 'trash', color: 'gray' },
        cancelled: { icon: 'cancelled', color: 'red' },
        ordered: { icon: 'ordered', color: 'blue' },
        shipped: { icon: 'shipped', color: 'purple' },
        delivered: { icon: 'delivered', color: 'yellow' },
        completed: { icon: 'completed', color: 'green' },
        returned: { icon: 'returned', color: 'orange' }
    };
    const [activeTab, setActiveTab] = useState('deleted');
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('deletedOrdersViewMode') || 'auto');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const effectiveViewMode = viewMode === 'auto' ? (isMobile ? 'cards' : 'table') : viewMode;

    const toggleViewMode = () => {
        const newMode = viewMode === 'table' ? 'cards' : viewMode === 'cards' ? 'auto' : 'table';
        setViewMode(newMode);
        localStorage.setItem('deletedOrdersViewMode', newMode);
    };

    const ordersByStatus = useMemo(() => {
        const groupedByStatus = { deleted: {} };
        orders.forEach(order => {
            const status = (order.status || 'ordered').toLowerCase();
            if (status === 'deleted') {
                const paymentMethod = order.payment || 'N/A';
                if (!groupedByStatus[status][paymentMethod]) groupedByStatus[status][paymentMethod] = [];
                groupedByStatus[status][paymentMethod].push(order);
            }
        });
        return groupedByStatus;
    }, [orders, language]);

    const OrdersBoardTabs = ({ currentStatus }) => {
        const visibleStatuses = ['deleted'];
        return (
            <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                    {visibleStatuses.map(status => {
                        const config = STATUS_CONFIG[status];
                        const count = Object.values(ordersByStatus[status] || {}).flat().length;
                        const isActive = true; // Always active since there's only one tab
                        return (
                            <button
                                key={status}
                                className={`group relative px-3 py-2 rounded-xl font-semibold transition-all duration-200 ease-in-out flex-shrink-0
                                    bg-gradient-to-br from-${config.color}-500 to-${config.color}-600 text-white shadow-lg scale-105
                                    focus:outline-none focus:ring-2 focus:ring-${config.color}-500 focus:ring-offset-2`}
                            >
                                <div className="flex items-center gap-2">
                                    {/* Icon */}
                                    <div className="p-1 rounded-lg transition-colors bg-white/20">
                                        <Icon path={ICONS_MAP[config.icon]} className="w-4 h-4 text-white" />
                                    </div>
                                    {/* Status name */}
                                    <span className="text-xs font-bold capitalize">{status}</span>
                                    {/* Count badge */}
                                    <div className="min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-xs font-bold bg-white/20 text-white">
                                        {count}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    const ordersInActiveTab = useMemo(() => {
        const paymentGroups = ordersByStatus[activeTab] || {};
        return Object.values(paymentGroups).flat();
    }, [ordersByStatus, activeTab]);

    const totalRevenue = useMemo(() => {
        return Object.entries(ordersByStatus[activeTab] || {}).reduce((sum, [paymentMethod, ordersList]) => {
            const revenue = ordersList.reduce((subSum, order) => {
                const customer = custMap.get(order.customerid);
                const shippingFee = customer ? (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;
                return subSum + (safeCoerceNumber(order.total) - shippingFee);
            }, 0);
            return sum + revenue;
        }, 0);
    }, [ordersByStatus, activeTab, custMap, shippingMap]);

    const OrderRow = ({ order, onCustomerClick }) => {
        const customer = custMap.get(order.customerid);
        const total = safeCoerceNumber(order.total);
        const shippingFee = customer ? (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;
        const status = order.status;
        const statusColor = STATUS_CONFIG[status]?.color || 'gray';
        const productsOrdered = (order.items || []).map(item =>
            `${item.pid} (x${item.qty})`
        ).join('\n');

        const possibleActions = [
            { action: 'reopen', label: 'Restore to Cancelled', color: 'red', icon: 'cancelled' },
            { action: 'perm_delete', label: 'Permanently Delete', color: 'gray', icon: 'trash' }
        ];

        return (
            <tr key={order.orderid} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedOrder(order)}>
                <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                        <span className="font-bold text-gray-900 bg-gray-100 px-2.5 py-1 rounded-lg text-xs inline-block w-fit border border-gray-200">{String(order.orderid).replace(/^#/, '')}</span>
                        {order.woo_order_id && (
                            <span className="font-bold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-lg text-[10px] inline-block w-fit border border-indigo-100 flex items-center gap-1">
                                {order.woo_order_id}
                            </span>
                        )}
                    </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                    <p
                        className="font-semibold text-indigo-600 hover:text-indigo-800 cursor-pointer hover:underline"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (customer) onCustomerClick(customer);
                        }}
                    >
                        {customer?.name || 'N/A'}
                    </p>
                    <p>{customer?.mobile || 'N/A'}</p>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                    <p>{customer?.state || 'N/A'}</p>
                    <p className="text-xs text-gray-400">{customer?.city || ''}</p>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <pre className="text-xs font-mono whitespace-pre-wrap">{productsOrdered}</pre>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-indigo-600">{formatCurrency(total, generalSettings?.currency || 'GBP')}<br /><span className="text-xs text-gray-500">Shipping: {getCurrencySymbol(generalSettings?.currency || 'GBP')}{Math.floor(shippingFee)}</span></td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                    <Badge color={order.payment === 'Visa' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>{order.payment}</Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end flex-wrap gap-1">
                        {possibleActions.map(act => {
                            const isButton = ['perm_delete'].includes(act.action);
                            if (isButton) {
                                return (
                                    <Button key={act.action} size="sm" color={act.color} onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                            await handleOrderAction(order.orderid, act.action);
                                        } catch (err) {
                                            console.error('Order action failed:', err);
                                        }
                                    }} tooltip={act.label}>
                                        <Icon path={ICONS_MAP[act.icon]} className="w-4 h-4" /> {act.label.split(' ')[0]}
                                    </Button>
                                );
                            } else {
                                return (
                                    <IconButton
                                        key={act.action}
                                        icon={act.icon}
                                        color={act.color}
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                                await handleOrderAction(order.orderid, act.action);
                                            } catch (err) {
                                                console.error('Order action failed:', err);
                                            }
                                        }}
                                        tooltip={act.label}
                                    />
                                );
                            }
                        })}
                    </div>
                </td>
            </tr>
        );
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">{t('deletedOrders')}</h1>
                <div className="flex gap-2">
                    <Button onClick={toggleViewMode} color="gray" size="sm">
                        {viewMode === 'auto' ? t('auto') : viewMode === 'table' ? t('table') : t('cards')}
                    </Button>
                    <Button onClick={() => setView('Orders Board')} color="gray">{t('back')} {t('to')} {t('ordersBoard')}</Button>
                </div>
            </div>

            <CategoryTabs
                currentStatus={activeTab}
                setActiveTab={setActiveTab}
                ordersByStatus={ordersByStatus}
                statusConfig={STATUS_CONFIG}
                isMobile={isMobile}
                totalRevenue={totalRevenue}
                visibleStatuses={['deleted']}
                generalSettings={generalSettings}
            />

            {effectiveViewMode === 'table' ? (
                <Card className="w-full border-t-4 border-gray-500">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[80px]">{t('orderId')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[120px]">{t('customer')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[100px]">{t('location')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[200px]">{t('products')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[80px]">{t('total')}</th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[80px]">{t('payment')}</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[140px]">{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {Object.entries(ordersByStatus[activeTab] || {})
                                    .sort(([a], [b]) => (b === 'Cash' ? 1 : 0) - (a === 'Cash' ? 1 : 0))
                                    .map(([paymentMethod, ordersList]) => {
                                        if (ordersList.length === 0) return null;
                                        const revenue = ordersList.reduce((sum, order) => {
                                            const customer = custMap.get(order.customerid);
                                            const shippingFee = customer ? (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;
                                            return sum + (safeCoerceNumber(order.total) - shippingFee);
                                        }, 0);
                                        return (
                                            <React.Fragment key={paymentMethod}>
                                                <tr className={paymentMethod === 'Cash' ? 'bg-green-600' : 'bg-blue-600'}>
                                                    <td colSpan="7" className="px-6 py-2 text-md font-semibold text-white">
                                                        <div className="flex justify-between items-center">
                                                            <span className="flex items-center"><Icon path={ICONS_MAP[paymentMethod === 'Cash' ? 'cash' : 'creditcard']} className="w-5 h-5 mr-2 text-white" /> {paymentMethod} ({ordersList.length} {t('orders')})</span>
                                                            <span className="font-bold text-white">
                                                                <span className="hidden sm:inline">{t('revenue')}: </span>
                                                                {formatCurrency(revenue, generalSettings?.currency || 'GBP')}
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                                {ordersList.map(order => <OrderRow key={order.orderid} order={order} onCustomerClick={setSelectedCustomerForHistory} />)}
                                            </React.Fragment>
                                        )
                                    })}
                                {ordersInActiveTab.length === 0 && (
                                    <tr><td colSpan="7" className="text-center py-6 text-gray-500 italic">{t('noDeletedOrders')}</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                <div className="space-y-4">
                    {Object.entries(ordersByStatus[activeTab] || {})
                        .sort(([a], [b]) => (b === 'Cash' ? 1 : 0) - (a === 'Cash' ? 1 : 0))
                        .map(([paymentMethod, ordersList]) => (
                            <div key={paymentMethod} className="space-y-2">
                                <div className={`flex justify-between items-center px-4 py-2 rounded-lg ${paymentMethod === 'Cash' ? 'bg-green-600' : 'bg-blue-600'}`}>
                                    <span className="flex items-center font-semibold text-white">
                                        <Icon path={ICONS_MAP[paymentMethod === 'Cash' ? 'cash' : 'creditcard']} className="w-5 h-5 mr-2 text-white" />
                                        {paymentMethod} ({ordersList.length} Orders)
                                    </span>
                                    <span className="font-bold text-white">
                                        <span className="hidden sm:inline">{t('revenue')}: </span>
                                        {formatCurrency(ordersList.reduce((sum, order) => {
                                            const customer = custMap.get(order.customerid);
                                            const shippingFee = customer ? (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;
                                            return sum + (safeCoerceNumber(order.total) - shippingFee);
                                        }, 0), generalSettings?.currency || 'GBP')}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 gap-4">
                                    {ordersList.map(order => (
                                        <MobileOrderCard
                                            key={order.orderid}
                                            order={order}
                                            customer={custMap.get(order.customerid)}
                                            onClick={() => setSelectedOrder(order)}
                                            handleOrderAction={handleOrderAction}
                                            statusConfig={STATUS_CONFIG}
                                            settings={settings}
                                            generalSettings={generalSettings}
                                            formatCurrency={formatCurrency}
                                            shippingFees={shippingFees}
                                            onCustomerClick={setSelectedCustomerForHistory}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    {ordersInActiveTab.length === 0 && (
                        <div className="text-center py-6 text-gray-500 italic">{t('noDeletedOrders')}</div>
                    )}
                </div>
            )}
        </div>
    );
}

function NewOrderForm({ customers, products, returnedPool, onSave, setView, bottles, oils, shippingFees, calculateBuildableStock, onSaveCustomer, settings, generalSettings, formatCurrency, getProductCost, orders, wooImportData, clearWooImportData }) {
    const { t } = useLanguage();
    const primaryColor = settings?.primaryColor || 'indigo';
    const [customerId, setCustomerId] = useState('');
    const [items, setItems] = useState([{ pid: '', qty: 1, used: false }]);
    const [orderTotal, setOrderTotal] = useState('');
    const [discount, setDiscount] = useState(0);
    const [payment, setPayment] = useState('Cash');
    const [orderDate, setOrderDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [notes, setNotes] = useState('');
    const [attachment, setAttachment] = useState('');
    const [validationError, setValidationError] = useState('');
    const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
    const [itemSearches, setItemSearches] = useState([]);
    const [dropdownOpens, setDropdownOpens] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [localWooOrderId, setLocalWooOrderId] = useState(null);
    const [localWooOrderData, setLocalWooOrderData] = useState(null);
    const customerDropdownRef = React.useRef(null);

    const custMap = useMemo(() => new Map(customers.map(c => [c.id, c])), [customers]);
    const prodMap = useMemo(() => new Map(products.map(p => [p.product, p])), [products]);
    const oilMap = useMemo(() => new Map(oils.map(o => [o.oil, o])), [oils]);
    const bottleMap = useMemo(() => new Map(bottles.map(b => [b.id, b])), [bottles]);
    const shippingMap = useMemo(() => new Map(shippingFees.map(s => [`${s.state?.trim()}-${s.city?.trim()}`, s])), [shippingFees]);

    // Available products must be filtered based on buildable stock OR returned stock
    // Sort by size order: 30ml, 50ml, 100ml, 20ml (pocket)
    const availableProducts = useMemo(() => {
        const itemPids = new Set(items.map(i => i.pid).filter(Boolean));
        const filtered = products.filter(p => calculateBuildableStock(p) > 0 || (returnedPool[p.product] || 0) > 0 || itemPids.has(p.product));
        const sizeOrder = ['30ml', '50ml', '100ml', '20ml', 'pocket'];
        return filtered.sort((a, b) => {
            // First sort by oil name
            const oilA = a.oil || '';
            const oilB = b.oil || '';
            const oilCompare = oilA.localeCompare(oilB);
            if (oilCompare !== 0) return oilCompare;

            // Then sort by size
            const sizeA = a.size || '';
            const sizeB = b.size || '';
            const indexA = sizeOrder.indexOf(sizeA);
            const indexB = sizeOrder.indexOf(sizeB);

            // If both sizes are in the order array, sort by their position
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            // If only one is in the array, prioritize it
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            // Otherwise, sort alphabetically
            return sizeA.localeCompare(sizeB);
        });
    }, [products, calculateBuildableStock, returnedPool, items]);

    useEffect(() => {
        const newSearches = items.map(item => item.pid ? (prodMap.get(item.pid)?.product || '') : '');
        setItemSearches(newSearches);
        setDropdownOpens(new Array(items.length).fill(false));
    }, [items, prodMap]);

    // Close customer dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
                setShowCustomerDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // --- WOOCOMMERCE IMPORT PREFILL LOGIC ---
    useEffect(() => {
        if (wooImportData) {
            console.log("📥 Pre-filling NewOrderForm with WooCommerce Data:", wooImportData);

            if (wooImportData.customerId) {
                setCustomerId(wooImportData.customerId);
                // Fix: 7.1 Customer Search UI lock
                const cust = custMap.get(wooImportData.customerId);
                if (cust) setCustomerSearch(cust.name);
            }

            if (wooImportData.items && wooImportData.items.length > 0) {
                setItems(wooImportData.items);
                setItemSearches(wooImportData.items.map(i => prodMap.get(i.pid)?.product || ''));
                setDropdownOpens(new Array(wooImportData.items.length).fill(false));
            }

            if (wooImportData.payment) setPayment(wooImportData.payment);
            if (wooImportData.notes) setNotes(wooImportData.notes);
            if (wooImportData.attachment) setAttachment(wooImportData.attachment);

            if (wooImportData.total) {
                // Fix: 7.2 reverse-calculate discount to ensure Exact WooCommerce total
                let wooSubtotal = 0;
                wooImportData.items.forEach(item => {
                    const product = prodMap.get(item.pid);
                    if (product) wooSubtotal += safeCoerceNumber(product.price) * safeCoerceNumber(item.qty);
                });
                const cust = custMap.get(wooImportData.customerId);
                const wooShipping = cust ? (safeCoerceNumber(shippingMap.get(`${cust.state?.trim()}-${cust.city?.trim()}`)?.fees) || 0) : 0;

                const expectedTotal = safeCoerceNumber(wooImportData.total);
                const calculatedDiscount = (wooSubtotal + wooShipping) - expectedTotal;
                setDiscount(calculatedDiscount > 0 ? calculatedDiscount : 0);
                setTimeout(() => setOrderTotal(expectedTotal.toFixed(2)), 50); // Ensure setter overrides useEffect calculation
            }

            if (wooImportData.woo_order_id) setLocalWooOrderId(wooImportData.woo_order_id);
            if (wooImportData.wooOrderData) setLocalWooOrderData(wooImportData.wooOrderData);

            // Important: Clear the data to prevent infinite loops or stale prefill on next visit
            if (clearWooImportData) clearWooImportData();
        }
    }, [wooImportData, clearWooImportData, prodMap, custMap, shippingMap]);

    // Filter customers by search term (name or mobile)
    const filteredCustomers = useMemo(() => {
        if (!customerSearch.trim()) return customers;
        const searchLower = customerSearch.toLowerCase();
        return customers.filter(c =>
            (c.name && c.name.toLowerCase().includes(searchLower)) ||
            (c.mobile && c.mobile.includes(customerSearch))
        );
    }, [customers, customerSearch]);

    const selectedCustomer = custMap.get(customerId);

    const getCustomerRank = (customer, orders) => {
        if (!customer) return { text: '', color: 'gray' };
        const completedOrdersCount = orders.filter(order => order.customerid === customer.id && order.status === 'completed').length;
        if (completedOrdersCount === 0) return { text: t('newRank'), color: 'blue' };
        if (completedOrdersCount >= 1 && completedOrdersCount <= 2) return { text: t('returningRank'), color: 'green' };
        if (completedOrdersCount >= 3 && completedOrdersCount <= 5) return { text: t('regularRank'), color: 'indigo' };
        if (completedOrdersCount >= 6 && completedOrdersCount <= 10) return { text: t('vipRank'), color: 'purple' };
        return { text: t('eliteRank', { count: completedOrdersCount }), color: 'pink' };
    };
    const rank = getCustomerRank(selectedCustomer, orders, t);

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };
    const addItem = () => {
        setItems([...items, { pid: '', qty: 1, used: false }]);
        setItemSearches([...itemSearches, '']);
        setDropdownOpens([...dropdownOpens, false]);
    };
    const removeItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
        setItemSearches(itemSearches.filter((_, i) => i !== index));
        setDropdownOpens(dropdownOpens.filter((_, i) => i !== index));
    };

    // Helper to calculate product component costs
    const getProductComponentCosts = useCallback((productInfo) => {
        if (!productInfo) return { oilCost: 0, bottleCost: 0 };
        const oilInfo = oilMap.get(productInfo.oil);
        let bottleId;
        if (productInfo.product?.toLowerCase().includes('tester')) {
            bottleId = `tester`; // Fixed key for testers
        } else {
            bottleId = `${productInfo.size}${productInfo.gender?.toLowerCase().replace(/ /g, '')}`;
        }
        const bottleInfo = bottleMap.get(bottleId);
        if (!oilInfo || !bottleInfo) return { oilCost: 0, bottleCost: 0 };
        const oilCost = safeCoerceNumber(oilInfo.price) * safeCoerceNumber(productInfo.qtyoilinbottle);
        const bottleCost = safeCoerceNumber(bottleInfo.totalcostbottle);
        return { oilCost, bottleCost };
    }, [oilMap, bottleMap]);

    const itemsSubtotal = useMemo(() => {
        return items.reduce((sum, item) => {
            const product = prodMap.get(item.pid);
            return sum + (product ? safeCoerceNumber(product.price) * safeCoerceNumber(item.qty) : 0);
        }, 0);
    }, [items, prodMap]);

    const shipping = selectedCustomer ? (safeCoerceNumber(shippingMap.get(`${selectedCustomer.state?.trim()}-${selectedCustomer.city?.trim()}`)?.fees) || 0) : 0;

    // Auto-calculated Subtotal now includes shipping
    const subtotalWithShipping = itemsSubtotal + shipping;

    const calculatedNetTotal = subtotalWithShipping - safeCoerceNumber(discount);

    useEffect(() => {
        setOrderTotal(calculatedNetTotal > 0 ? calculatedNetTotal.toFixed(2) : '');
    }, [calculatedNetTotal, setOrderTotal]);

    const cost = useMemo(() => {
        // FIX: Goods Cost calculation now correctly references the prop passed down.
        return items.reduce((sum, item) => {
            const productInfo = prodMap.get(item.pid);
            if (!productInfo) return sum;

            // Get cost product using the passed utility function
            const calculatedCostProduct = getProductCost(productInfo);

            // Get bottle cost component for 'used' item deduction
            let bottleId;
            if (productInfo.product?.toLowerCase().includes('tester')) { bottleId = `tester`; }
            else { bottleId = `${productInfo.size}${productInfo.gender?.toLowerCase().replace(/ /g, '')}`; }
            const bottleInfo = bottleMap.get(bottleId);
            const bottleCost = safeCoerceNumber(bottleInfo?.totalcostbottle);

            // Deduct bottle cost only if a returned (used) bottle is being used
            const lineCost = (calculatedCostProduct - (item.used ? bottleCost : 0)) * safeCoerceNumber(item.qty);
            return sum + lineCost;
        }, 0);
    }, [items, prodMap, getProductCost, bottleMap]); // Added getProductCost to dependency array

    const profit = safeCoerceNumber(orderTotal) - cost - shipping;

    const handleSubmit = (e) => {
        e.preventDefault();
        setValidationError('');
        if (!customerId) { setValidationError('Please select a customer.'); return; }

        for (const item of items) {
            if (!item.pid || safeCoerceNumber(item.qty) <= 0) { setValidationError('Please ensure all order lines are complete and have a valid quantity.'); return; }
            if (item.used) {
                const poolKey = item.pid;
                const available = returnedPool[poolKey] || 0;
                if (safeCoerceNumber(item.qty) > available) { setValidationError(`Cannot use ${item.qty} bottle(s) for ${item.pid}. Only ${available} available in returned pool.`); return; }
            }
        }
        onSave({
            customerid: customerId,
            items: items.filter(item => item.pid), // filter out empty lines before saving
            total: safeCoerceNumber(orderTotal),
            payment,
            discount: safeCoerceNumber(discount),
            date: new Date(orderDate).toISOString(),
            notes: notes,
            attachment,
            woo_order_id: localWooOrderId || null, // Ensure WooCommerce ID is saved
            wooOrderData: localWooOrderData || null // Pass wooOrderData to ensure extra info is available
        });
    };

    if (showNewCustomerForm) {
        return <NewCustomerForm onSave={async (c) => {
            const savedCustomer = await onSaveCustomer({ ...c, completed: 0 });
            setCustomerId(savedCustomer.id); // Use the UUID returned from database
            setCustomerSearch(savedCustomer.name); // Update search field with customer name
            setShowNewCustomerForm(false);
        }} setView={() => setShowNewCustomerForm(false)} shippingFees={shippingFees} isModal={true} settings={settings} wooImportData={wooImportData} />;
    }

    const fallbackImage = `https://placehold.co/32x32/4f46e5/ffffff?text=LV`;

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{t('newOrderTitle')}</h1>
                <Button onClick={() => setView('Orders Board')} color="gray">{t('backToBoard')}</Button>
            </div>
            {validationError && (<div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert"><span className="font-medium">{t('error')}!</span> {validationError}</div>)}
            <div className="bg-white p-6 rounded-[1.5rem] shadow-sm border border-gray-100 mb-6">
                <div className="flex justify-between items-center mb-5">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <Icon path={ICONS_MAP['customers'] || ICONS_MAP['edit']} className={`w-5 h-5 text-${primaryColor}-500`} />
                        {t('customerDetails')}
                    </h2>
                    <button type="button" onClick={() => setShowNewCustomerForm(true)} className={`flex items-center gap-1 rounded-full px-4 py-1.5 bg-${primaryColor}-50 text-${primaryColor}-700 font-bold text-sm hover:bg-${primaryColor}-100 transition-colors`}>
                        <Icon path={ICONS_MAP['add']} className="w-4 h-4" /> {t('newButton')}
                    </button>
                </div>
                <div className="flex flex-col gap-4">
                    <div>
                        <div className="relative flex-1" ref={customerDropdownRef}>
                            <Icon path={ICONS_MAP['search']} className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                id="customer"
                                placeholder={t('searchByNameOrMobile')}
                                value={customerSearch}
                                onChange={(e) => {
                                    setCustomerSearch(e.target.value);
                                    setShowCustomerDropdown(true);
                                }}
                                onFocus={() => setShowCustomerDropdown(true)}
                                className={`block w-full pl-10 pr-10 py-3 text-sm font-medium border-gray-200 bg-gray-50 rounded-xl transition-all focus:bg-white focus:ring-2 focus:ring-${primaryColor}-500`}
                            />
                            {customerSearch && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCustomerSearch('');
                                        setCustomerId('');
                                        setShowCustomerDropdown(false);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-200 rounded-full transition-colors"
                                    title={t('clearSearchTitle')}
                                >
                                    <Icon path={ICONS_MAP['close']} className="w-4 h-4 text-gray-500" />
                                </button>
                            )}
                            {showCustomerDropdown && filteredCustomers.length > 0 && (
                                <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-xl max-h-60 overflow-y-auto overflow-hidden">
                                    {filteredCustomers.map(c => (
                                        <div
                                            key={c.id}
                                            onClick={() => {
                                                setCustomerId(c.id);
                                                setCustomerSearch(c.name);
                                                setShowCustomerDropdown(false);
                                            }}
                                            className={`px-4 py-3 hover:bg-${primaryColor}-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors`}
                                        >
                                            <div className="font-bold text-gray-900">{c.name}</div>
                                            <div className="text-xs text-gray-500 font-medium">{c.mobile}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    {selectedCustomer && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-center bg-gray-50/50 border border-gray-100 p-5 rounded-2xl mt-2">
                            <div className="text-sm">
                                <span className="block text-[10px] font-bold text-gray-400 mb-1 tracking-wider uppercase">{t('phone')}</span>
                                <span className="font-semibold text-gray-800">{selectedCustomer.mobile}</span>
                            </div>
                            <div className="text-sm">
                                <span className="block text-[10px] font-bold text-gray-400 mb-1 tracking-wider uppercase">{t('location')}</span>
                                <span className="font-semibold text-gray-800">{selectedCustomer.city}, {selectedCustomer.state}</span>
                            </div>
                            <div className="text-sm">
                                <span className="block text-[10px] font-bold text-gray-400 mb-1 tracking-wider uppercase">{t('shippingFee')}</span>
                                <span className="font-semibold text-gray-800">{getCurrencySymbol(generalSettings.currency)}{Math.floor(shipping)}</span>
                            </div>
                            <div className="text-sm">
                                <span className="block text-[10px] font-bold text-gray-400 mb-1 tracking-wider uppercase">{t('rank')}</span>
                                <Badge color={`bg-${rank.color}-50 text-${rank.color}-700 border border-${rank.color}-200 shadow-sm flex items-center gap-1 w-fit`}>
                                    {rank.text}
                                </Badge>
                            </div>
                            {selectedCustomer.cancels?.length > 0 && (<div className="col-span-full bg-red-50 p-3 rounded-xl text-red-700 text-sm font-medium border border-red-100 flex items-center gap-2 mt-2"><Icon path={ICONS_MAP['warning']} className="w-5 h-5 text-red-500" />{t('cancelledBefore')}: {selectedCustomer.cancels.length} {t('times')}</div>)}
                        </div>
                    )}
                </div>
            </div>
            <div className="bg-white p-6 rounded-[1.5rem] shadow-sm border border-gray-100 mb-6 flex flex-col gap-4">
                <div className="flex justify-between items-center mb-2">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <Icon path={ICONS_MAP['stock'] || ICONS_MAP['edit']} className={`w-5 h-5 text-${primaryColor}-500`} />
                        {t('orderItems')}
                    </h2>
                    <Button onClick={addItem} color="gray" size="sm" className={`bg-${primaryColor}-50 text-${primaryColor}-700 hover:bg-${primaryColor}-100 border-none rounded-full px-4 font-bold`}>
                        <Icon path={ICONS_MAP['add']} className="w-4 h-4 mr-1" /> {t('addProduct')}
                    </Button>
                </div>

                <div className="space-y-4">
                    {items.map((item, index) => {
                        const product = prodMap.get(item.pid);
                        const availableReturned = returnedPool[item.pid] || 0;
                        return (
                            <div key={index} className="flex flex-col xl:flex-row items-start xl:items-center gap-4 p-5 bg-white border border-gray-100 rounded-2xl shadow-sm transition-all hover:shadow-md relative">
                                {/* Product Search / Image wrapper */}
                                <div className="flex-1 w-full xl:w-auto flex items-center gap-4">
                                    {product ? (
                                        <img src={product.imageurl || fallbackImage} alt={product.product} className="w-16 h-16 rounded-xl object-cover border border-gray-100 shadow-sm mix-blend-multiply flex-shrink-0" onError={(e) => e.target.src = fallbackImage} />
                                    ) : (
                                        <div className="w-16 h-16 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0">
                                            <Icon path={ICONS_MAP['stock']} className="w-6 h-6" />
                                        </div>
                                    )}
                                    <div className="relative w-full">
                                        <input
                                            type="text"
                                            value={itemSearches[index] || ''}
                                            onChange={(e) => setItemSearches(prev => { const newArr = [...prev]; newArr[index] = e.target.value; return newArr; })}
                                            onFocus={() => setDropdownOpens(prev => { const newArr = [...prev]; newArr[index] = true; return newArr; })}
                                            onBlur={() => setTimeout(() => setDropdownOpens(prev => { const newArr = [...prev]; newArr[index] = false; return newArr; }), 150)}
                                            placeholder={t('searchAndSelectProduct')}
                                            className={`block w-full pl-4 pr-10 py-3 text-sm font-semibold border border-gray-200 bg-gray-50 rounded-xl transition-all focus:bg-white focus:ring-2 focus:border-${primaryColor}-500 focus:ring-${primaryColor}-100 text-gray-800 ${product && calculateBuildableStock(product) < (item.qty || 1) && !item.used ? 'border-red-400 bg-red-50 text-red-800' : ''}`}
                                        />
                                        {/* Out of Stock Warning */}
                                        {product && !item.used && calculateBuildableStock(product) < (item.qty || 1) && (
                                            <div className="absolute top-full left-0 mt-1 text-[0.7rem] text-red-600 bg-red-100 px-2 py-0.5 rounded shadow-sm z-0 font-bold whitespace-nowrap">
                                                {t('error')}! ({t('available')}: {calculateBuildableStock(product)})
                                            </div>
                                        )}
                                        {(dropdownOpens[index] || false) && (() => {
                                            const search = itemSearches[index] || '';
                                            const filtered = availableProducts.filter(p => p.product.toLowerCase().includes(search.toLowerCase()));
                                            return filtered.length > 0 ? (
                                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-100 rounded-xl shadow-xl max-h-60 overflow-y-auto overflow-hidden">
                                                    {filtered.map(p => (
                                                        <div key={p.product} onClick={() => {
                                                            handleItemChange(index, 'pid', p.product);
                                                            setItemSearches(prev => { const newArr = [...prev]; newArr[index] = p.product; return newArr; });
                                                            setDropdownOpens(prev => { const newArr = [...prev]; newArr[index] = false; return newArr; });
                                                        }} className={`px-4 py-3 hover:bg-${primaryColor}-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors`}>
                                                            <div className="font-bold text-gray-900">{p.product}</div>
                                                            <div className={`text-xs text-${primaryColor}-600 font-semibold`}>{t('available')}: {calculateBuildableStock(p)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                </div>

                                {/* Controls row */}
                                <div className="flex flex-wrap items-center justify-between xl:justify-end gap-4 w-full xl:w-auto mt-4 xl:mt-0 pt-4 xl:pt-0 border-t xl:border-t-0 border-gray-100">
                                    {/* Quantity */}
                                    <div className="flex items-center bg-gray-50 rounded-full border border-gray-200 overflow-hidden shadow-sm">
                                        <button
                                            type="button"
                                            onClick={() => handleItemChange(index, 'qty', Math.max(1, (item.qty || 1) - 1))}
                                            className="w-10 h-10 flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none transition-colors"
                                        >−</button>
                                        <input
                                            type="number"
                                            value={item.qty || ''}
                                            onChange={e => {
                                                const val = e.target.value;
                                                if (val === '') handleItemChange(index, 'qty', '');
                                                else handleItemChange(index, 'qty', Math.max(1, parseInt(val) || 1));
                                            }}
                                            onBlur={e => {
                                                if (e.target.value === '' || parseInt(e.target.value) < 1) handleItemChange(index, 'qty', 1);
                                            }}
                                            min="1"
                                            className="w-12 text-center bg-transparent border-none font-bold text-gray-800 focus:ring-0 p-0"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleItemChange(index, 'qty', (item.qty || 1) + 1)}
                                            className="w-10 h-10 flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none transition-colors"
                                        >+</button>
                                    </div>

                                    {/* Price */}
                                    <div className="text-right min-w-[80px]">
                                        <span className="block text-[10px] font-bold text-gray-400 uppercase xl:hidden mb-1">{t('price')}</span>
                                        <p className="font-extrabold text-lg text-gray-900">{formatCurrency(product ? product.price : 0, generalSettings.currency)}</p>
                                    </div>

                                    {/* Badges & Actions */}
                                    <div className="flex gap-2 items-center">
                                        {(availableReturned > 0 || item.used) && (
                                            <label className={`cursor-pointer flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${item.used ? 'bg-green-50 text-green-700 border-green-200 shadow-sm' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'}`}>
                                                <input type="checkbox" checked={item.used} onChange={e => handleItemChange(index, 'used', e.target.checked)} disabled={availableReturned === 0 && !item.used} className="hidden" />
                                                {item.used && <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>}
                                                {item.used ? t('returned').toUpperCase() : t('returned')} <span className="opacity-60 font-semibold">({availableReturned})</span>
                                            </label>
                                        )}
                                        {items.length > 1 && (
                                            <button type="button" onClick={() => removeItem(index)} className="p-2 ml-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors flex items-center justify-center">
                                                <Icon path={ICONS_MAP['trash']} className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
                <button type="button" onClick={addItem} className="mt-2 w-full py-4 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 font-bold flex items-center justify-center gap-2 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 transition-all">
                    <Icon path={ICONS_MAP['add']} className="w-5 h-5" /> {t('addAnotherProduct')}
                </button>
            </div>

            <div className="bg-white p-6 rounded-[1.5rem] shadow-sm border border-gray-100 mb-6 font-bold">
                <h2 className="text-lg font-extrabold mb-5 text-gray-800 flex items-center gap-2">
                    <Icon path={ICONS_MAP['dashboard'] || ICONS_MAP['edit']} className={`w-5 h-5 text-${primaryColor}-500`} />
                    {t('totalsAndProfit')}
                </h2>

                <div className="flex flex-col lg:flex-row gap-6">
                    {/* Left: Summary Cards */}
                    <div className="flex-1 grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 border border-gray-100 p-4 rounded-2xl flex flex-col justify-center items-center text-center shadow-sm">
                            <p className="text-xs font-bold text-gray-400 tracking-wider uppercase mb-1">{t('itemsSubtotal')}</p>
                            <p className="text-xl font-extrabold text-gray-800">{formatCurrency(itemsSubtotal, generalSettings.currency)}</p>
                        </div>
                        <div className="bg-gray-50 border border-gray-100 p-4 rounded-2xl flex flex-col justify-center items-center text-center shadow-sm">
                            <p className="text-xs font-bold text-gray-400 tracking-wider uppercase mb-1">{t('shippingFee')}</p>
                            <p className="text-xl font-extrabold text-gray-800">{formatCurrency(shipping, generalSettings.currency)}</p>
                        </div>
                        <div className="bg-red-50 border border-red-100 p-4 rounded-2xl flex flex-col items-center justify-center text-center shadow-sm">
                            <label htmlFor="discount" className="text-xs font-bold text-red-500 tracking-wider uppercase mb-1 cursor-pointer">{t('discount')}</label>
                            <div className="flex items-center justify-center bg-white rounded-xl px-2 py-1 border border-red-200 w-full max-w-[120px]">
                                <span className="text-lg font-bold text-red-800">-{getCurrencySymbol(generalSettings.currency)}</span>
                                <input type="number" id="discount" value={discount} onChange={e => setDiscount(safeCoerceNumber(e.target.value))} min="0" className={`flex-1 w-full text-lg font-bold text-center border-none bg-transparent ${FOCUSED_INPUT_STYLE} focus:ring-0 text-red-900 p-0 m-0`} />
                            </div>
                        </div>
                        <div className={`bg-${primaryColor}-50 border border-${primaryColor}-100 p-4 rounded-2xl flex flex-col justify-center items-center text-center shadow-sm`}>
                            <p className={`text-xs font-bold text-${primaryColor}-500 tracking-wider uppercase mb-1`}>{t('netSale')}</p>
                            <p className={`text-xl font-extrabold text-${primaryColor}-900`}>{formatCurrency(calculatedNetTotal, generalSettings.currency)}</p>
                        </div>
                    </div>

                    {/* Right: Profit & Inputs */}
                    <div className="lg:w-[400px] flex flex-col gap-4">
                        {/* Estimated Profit card (light blue) */}
                        <div className="bg-blue-50 border border-blue-100 p-4 rounded-2xl shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-blue-700 text-[10px] font-bold tracking-wider uppercase">{t('estProfit')}</p>
                                <span className="text-[10px] font-bold text-blue-600 bg-blue-100/80 px-2.5 py-1 rounded-full">{t('margin')}</span>
                            </div>
                            <div className="flex items-end justify-between">
                                <p className="text-2xl font-extrabold text-blue-900 leading-none">{formatCurrency(profit, generalSettings.currency)}</p>
                                <span className="text-lg font-extrabold text-blue-700 leading-none">{safeCoerceNumber(orderTotal) > 0 ? ((profit / safeCoerceNumber(orderTotal)) * 100).toFixed(0) : 0}%</span>
                            </div>
                        </div>

                        {/* Interactive fields separated */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2 sm:col-span-1 bg-gray-50 border border-gray-200 p-3 rounded-xl focus-within:ring-2 focus-within:ring-indigo-500 focus-within:bg-white transition-all shadow-sm">
                                <label htmlFor="orderTotal" className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{t('finalOrderTotal')}</label>
                                <input type="number" id="orderTotal" value={orderTotal} onChange={e => setOrderTotal(e.target.value)} className="w-full bg-transparent border-none p-0 text-lg font-extrabold text-gray-900 focus:ring-0" />
                            </div>
                            <div className="col-span-2 sm:col-span-1 bg-gray-50 border border-gray-200 p-3 rounded-xl focus-within:ring-2 focus-within:ring-indigo-500 focus-within:bg-white transition-all shadow-sm">
                                <label htmlFor="payment" className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{t('paymentMethod')}</label>
                                <select id="payment" value={payment} onChange={e => setPayment(e.target.value)} className="w-full bg-transparent border-none p-0 text-sm font-bold text-gray-900 focus:ring-0 cursor-pointer">
                                    <option>{t('cashPayment')}</option>
                                    <option>{t('visaPayment')}</option>
                                </select>
                            </div>
                            <div className={`col-span-2 bg-gray-50 border border-gray-200 p-3 rounded-xl focus-within:ring-2 focus-within:ring-${primaryColor}-500 focus-within:bg-white transition-all shadow-sm`}>
                                <label htmlFor="orderDate" className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{t('orderDate')}</label>
                                <input type="date" id="orderDate" value={orderDate} onChange={e => setOrderDate(e.target.value)} className="w-full bg-transparent border-none p-0 text-sm font-bold text-gray-900 focus:ring-0" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-gray-100">
                    <div>
                        <label htmlFor="notes" className="block text-sm font-bold text-gray-700 mb-2">{t('orderNotesOptional')}</label>
                        <textarea id="notes" value={notes} onChange={e => setNotes(e.target.value)} rows="3" className={`block w-full px-4 py-3 border border-gray-200 bg-gray-50 rounded-xl ${FOCUSED_INPUT_STYLE}`} placeholder={t('specialInstructionsPlaceholder')}></textarea>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">{t('attachmentLabel')}</label>
                        <div className="flex gap-2 flex-wrap">
                            <label className="cursor-pointer bg-white hover:bg-gray-50 text-gray-700 px-5 py-3 border border-gray-200 shadow-sm rounded-xl font-semibold text-sm whitespace-nowrap flex items-center gap-2 transition-all">
                                {isUploading ? t('loading') : <><Icon path={ICONS_MAP['export']} className="w-4 h-4 text-gray-400" /> {t('chooseTakePhoto')}</>}
                                <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    setIsUploading(true);
                                    try {
                                        const ext = file.name.split('.').pop();
                                        const path = `${Date.now()}-${Math.random().toString(36).substring(2, 7)}.${ext}`;
                                        const { error } = await supabaseTracked.storage.from('attachment').upload(path, file);
                                        if (error) throw error;
                                        const { data } = supabaseTracked.storage.from('attachment').getPublicUrl(path);
                                        setAttachment(data.publicUrl);
                                    } catch (err) { alert('Upload failed: ' + err.message); }
                                    finally { setIsUploading(false); }
                                }} />
                            </label>
                            {attachment && (
                                <button type="button" onClick={() => setAttachment('')} className="bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2 border border-red-100 rounded-xl font-semibold text-sm transition-all">{t('remove')}</button>
                            )}
                        </div>
                        {attachment && (
                            <a href={attachment} target="_blank" rel="noopener noreferrer" className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-bold flex items-center gap-1 bg-indigo-50 px-3 py-2 rounded-lg w-fit transition-colors">
                                <Icon path={ICONS_MAP['export']} className="w-4 h-4" /> {t('viewAll')}
                            </a>
                        )}
                    </div>
                </div>
            </div>

            <div className="fixed bottom-0 left-0 right-0 md:relative md:bottom-auto md:left-auto md:right-auto bg-white border-t border-gray-100 md:border-0 p-3 md:p-0 md:pt-4 md:border-t md:border-gray-100 flex gap-3 z-40">
                <Button onClick={() => setView('Orders Board')} color="gray" className="flex-1 md:flex-initial py-2.5 rounded-xl font-medium bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-center">{t('cancel')}</Button>
                <Button type="submit" color={settings.primaryColor} className="flex-1 md:flex-initial py-2.5 rounded-xl font-bold shadow-md hover:shadow-lg transition-all text-center">{t('saveOrder')}</Button>
            </div>
            <div className="h-16 md:hidden"></div>
        </form>
    );
}

function NewCustomerForm({ onSave, setView, shippingFees, isModal = false, settings, wooImportData }) {
    const { t } = useLanguage();
    const shippingStates = useMemo(() => [...new Set(shippingFees.map(s => s.state))], [shippingFees]);

    // Pre-fill from WooCommerce import data if available
    const initCustomer = () => {
        const base = { id: `c${Date.now()}`, name: '', mobile: '', email: '', state: '', city: '' };
        const raw = wooImportData?.rawCustomerData;
        if (!raw) return base;

        const incomingState = raw.state || '';

        // Normalize Arabic characters for more resilient matching
        const normalizeArabic = (text) => {
            if (!text) return '';
            return text
                .replace(/[إأآا]/g, 'ا')
                .replace(/ة/g, 'ه')
                .replace(/ى/g, 'ي')
                .trim();
        };

        const normalizedIncoming = normalizeArabic(incomingState);
        let matchedState = shippingStates.find(s => normalizeArabic(s) === normalizedIncoming) || incomingState;

        // Check for exact state match without Arabic normalizations if it fails
        if (matchedState === incomingState && incomingState) {
            const exact = shippingStates.find(s => s.trim().toLowerCase() === incomingState.trim().toLowerCase());
            if (exact) {
                matchedState = exact;
            } else {
                // Try partial match as a last resort
                const partial = shippingStates.find(s => normalizeArabic(s).includes(normalizedIncoming) || normalizedIncoming.includes(normalizeArabic(s)));
                if (partial) matchedState = partial;
                else matchedState = ''; // Force empty so it doesn't leave an invisible unselectable state
            }
        }

        let matchedCity = raw.city || '';
        if (matchedState) {
            const stateCities = shippingFees.filter(s => s.state === matchedState).map(s => s.city);
            const normalizedCity = normalizeArabic(matchedCity);
            const foundCity = stateCities.find(c => normalizeArabic(c) === normalizedCity) || stateCities.find(c => c.trim().toLowerCase() === matchedCity.trim().toLowerCase());
            if (foundCity) matchedCity = foundCity;
            else if (stateCities.length > 0 && !matchedCity) matchedCity = stateCities[0]; // fallback to first city
        }

        return {
            ...base,
            name: [raw.first_name, raw.last_name].filter(Boolean).join(' ') || '',
            mobile: raw.phone || '',
            email: raw.email || '',
            state: matchedState,
            city: matchedCity,
            address: raw.address_1 || '',
        };
    };

    const [customer, setCustomer] = useState(initCustomer);
    const [availableCities, setAvailableCities] = useState([]);
    const [searchMode, setSearchMode] = useState(false); // Toggle between state-first and city-search modes
    const [citySearch, setCitySearch] = useState('');
    const [duplicateWarning, setDuplicateWarning] = useState(null);
    const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
    const [showCityDropdown, setShowCityDropdown] = useState(false);
    const dropdownRef = React.useRef(null);

    // Pre-populate cities dropdown when state is pre-filled (e.g. from WooCommerce import)
    useEffect(() => {
        if (customer.state) {
            const cities = shippingFees.filter(s => s.state === customer.state).map(s => s.city);
            setAvailableCities(cities);
        }
    }, [customer.state, shippingFees]);

    // All cities with their states for search mode
    const allCities = useMemo(() => shippingFees.map(s => ({ city: s.city, state: s.state })), [shippingFees]);

    // Filtered cities based on search
    const filteredCities = useMemo(() => {
        if (!citySearch) return [];
        return allCities.filter(c => c.city.toLowerCase().includes(citySearch.toLowerCase())).slice(0, 10);
    }, [citySearch, allCities]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowCityDropdown(false);
            }
        };

        if (showCityDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCityDropdown]);

    const handleStateChange = (e) => {
        const selectedState = e.target.value;
        setCustomer(prev => ({ ...prev, state: selectedState, city: '' }));
        setAvailableCities(shippingFees.filter(s => s.state === selectedState).map(s => s.city));
    };

    const handleCitySearch = (cityName, stateName) => {
        setCustomer(prev => ({ ...prev, city: cityName, state: stateName }));
        setCitySearch(cityName);
        setShowCityDropdown(false); // Close dropdown on selection
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCustomer(prev => ({ ...prev, [name]: value }));
        // Clear duplicate warning when mobile number changes
        if (name === 'mobile') {
            setDuplicateWarning(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check for duplicate mobile number
        if (customer.mobile) {
            setIsCheckingDuplicate(true);
            try {
                const { data: existingCustomers, error } = await supabaseTracked
                    .from('customers')
                    .select('name, mobile')
                    .eq('mobile', customer.mobile)
                    .limit(1);

                if (error) {
                    console.error('Error checking for duplicate mobile:', error);
                } else if (existingCustomers && existingCustomers.length > 0) {
                    setDuplicateWarning(`This number is already registered for ${existingCustomers[0].name}`);
                    setIsCheckingDuplicate(false);
                    return; // Prevent saving
                }
            } catch (err) {
                console.error('Error checking for duplicate mobile:', err);
            }
            setIsCheckingDuplicate(false);
        }

        // Save customer
        await onSave(customer);

        // Auto-close form after successful save
        if (isModal) {
            setView(false);
        } else if (wooImportData) {
            // After saving the new customer from a WooCommerce import,
            // redirect to New Order so the order data auto-loads
            setView('New Order');
        } else {
            setView('Customers');
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold text-gray-800">{isModal ? t('addNewCustomer') : t('newCustomerTitle')}</h1>
                <Button onClick={() => isModal ? setView(false) : setView('Customers')} color="gray">
                    {isModal ? t('closeForm') : t('backToCustomers')}
                </Button>
            </div>
            <Card><div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label htmlFor="name" className="block text-sm font-medium text-gray-700">{t('name')}</label><input type="text" id="name" name="name" value={customer.name} onChange={handleChange} required className={`mt-1 block w-full pl-3 pr-2 py-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} /></div>
                <div>
                    <label htmlFor="mobile" className="block text-sm font-medium text-gray-700">{t('mobile')}</label>
                    <input
                        type="tel"
                        id="mobile"
                        name="mobile"
                        value={customer.mobile}
                        onChange={handleChange}
                        required
                        className={`mt-1 block w-full pl-3 pr-2 py-2 border rounded-md ${duplicateWarning ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : FOCUSED_INPUT_STYLE}`}
                    />
                    {duplicateWarning && (
                        <div className="mt-2 flex items-center gap-2 text-red-600 text-sm bg-red-50 p-2 rounded-md">
                            <Icon path={ICONS_MAP['warning']} className="w-5 h-5 flex-shrink-0" />
                            <span>{duplicateWarning}</span>
                        </div>
                    )}
                </div>
                <div><label htmlFor="email" className="block text-sm font-medium text-gray-700">{t('email')}</label><input type="email" id="email" name="email" value={customer.email} onChange={handleChange} className={`mt-1 block w-full pl-3 pr-2 py-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} /></div>

                {/* State selector */}
                <div className="relative">
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700">{t('state')}</label>
                    <select id="state" name="state" value={customer.state} onChange={handleStateChange} required className={`mt-1 block w-full pl-3 pr-10 py-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                        <option value="">{t('selectState')}</option>
                        {shippingStates.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>

                {/* City Selector */}
                <div className="relative" ref={dropdownRef}>
                    <div className="flex justify-between items-center mb-1">
                        <label htmlFor="citySearch" className="block text-sm font-medium text-gray-700">{t('city')}</label>
                        <button type="button" onClick={() => setSearchMode(!searchMode)} className="text-sm font-medium text-indigo-600 hover:text-indigo-800">
                            {searchMode ? t('useStateFirst') : t('searchCity')}
                        </button>
                    </div>
                    {searchMode ? (
                        <>
                            <input
                                type="text"
                                id="citySearch"
                                value={citySearch}
                                onChange={(e) => {
                                    setCitySearch(e.target.value);
                                    setShowCityDropdown(true);
                                }}
                                onFocus={() => setShowCityDropdown(true)}
                                placeholder={t('typeToSearchCity')}
                                className={`mt-1 block w-full pl-3 pr-2 py-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}
                            />
                            {showCityDropdown && citySearch && filteredCities.length > 0 && (
                                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                    {filteredCities.map((item, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => handleCitySearch(item.city, item.state)}
                                            className="w-full text-left px-4 py-2 hover:bg-indigo-50 focus:bg-indigo-100"
                                        >
                                            <div className="font-medium">{item.city}</div>
                                            <div className="text-xs text-gray-500">{item.state}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <select id="city" name="city" value={customer.city} onChange={handleChange} required className={`mt-1 block w-full pl-3 pr-10 py-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                            <option value="">{t('selectCity')}</option>
                            {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    )}
                </div>

                {/* Address Field */}
                <div className="md:col-span-2">
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">{t('address')}</label>
                    <textarea
                        id="address"
                        name="address"
                        value={customer.address || ''}
                        onChange={handleChange}
                        rows="2"
                        className={`mt-1 block w-full pl-3 pr-2 py-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}
                        placeholder="Street, Building, Flat..."
                    ></textarea>
                </div>
            </div></Card>
            <div className="flex justify-end space-x-4">
                <Button onClick={() => isModal ? setView(false) : setView('Customers')} color="gray">{t('cancel')}</Button>
                <Button type="submit" color={settings.primaryColor} disabled={isCheckingDuplicate || duplicateWarning}>
                    {isCheckingDuplicate ? t('checking') : t('saveCustomer')}
                </Button>
            </div>
        </form>
    );
}

function StockView({ data, setData, handleFileUpload, getProductCost, calculateBuildableStock, settings, generalSettings, showAlert, setEditingSingleItem, formatCurrency, syncSingleItem, syncMultipleItems, syncObjectData, handleDeleteOil, handleDeleteBottle, handleDeleteVendor, checkLowStockThreshold, isAdmin }) {
    const { t, isRTL } = useLanguage();

    // Get dynamic inventory labels
    const bottlesLabel = data.stockSettings.inventoryLabels?.bottles || t('bottles');
    const oilsLabel = data.stockSettings.inventoryLabels?.oils || t('oils');

    const [subTab, setSubTab] = useState('Products');
    const [editingTable, setEditingTable] = useState(null);
    const [tempData, setTempData] = useState(null);
    const [productFilterSize, setProductFilterSize] = useState('All');
    const [productFilterGender, setProductFilterGender] = useState('All'); // New Gender Filter
    const [productSearch, setProductSearch] = useState(''); // New Search
    const [editingReturned, setEditingReturned] = useState(false);
    const [tempReturned, setTempReturned] = useState({});
    const [newProduct, setNewProduct] = useState('');
    const [newQty, setNewQty] = useState(1);
    const [bulkGenerateModalOpen, setBulkGenerateModalOpen] = useState(false);
    const [selectedOil, setSelectedOil] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedSizesPerCategory, setSelectedSizesPerCategory] = useState({});
    const [productsToGenerate, setProductsToGenerate] = useState([]);
    const [addOilModalOpen, setAddOilModalOpen] = useState(false);
    const [addBottleModalOpen, setAddBottleModalOpen] = useState(false);
    const [newBottleSize, setNewBottleSize] = useState('');
    const [newBottleGender, setNewBottleGender] = useState('');
    const [newBottleStock, setNewBottleStock] = useState('');
    const [newBottleTotalCost, setNewBottleTotalCost] = useState('');
    const [newBottleImageUrl, setNewBottleImageUrl] = useState('');
    const [newBottleOilPerBottle, setNewBottleOilPerBottle] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const filterRef = useRef(null);
    const bottleFilterRef = useRef(null);
    const oilFilterRef = useRef(null);
    const [bottleSearch, setBottleSearch] = useState('');
    const [oilSearch, setOilSearch] = useState('');
    const [showBottleFilters, setShowBottleFilters] = useState(false);
    const [showOilFilters, setShowOilFilters] = useState(false);
    const [bottleFilterSize, setBottleFilterSize] = useState('All');
    const [bottleFilterGender, setBottleFilterGender] = useState('All');
    const [oilFilterType, setOilFilterType] = useState('All');

    // Get primary color from settings for dynamic theming
    const primaryColor = settings?.primaryColor || 'indigo';
    const colorMap = {
        indigo: { text: '#4F46E5' },
        blue: { text: '#2563EB' },
        green: { text: '#059669' },
        purple: { text: '#7C3AED' },
        pink: { text: '#DB2777' },
        orange: { text: '#EA580C' },
        red: { text: '#DC2626' },
        yellow: { text: '#CA8A04' },
        violet: { text: COLOR_PALETTE.violet },
        teal: { text: '#0D9488' },
        cyan: { text: '#0891B2' },
        amber: { text: '#D97706' },
        lime: { text: '#65A30D' },
        fuchsia: { text: '#C026D3' },
        rose: { text: '#E11D48' },
    };
    const themeColor = colorMap[primaryColor]?.text || colorMap.indigo.text;
    const [newOilName, setNewOilName] = useState('');
    const [newOilPrice, setNewOilPrice] = useState('');
    const [newOilType, setNewOilType] = useState('Unisex');
    const [newOilStock, setNewOilStock] = useState('');
    const [newOilVendor, setNewOilVendor] = useState('');
    const [newOilPer5ml, setNewOilPer5ml] = useState(1);
    const [newOilPer20ml, setNewOilPer20ml] = useState(5);
    const [newOilPer30ml, setNewOilPer30ml] = useState(9);
    const [newOilPer50ml, setNewOilPer50ml] = useState(15);
    const [newOilPer100ml, setNewOilPer100ml] = useState(30);
    const [viewMode, setViewMode] = useState(() => {
        try {
            const saved = localStorage.getItem('stockViewMode');
            return saved ? JSON.parse(saved) : { products: 'auto', oils: 'auto', bottles: 'auto', 'returned products': 'auto' };
        } catch {
            return { products: 'auto', oils: 'auto', bottles: 'auto', 'returned products': 'auto' };
        }
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const effectiveViewMode = (subTab) => {
        const mode = viewMode[subTab.toLowerCase()] || 'auto';
        return mode === 'auto' ? (isMobile ? 'cards' : 'table') : mode;
    };

    const toggleViewMode = (subTab) => {
        const current = viewMode[subTab.toLowerCase()] || 'auto';
        const newMode = current === 'table' ? 'cards' : current === 'cards' ? 'auto' : 'table';
        const newViewMode = { ...viewMode, [subTab.toLowerCase()]: newMode };
        setViewMode(newViewMode);
        localStorage.setItem('stockViewMode', JSON.stringify(newViewMode));
    };

    useEffect(() => {
        const list = [];
        if (selectedOil && selectedCategories.length > 0) {
            console.log(`🔍 [GENERATE] Starting product generation...`);
            console.log(`🔍 [GENERATE] Selected Oil: "${selectedOil}"`);
            console.log(`🔍 [GENERATE] Selected Categories:`, selectedCategories);
            console.log(`🔍 [GENERATE] Selected Sizes Per Category:`, selectedSizesPerCategory);
            console.log(`🔍 [GENERATE] Available bottles in database:`, data.bottles.map(b => ({ id: b.id, size: b.size, gender: b.gender })));

            selectedCategories.forEach(gender => {
                const sizes = selectedSizesPerCategory[gender] || [];
                sizes.forEach(size => {
                    const name = `${selectedOil} - ${gender} - ${size}`;

                    // Check if product already exists
                    const exists = data.products.some(p => p.product === name);
                    console.log(`🔍 [GENERATE] Checking product: "${name}" - Exists: ${exists}`);

                    if (!exists) {
                        // Try multiple bottleId patterns to find the bottle
                        let bottleId;
                        let possibleBottleIds = [];

                        if (size === '5ml') {
                            possibleBottleIds = ['tester', '5ml', `5ml${gender.toLowerCase().replace(/ /g, '')}`];
                        } else if (size === '20ml') {
                            possibleBottleIds = ['20mlpockets', '20ml', `20ml${gender.toLowerCase().replace(/ /g, '')}`];
                        } else {
                            // Try different patterns: size+gender, gender+size, just size, etc.
                            possibleBottleIds = [
                                `${size}${gender.toLowerCase().replace(/ /g, '')}`,
                                `${gender.toLowerCase().replace(/ /g, '')}${size}`,
                                `${size.toLowerCase()}${gender.toLowerCase().replace(/ /g, '')}`,
                                size.toLowerCase(),
                                `${size.toLowerCase()}_${gender.toLowerCase().replace(/ /g, '')}`,
                                `${size.toLowerCase()}-${gender.toLowerCase().replace(/ /g, '')}`
                            ];
                        }

                        console.log(`🔍 [GENERATE] Trying bottle IDs:`, possibleBottleIds);

                        let bottleInfo = null;
                        for (const id of possibleBottleIds) {
                            bottleInfo = data.bottles.find(b => b.id === id);
                            if (bottleInfo) {
                                bottleId = id;
                                console.log(`✅ [GENERATE] Found bottle with ID: "${bottleId}"`);
                                break;
                            }
                        }

                        const oilInfo = data.oils.find(o => o.oil === selectedOil);

                        console.log(`🔍 [GENERATE] Bottle found:`, bottleInfo ? `Yes (${bottleInfo.id})` : 'No');
                        console.log(`🔍 [GENERATE] Oil found:`, oilInfo ? `Yes (${oilInfo.oil})` : 'No');

                        if (oilInfo && bottleInfo) {
                            // Calculate oil quantity: 
                            // Priority: 
                            // 1. Oil-specific quantity for this size (e.g. oil_per_50ml)
                            // 2. Bottle's default oilperbottle
                            // 3. Static fallback map
                            const sizeStr = size.toLowerCase().replace(/[^0-9]/g, '');
                            const defaultField = `oil_per_${sizeStr}ml`;
                            const oilSpecificQty = oilInfo[defaultField];

                            const qtyMap = { '30ml': 10, '50ml': 16, '100ml': 32, '20ml': 5, '5ml': 1, '25ml': 8 };
                            const qtyoilinbottle = oilSpecificQty || qtyMap[size] || parseInt(size.replace('ml', '')) || 10;

                            console.log(`🔍 [GENERATE] Oil qty: oilSpecificQty=${oilSpecificQty}, final qty=${qtyoilinbottle}`);

                            const oilCost = safeCoerceNumber(oilInfo.price) * safeCoerceNumber(qtyoilinbottle);
                            const bottleCost = safeCoerceNumber(bottleInfo.totalcostbottle);
                            const costproduct = oilCost + bottleCost;

                            // Price per Size
                            const priceMap = { '30ml': 300, '50ml': 400, '100ml': 650, '20ml': 90, '5ml': 40, '25ml': 150 };
                            const price = priceMap[size] || 0;

                            list.push({
                                product: name,
                                oil: selectedOil,
                                size,
                                gender,
                                qtyoilinbottle,
                                price,
                                qtysold: 0,
                                costproduct,
                                imageurl: ''
                            });
                            console.log(`✅ [GENERATE] Added product to list: "${name}"`);
                        } else {
                            console.warn(`⚠️ [GENERATE] Cannot create product "${name}" - Missing ${!oilInfo ? 'oil' : 'bottle'}`);
                            console.warn(`⚠️ [GENERATE] Tried bottle IDs:`, possibleBottleIds);
                            console.warn(`⚠️ [GENERATE] Available bottles for this category:`, data.bottles.filter(b => b.gender === gender).map(b => b.id));
                        }
                    }
                });
            });
        }
        console.log(`📋 [GENERATE] Total products to generate: ${list.length}`, list);
        setProductsToGenerate(list);
    }, [selectedOil, selectedCategories, selectedSizesPerCategory, data.products, data.bottles, data.oils]);

    const allGenders = ['All', ...new Set(data.products.map(p => p.gender))].filter(Boolean);

    // Get unique categories and sizes from bottles (not products) for Generate Product modal
    const uniqueCategories = useMemo(() => {
        return [...new Set(data.bottles.map(b => b.gender))].filter(Boolean).sort();
    }, [data.bottles]);

    // Get sizes available for each category
    const getSizesForCategory = useCallback((category) => {
        return [...new Set(data.bottles.filter(b => b.gender === category).map(b => b.size))].filter(Boolean).sort();
    }, [data.bottles]);


    const handleEditTable = (tableName) => {
        setEditingTable(tableName);
        setTempData(JSON.parse(JSON.stringify(data[tableName])));
    };
    const handleSaveTable = async () => {
        const normalizedData = tempData.map(row => {
            // Ensure numerical fields are coerced before saving
            if (editingTable === 'bottles') {
                return { ...row, actualstock: safeCoerceNumber(row.actualstock), totalcostbottle: safeCoerceNumber(row.totalcostbottle) };
            } else if (editingTable === 'oils') {
                return { ...row, price: safeCoerceNumber(row.price), actualstock: safeCoerceNumber(row.actualstock) };
            } else if (editingTable === 'products') {
                return { ...row, price: safeCoerceNumber(row.price), qtyoilinbottle: safeCoerceNumber(row.qtyoilinbottle) };
            }
            return row;
        });

        setData({ ...data, [editingTable]: normalizedData });

        // Sync to Supabase
        console.log(`🔄 Syncing ${editingTable} table to Supabase...`);
        await syncMultipleItems(editingTable, normalizedData);

        setEditingTable(null);
        setTempData(null);
        showAlert(`${editingTable} saved successfully.`);
    };
    const handleCancelEdit = () => {
        setEditingTable(null);
        setTempData(null);
    };

    const handleCellChange = (index, field, value) => {
        const newTempData = [...tempData];
        newTempData[index][field] = value;
        if (editingTable === 'products') {
            if (field === 'product') {
                const sizeMatch = value.match(/(\d+ml)/);
                if (sizeMatch) {
                    newTempData[index].size = sizeMatch[1];
                }
            }
            newTempData[index]['costproduct'] = getProductCost(newTempData[index]);
        }
        setTempData(newTempData);
    };

    const returnedProductsData = useMemo(() => { return Object.entries(editingReturned ? tempReturned : data.returnedPool).filter(([key, qty]) => qty > 0).map(([key, qty]) => ({ key, productName: key, qty })); }, [data.returnedPool, editingReturned, tempReturned]);

    const handleAddItem = (tableName) => {
        const newItem = tableName === 'bottles' ? { id: `b${Date.now()}`, size: '', gender: '', actualstock: 0, totalcostbottle: 0, totalordered: 0 } :
            tableName === 'oils' ? { oil: `o${Date.now()}`, price: 0, actualstock: 0, totalordered: 0, oiltype: 'Unisex', vendor_id: null, oil_per_5ml: 1, oil_per_20ml: 5, oil_per_30ml: 9, oil_per_50ml: 15, oil_per_100ml: 30 } :
                tableName === 'vendors' ? { id: `v${Date.now()}`, name: '' } :
                    { product: `p${Date.now()}`, price: 0, qtysold: 0, costproduct: 0, oil: '', qtyoilinbottle: 0, size: '', gender: '', imageurl: '' };
        setTempData(prev => [...(prev || []), newItem]);
    };

    const handleDeleteItem = (index) => {
        const newTempData = [...tempData];
        newTempData.splice(index, 1);
        setTempData(newTempData);
    };

    const oilsGrouped = useMemo(() => {
        return data.oils.reduce((acc, oil) => {
            const type = oil.oiltype || 'Unisex';
            if (!acc[type]) acc[type] = [];
            acc[type].push(oil);
            return acc;
        }, {});
    }, [data.oils]);

    // Helper to normalize gender/category names for consistent filtering and display
    const normalizeGenderLabel = useCallback((g) => {
        if (!g) return '';
        const lower = g.toLowerCase().trim();
        if (lower === 'men' || lower === 'male' || lower === 'boys' || lower === 'for him') return 'For Him';
        if (lower === 'women' || lower === 'female' || lower === 'girls' || lower === 'for her') return 'For Her';
        if (lower === 'tester' || lower === 'testers') return 'Testers';
        if (lower === 'unisex') return 'Unisex';
        // Title Case for others
        return g.charAt(0).toUpperCase() + g.slice(1).toLowerCase();
    }, []);

    const bottlesGrouped = useMemo(() => {
        let filteredBottles = data.bottles;

        // 1. Apply Size Filter
        if (bottleFilterSize !== 'All') {
            filteredBottles = filteredBottles.filter(b => b.size === bottleFilterSize);
        }

        // 2. Apply Gender/Category Filter
        if (bottleFilterGender !== 'All') {
            filteredBottles = filteredBottles.filter(b => normalizeGenderLabel(b.gender) === bottleFilterGender);
        }

        // 3. Apply Search Filter
        if (bottleSearch) {
            filteredBottles = filteredBottles.filter(b =>
                (b.size || '').toLowerCase().includes(bottleSearch.toLowerCase()) ||
                (b.gender || '').toLowerCase().includes(bottleSearch.toLowerCase()) ||
                (b.id || '').toLowerCase().includes(bottleSearch.toLowerCase())
            );
        }

        const grouped = filteredBottles.reduce((acc, bottle) => {
            const type = normalizeGenderLabel(bottle.gender) || 'Unisex';
            if (!acc[type]) acc[type] = [];
            acc[type].push(bottle);
            return acc;
        }, {});

        // Sort bottles within each group by size order
        const sizeOrder = ['30ml', '50ml', '100ml', '20ml', 'tester', 'Tester', '5ml'];
        Object.keys(grouped).forEach(type => {
            grouped[type].sort((a, b) => {
                const indexA = sizeOrder.indexOf(a.size);
                const indexB = sizeOrder.indexOf(b.size);
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;
                return (a.size || '').localeCompare(b.size || '');
            });
        });

        return grouped;
    }, [data.bottles, bottleFilterSize, bottleFilterGender, bottleSearch, normalizeGenderLabel]);

    const productsGrouped = useMemo(() => {
        let filteredProducts = data.products;

        // 1. Apply Size Filter
        if (productFilterSize !== 'All') {
            filteredProducts = filteredProducts.filter(p => p.size === productFilterSize);
        }

        // 2. Apply Gender Filter
        if (productFilterGender !== 'All') {
            filteredProducts = filteredProducts.filter(p => normalizeGenderLabel(p.gender) === productFilterGender);
        }

        // 3. Apply Search Filter
        if (productSearch.trim()) {
            const searchNorm = normalizeSearch(productSearch);
            filteredProducts = filteredProducts.filter(p =>
                (p.product && normalizeSearch(p.product).includes(searchNorm)) ||
                (p.oil && normalizeSearch(p.oil).includes(searchNorm))
            );
        }

        const grouped = filteredProducts.reduce((acc, prod) => {
            const type = normalizeGenderLabel(prod.gender) || 'Unisex';
            if (!acc[type]) acc[type] = [];
            acc[type].push(prod);
            return acc;
        }, {});

        // Sort each group by base name, then by size
        Object.keys(grouped).forEach(type => {
            grouped[type].sort((a, b) => {
                const baseA = a.product.split(' - ')[0] || a.product;
                const baseB = b.product.split(' - ')[0] || b.product;
                const baseCompare = baseA.localeCompare(baseB);
                if (baseCompare !== 0) return baseCompare;
                const sizeOrder = ['30ml', '50ml', '100ml', 'tester', '20ml', '5ml'];
                return sizeOrder.indexOf(a.size) - sizeOrder.indexOf(b.size);
            });
        });

        return grouped;
    }, [data.products, productFilterSize, productFilterGender, productSearch, normalizeGenderLabel]);

    const productSizes = useMemo(() => {
        const sizes = [...new Set(data.products.map(p => p.size))];
        return ['All', ...sizes];
    }, [data.products]);

    const fallbackImage = 'https://placehold.co/32x32/ccc/000?text=IMG';

    // --- Bottle Card View ---
    const BottleCard = ({ bottle }) => (
        <Card className="flex flex-col justify-between h-full hover:shadow-lg transition-shadow p-3 md:p-4">
            <div className="text-center">
                <div className="text-3xl mb-1 md:mb-2" style={{ color: themeColor }}>
                    {bottle.imageurl ? <img src={bottle.imageurl} alt={bottle.size} className="w-12 h-12 md:w-16 md:h-16 mx-auto rounded-lg object-cover" onError={(e) => e.target.src = fallbackImage} /> : <Icon path={ICONS_MAP['stock']} className="w-12 h-12 md:w-16 md:h-16 mx-auto" />}
                </div>
                <h3 className="font-bold text-base md:text-lg text-gray-800">{bottle.size} {bottle.gender}</h3>
            </div>
            <div className="mt-2 md:mt-4 text-center">
                <p className="text-xs md:text-sm text-gray-500">{t('stockAvailable')}</p>
                <p className={`text-lg md:text-xl font-extrabold ${safeCoerceNumber(bottle.actualstock) < 10 ? 'text-red-600' : 'text-green-600'}`}>{safeCoerceNumber(bottle.actualstock)} {t('units')}</p>
            </div>
            <div className="mt-1 md:mt-2 text-center border-t pt-1 md:pt-2">
                <p className="text-xs md:text-sm text-gray-500">{t('unitCost')}</p>
                <p className="font-semibold text-sm md:text-base text-gray-700">{formatCurrency(safeCoerceNumber(bottle.totalcostbottle), generalSettings.currency)}</p>
            </div>
            <div className="mt-1 md:mt-2 text-center flex justify-center gap-1 md:gap-2">
                {isAdmin && <Button onClick={() => setEditingSingleItem({ table: 'bottles', data: bottle })} size="sm" color="blue">{t('edit')}</Button>}
                <StockAdjustButton item={bottle} onDone={async (newStock) => {
                    let updatedBottle;
                    setData(prev => {
                        const newData = { ...prev };
                        const index = newData.bottles.findIndex(b => b.id === bottle.id);
                        if (index > -1) {
                            newData.bottles[index].actualstock = newStock;
                            updatedBottle = newData.bottles[index];
                        }
                        return newData;
                    });
                    if (updatedBottle) {
                        console.log('🔄 Syncing bottle stock to Supabase...');
                        await syncSingleItem('bottles', updatedBottle);
                        // Check if stock is below threshold
                        checkLowStockThreshold('bottles', bottle.id, newStock);
                    }
                    showAlert('Stock updated');
                }} />
            </div>
        </Card>
    );


    // --- Product Card View ---
    const ProductCard = ({ product }) => (
        <Card className="flex flex-col justify-between h-full hover:shadow-lg transition-shadow p-3 md:p-4">
            <div className="text-center">
                {product.imageurl ? <img src={product.imageurl} alt={product.product} className="w-12 h-12 md:w-16 md:h-16 mx-auto rounded-lg object-cover mb-1 md:mb-2" onError={(e) => e.target.src = fallbackImage} /> : <Icon path={ICONS_MAP['stock']} className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-1 md:mb-2" />}
                <h3 className="font-bold text-base md:text-lg text-gray-800">{product.product}</h3>
                <p className="text-xs md:text-sm text-gray-500">{product.size} - {product.gender}</p>
            </div>
            <div className="mt-2 md:mt-4 text-center">
                <p className="text-xs md:text-sm text-gray-500">{t('price')}</p>
                <p className="font-semibold text-sm md:text-base text-gray-700">{formatCurrency(product.price, generalSettings.currency)}</p>
            </div>
            <div className="mt-1 md:mt-2 text-center border-t pt-1 md:pt-2">
                <p className="text-xs md:text-sm text-gray-500">{t('buildableStock')}</p>
                <p className="font-semibold text-sm md:text-base" style={{ color: themeColor }}>{calculateBuildableStock(product)}</p>
            </div>
            <div className="mt-1 md:mt-2 text-center flex justify-center gap-2">
                {isAdmin && <Button onClick={() => setEditingSingleItem({ table: 'products', data: product })} size="sm" color="blue">{t('edit')}</Button>}
            </div>
        </Card>
    );

    // --- Oil Card View ---
    const OilCard = ({ oil }) => {
        const vendorName = data.vendors?.find(v => v.id === oil.vendor_id)?.name;
        return (
            <Card className="flex flex-col justify-between h-full hover:shadow-lg transition-shadow p-3 md:p-4">
                <div className="text-center">
                    <h3 className="font-bold text-base md:text-lg text-gray-800">{oil.oil}</h3>
                    <p className="text-xs md:text-sm text-gray-500">{oil.oiltype}</p>
                </div>
                <div className="mt-2 md:mt-4 text-center">
                    <p className="text-xs md:text-sm text-gray-500">{t('pricePerMl')}</p>
                    <p className="font-semibold text-sm md:text-base text-gray-700">{formatCurrency(oil.price, generalSettings.currency)}</p>
                    {vendorName && <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">Vendor: {vendorName}</p>}
                </div>
                <div className="mt-1 md:mt-2 text-center border-t pt-1 md:pt-2">
                    <p className="text-xs md:text-sm text-gray-500">{t('stockMl')}</p>
                    <p className={`font-semibold text-sm md:text-base ${safeCoerceNumber(oil.actualstock) < 50 ? 'text-red-600' : 'text-green-600'}`}>{safeCoerceNumber(oil.actualstock)}</p>
                </div>
                <div className="mt-1 md:mt-2 text-center flex justify-center gap-1 md:gap-2">
                    {isAdmin && <Button onClick={() => setEditingSingleItem({ table: 'oils', data: oil })} size="sm" color="blue">{t('edit')}</Button>}
                    <StockAdjustButton item={oil} onDone={async (newStock) => {
                        let updatedOil;
                        setData(prev => {
                            const newData = { ...prev };
                            const index = newData.oils.findIndex(o => o.oil === oil.oil);
                            if (index > -1) {
                                newData.oils[index].actualstock = newStock;
                                updatedOil = newData.oils[index];
                            }
                            return newData;
                        });
                        if (updatedOil) {
                            console.log('🔄 Syncing oil stock to Supabase...');
                            await syncSingleItem('oils', updatedOil);
                            // Check if stock is below threshold
                            checkLowStockThreshold('oils', oil.oil, newStock);
                        }
                        showAlert('Stock updated');
                    }} />
                </div>
            </Card>
        );
    };

    const filteredBottles = useMemo(() => {
        return data.bottles.filter(b => {
            const matchesSearch = `${b.size} ${b.gender}`.toLowerCase().includes(bottleSearch.toLowerCase());
            const matchesSize = bottleFilterSize === 'All' || b.size === bottleFilterSize;
            const matchesGender = bottleFilterGender === 'All' || b.gender === bottleFilterGender;
            return matchesSearch && matchesSize && matchesGender;
        });
    }, [data.bottles, bottleSearch, bottleFilterSize, bottleFilterGender]);

    const filteredOils = useMemo(() => {
        return data.oils.filter(o => {
            const matchesSearch = `${o.oil} ${o.oiltype}`.toLowerCase().includes(oilSearch.toLowerCase());
            const matchesType = oilFilterType === 'All' || o.oiltype === oilFilterType;
            return matchesSearch && matchesType;
        });
    }, [data.oils, oilSearch, oilFilterType]);

    const renderTable = (tableName, headers, tableData) => (
        <div className="w-full overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                            {headers.map(h => (
                                <th key={h.key} scope="col" className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                    {h.label}
                                </th>
                            ))}
                            <th scope="col" className="relative px-2 md:px-4 py-3 whitespace-nowrap">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {(editingTable === tableName ? tempData : tableData).map((row, rowIndex) => (
                            <tr key={row.id || row.oil || row.product || rowIndex} className="relative hover:z-10">
                                {headers.map(h => (
                                    <td key={h.key} className="px-2 md:px-4 py-2 md:py-4 text-sm text-gray-900 max-w-[150px] truncate">
                                        {editingTable !== tableName || h.isCalculated || !row.id && (tableName === 'bottles' && h.key === 'id') ? (
                                            h.isCalculated ? h.content(row) : (
                                                // Handle image display if it's the imageurl field when not editing
                                                h.key === 'imageurl' && row.imageurl ?
                                                    <img src={row.imageurl} alt="Product" className="w-8 h-8 object-cover rounded" onError={(e) => e.target.src = fallbackImage} /> :
                                                    <span className="block truncate" title={row[h.key]}>{row[h.key]}</span>
                                            )
                                        ) : h.key === 'gender' ? (
                                            <select value={row[h.key] || ''} onChange={e => handleCellChange(rowIndex, h.key, e.target.value)} className={`w-full p-1 border rounded-md text-xs ${FOCUSED_INPUT_STYLE}`}>
                                                <option value="For Him">For Him</option>
                                                <option value="For Her">For Her</option>
                                                <option value="Testers">Testers</option>
                                            </select>
                                        ) : h.key === 'size' ? (
                                            <select value={row[h.key] || ''} onChange={e => handleCellChange(rowIndex, h.key, e.target.value)} className={`w-full p-1 border rounded-md text-xs ${FOCUSED_INPUT_STYLE}`}>
                                                <option value="30ml">30ml</option>
                                                <option value="50ml">50ml</option>
                                                <option value="100ml">100ml</option>
                                                <option value="20ml">20ml</option>
                                                <option value="5ml">5ml</option>
                                            </select>
                                        ) : h.key === 'vendor_id' ? (
                                            <select value={row[h.key] || ''} onChange={e => handleCellChange(rowIndex, h.key, e.target.value)} className={`w-full p-1 border rounded-md text-xs ${FOCUSED_INPUT_STYLE}`}>
                                                <option value="">{t('selectVendor') || 'Select Vendor'}</option>
                                                {data.vendors?.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                            </select>
                                        ) : <input type="text" value={row[h.key] || ''} onChange={e => handleCellChange(rowIndex, h.key, e.target.value)} className={`w-full p-1 border rounded-md text-xs ${FOCUSED_INPUT_STYLE}`} />}
                                    </td>
                                ))}
                                <td className="px-2 md:px-4 py-2 md:py-4 text-right">
                                    <div className="flex justify-end gap-1">
                                        {editingTable === tableName && <IconButton icon="trash" color="red" onClick={() => handleDeleteItem(rowIndex)} tooltip={t('deleteItem')} />}
                                        {tableName !== 'vendors' && <IconButton icon="edit" color="blue" onClick={() => setEditingSingleItem({ table: tableName, data: row })} tooltip={t('editItem')} />}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div>
            <div className="flex justify-between items-center gap-2 mb-6">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">{t('stockManagement')}</h1>
                <div className="flex flex-wrap gap-2">
                    {['Products', 'Oils', 'Bottles', 'Returned Products'].includes(subTab) && <Button onClick={() => toggleViewMode(subTab)} color="gray" size="sm" className="px-2" title={t('cards')}>
                        <Icon path={effectiveViewMode(subTab) === 'table' ? ICONS_MAP['table'] : ICONS_MAP['cards']} className="w-5 h-5 text-gray-600" />
                    </Button>}
                    {editingTable === subTab.toLowerCase() ? <>
                        <Button onClick={handleCancelEdit} color="gray">
                            <span className="hidden sm:inline">{t('cancel')}</span>
                            <span className="sm:hidden">{t('cancel')}</span>
                        </Button>
                        <Button onClick={handleSaveTable} color="green">
                            <span className="hidden sm:inline">{t('save')}</span>
                            <span className="sm:hidden">{t('save')}</span>
                        </Button>
                        <Button onClick={() => handleAddItem(editingTable)} color={settings.primaryColor}>
                            <Icon path={ICONS_MAP['add']} className="w-5 h-5" />
                            <span className="hidden sm:inline ml-1">{t('addItem')}</span>
                        </Button>
                    </> : <>
                        {subTab !== 'Returned Products' && !['Products', 'Oils', 'Bottles'].includes(subTab) && <Button onClick={() => handleEditTable(subTab.toLowerCase())} color={settings.primaryColor}>
                            <Icon path={ICONS_MAP['edit']} className="w-5 h-5" />
                            <span className="hidden sm:inline ml-1">{t('edit')}</span>
                        </Button>}
                        <AdminOnly>
                            <Button onClick={() => document.getElementById(`csv-upload-${subTab.toLowerCase().replace(/ /g, '')}`).click()} color="gray">
                                <Icon path={ICONS_MAP['upload']} className="w-5 h-5" />
                                <span className="hidden sm:inline ml-1">{t('upload')} {subTab}</span>
                            </Button>
                        </AdminOnly>
                    </>}
                    <input id={`csv-upload-returnedproducts`} type="file" accept=".csv" className="hidden" onChange={(e) => handleFileUpload(e, 'used')} />
                    <input id={`csv-upload-bottles`} type="file" accept=".csv" className="hidden" onChange={(e) => handleFileUpload(e, 'bottles')} />
                    <input id={`csv-upload-oils`} type="file" accept=".csv" className="hidden" onChange={(e) => handleFileUpload(e, 'oils')} />
                    <input id={`csv-upload-products`} type="file" accept=".csv" className="hidden" onChange={(e) => handleFileUpload(e, 'products')} />
                </div>
            </div>
            <div className="mb-6">
                <div className="inline-flex rounded-md shadow-sm">
                    {[
                        { key: 'Products', label: t('productsTab') },
                        { key: 'Bottles', label: t('bottles') },
                        { key: 'Oils', label: t('oils') },
                        { key: 'Returned Products', label: t('returnedProducts') },
                        { key: 'Vendors', label: t('vendors') || 'Vendors' }
                    ].map(({ key, label }) => (
                        <button key={key} onClick={() => setSubTab(key)} className={`px-4 py-2 text-sm font-medium ${subTab === key ? `bg-${settings.primaryColor}-600 text-white` : 'bg-white text-gray-700 hover:bg-gray-50'} border border-gray-200 first:rounded-l-lg last:rounded-r-lg focus:z-10 focus:ring-2 focus:ring-${settings.primaryColor}-500`}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {subTab === 'Returned Products' && (
                <Card>
                    <div className="mb-4 flex flex-col sm:flex-row gap-2">
                        <select value={newProduct} onChange={e => setNewProduct(e.target.value)} className={`flex-1 p-2 border rounded ${FOCUSED_INPUT_STYLE}`}>
                            <option value="">{t('selectProduct')}</option>
                            {data.products.map(p => <option key={p.product} value={p.product}>{p.product}</option>)}
                        </select>
                        <input type="number" value={newQty} onChange={e => setNewQty(parseInt(e.target.value) || 1)} min="1" className={`w-full sm:w-24 p-2 border rounded ${FOCUSED_INPUT_STYLE}`} />
                        <Button onClick={async () => {
                            if (newProduct) {
                                const newReturned = { ...data.returnedPool, [newProduct]: (data.returnedPool[newProduct] || 0) + newQty };
                                setData({ ...data, returnedPool: newReturned });
                                setNewProduct('');
                                setNewQty(1);
                                // Sync immediately
                                try {
                                    await syncObjectData('returned_pool', newReturned, 'product_name', 'quantity');
                                    showAlert('Product added successfully.');
                                } catch (err) {
                                    console.error('❌ Error syncing returned pool:', err);
                                    showAlert(`Failed to sync: ${err.message}`);
                                }
                            }
                        }} color={settings.primaryColor} className="w-full sm:w-auto">{t('add')}</Button>
                    </div>

                    {effectiveViewMode('Returned Products') === 'cards' ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            {returnedProductsData.map(item => {
                                const product = data.products.find(p => p.product === item.productName);
                                return (
                                    <Card key={item.key} className="flex flex-col justify-between h-full hover:shadow-lg transition-shadow">
                                        <div className="text-center">
                                            <div className="text-3xl mb-2 text-orange-600">
                                                {product?.imageurl ? (
                                                    <img src={product.imageurl} alt={item.productName} className="w-16 h-16 mx-auto rounded-lg object-cover" onError={(e) => e.target.src = fallbackImage} />
                                                ) : (
                                                    <Icon path={ICONS_MAP['returned']} className="w-16 h-16 mx-auto" />
                                                )}
                                            </div>
                                            <h3 className="font-bold text-sm text-gray-800 line-clamp-2">{item.productName}</h3>
                                        </div>
                                        <div className="mt-4 text-center">
                                            <p className="text-sm text-gray-500">Quantity Available</p>
                                            <input
                                                type="number"
                                                value={data.returnedPool[item.key] || 0}
                                                onChange={async (e) => {
                                                    const newQty = parseInt(e.target.value) || 0;
                                                    const newReturned = { ...data.returnedPool, [item.key]: newQty };
                                                    setData({ ...data, returnedPool: newReturned });
                                                    // Sync immediately
                                                    try {
                                                        await syncObjectData('returned_pool', newReturned, 'product_name', 'quantity');
                                                    } catch (err) {
                                                        console.error('❌ Error syncing returned pool:', err);
                                                    }
                                                }}
                                                className="w-20 p-2 mt-1 border-2 border-orange-300 rounded text-center font-bold text-orange-600 focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                                            />
                                        </div>
                                        <div className="mt-4 text-center border-t pt-3">
                                            <IconButton icon="trash" color="red" onClick={async () => {
                                                console.log(`🔵 [DELETE] Deleting returned product: ${item.key}`);
                                                const newReturned = { ...data.returnedPool };
                                                delete newReturned[item.key];
                                                setData({ ...data, returnedPool: newReturned });

                                                // Delete from database immediately
                                                try {
                                                    console.log(`🔄 [DELETE] Deleting ${item.key} from returned_pool table...`);
                                                    const { error } = await supabaseTracked
                                                        .from('returned_pool')
                                                        .delete()
                                                        .eq('product_name', item.key);

                                                    if (error) {
                                                        console.error('❌ [DELETE] Error deleting from database:', error);
                                                        throw new Error(`Failed to delete: ${error.message}`);
                                                    }

                                                    console.log(`✅ [DELETE] Successfully deleted ${item.key} from database`);
                                                    showAlert('Product deleted successfully.');
                                                } catch (err) {
                                                    console.error('❌ [DELETE] Error deleting returned product:', err);
                                                    showAlert(`Failed to delete: ${err.message}`);
                                                }
                                            }} tooltip="Delete" />
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('productName')}</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('quantity')}</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {returnedProductsData.map(item => (
                                        <tr key={item.key} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.productName}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <input
                                                    type="number"
                                                    value={data.returnedPool[item.key] || 0}
                                                    onChange={async (e) => {
                                                        const newQty = parseInt(e.target.value) || 0;
                                                        const newReturned = { ...data.returnedPool, [item.key]: newQty };
                                                        setData({ ...data, returnedPool: newReturned });
                                                        // Sync immediately
                                                        try {
                                                            await syncObjectData('returned_pool', newReturned, 'product_name', 'quantity');
                                                        } catch (err) {
                                                            console.error('❌ Error syncing returned pool:', err);
                                                        }
                                                    }}
                                                    className="w-20 p-2 border-2 border-orange-300 rounded text-center font-bold text-orange-600 focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <IconButton icon="trash" color="red" onClick={async () => {
                                                    console.log(`🔵 [DELETE] Deleting returned product: ${item.key}`);
                                                    const newReturned = { ...data.returnedPool };
                                                    delete newReturned[item.key];
                                                    setData({ ...data, returnedPool: newReturned });

                                                    // Delete from database immediately
                                                    try {
                                                        console.log(`🔄 [DELETE] Deleting ${item.key} from returned_pool table...`);
                                                        const { error } = await supabaseTracked
                                                            .from('returned_pool')
                                                            .delete()
                                                            .eq('product_name', item.key);

                                                        if (error) {
                                                            console.error('❌ [DELETE] Error deleting from database:', error);
                                                            throw new Error(`Failed to delete: ${error.message}`);
                                                        }

                                                        console.log(`✅ [DELETE] Successfully deleted ${item.key} from database`);
                                                        showAlert('Product deleted successfully.');
                                                    } catch (err) {
                                                        console.error('❌ [DELETE] Error deleting returned product:', err);
                                                        showAlert(`Failed to delete: ${err.message}`);
                                                    }
                                                }} tooltip="Delete" />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            )}

            {subTab === 'Bottles' && (
                <div>
                    <div className="mb-4 flex items-center gap-3">
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Icon path={ICONS_MAP['stock']} className="w-5 h-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder={`${t('search')} ${bottlesLabel}...`}
                                value={bottleSearch}
                                onChange={(e) => setBottleSearch(e.target.value)}
                                className={`w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 focus:bg-white rounded-xl text-sm transition-all duration-300 focus:ring-2 focus:ring-${primaryColor}-500 focus:border-${primaryColor}-500 outline-none`}
                            />
                            {bottleSearch && (
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                    <button onClick={() => setBottleSearch('')} className="p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none">
                                        <Icon path={ICONS_MAP['close']} className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="relative" ref={bottleFilterRef}>
                            <button
                                onClick={() => setShowBottleFilters(!showBottleFilters)}
                                className={`p-2.5 rounded-xl border transition-all duration-200 ${showBottleFilters ? `bg-${primaryColor}-50 border-${primaryColor}-200 text-${primaryColor}-600 shadow-sm` : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300 hover:bg-gray-50'}`}
                                title={t('filters')}
                            >
                                <Icon path={ICONS_MAP['filter']} className="w-5 h-5" />
                            </button>

                            {showBottleFilters && (
                                <div className={`absolute top-full mt-2 ${isRTL ? 'left-0' : 'right-0'} bg-white border border-gray-100 rounded-xl shadow-xl z-50 p-4 min-w-[200px] space-y-4`}>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{t('size')}</label>
                                        <select value={bottleFilterSize} onChange={e => setBottleFilterSize(e.target.value)} className={`w-full p-2 border border-gray-200 rounded-lg text-sm ${FOCUSED_INPUT_STYLE}`}>
                                            {productSizes.map(size => <option key={size} value={size}>{size === 'All' ? t('all') : size}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{t('category')}</label>
                                        <select value={bottleFilterGender} onChange={e => setBottleFilterGender(e.target.value)} className="w-full p-2 text-sm border-gray-200 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50/50">
                                            <option value="All">{t('all')}</option>
                                            {([...new Set([
                                                ...(data.stockSettings.bottleCategories || []),
                                                ...data.bottles.map(b => b.gender)
                                            ].map(normalizeGenderLabel))].filter(g => g && g !== 'All' && g.toLowerCase() !== 'all genders')).sort().map(gender => (
                                                <option key={gender} value={gender}>{gender}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="pt-2 border-t border-gray-100 flex justify-end">
                                        <button onClick={() => setShowBottleFilters(false)} className={`text-sm font-bold text-${primaryColor}-600 hover:underline`}>{t('done')}</button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {isAdmin && <Button onClick={() => setAddBottleModalOpen(true)} color={primaryColor} className="whitespace-nowrap px-4 py-2.5 rounded-xl shadow-sm"><Icon path={ICONS_MAP['add']} className="w-5 h-5 sm:mr-1.5" /><span className="hidden sm:inline">{t('add')}</span></Button>}
                    </div>

                    {effectiveViewMode('bottles') === 'table' ? (
                        <Card>{renderTable('bottles', [{ key: 'id', label: t('id') }, { key: 'size', label: t('size') }, { key: 'gender', label: t('gender') }, { key: 'actualstock', label: t('stock') }, { key: 'totalordered', label: t('totalOrdered') }, { key: 'totalcostbottle', label: t('totalCost') }, { key: 'imageurl', label: t('imageUrl') }, {
                            key: 'adjust', label: t('adjustStock'), isCalculated: true, content: (row) => <StockAdjustButton item={row} onDone={async (newStock) => {
                                let updatedBottle;
                                setData(prev => {
                                    const newData = { ...prev };
                                    const index = newData.bottles.findIndex(b => b.id === row.id);
                                    if (index > -1) {
                                        newData.bottles[index].actualstock = newStock;
                                        updatedBottle = newData.bottles[index];
                                    }
                                    return newData;
                                });
                                if (updatedBottle) {
                                    await syncSingleItem('bottles', updatedBottle);
                                    checkLowStockThreshold('bottles', row.id, newStock);
                                }
                                showAlert('Stock updated');
                            }} />
                        }, {
                            key: 'actions', label: t('actions'), content: (row) => <div className="flex gap-2">
                                <button onClick={() => setEditingSingleItem({ table: 'bottles', data: row })} className="text-blue-500 hover:underline">{t('edit')}</button>
                                <button onClick={() => handleDeleteBottle(row.id)} className="text-red-500 hover:underline">{t('delete')}</button>
                            </div>
                        }], filteredBottles)}</Card>
                    ) : (
                        <div className="space-y-6">
                            {Object.entries(bottlesGrouped).map(([gender, bottlesInGroup]) => (
                                <Card key={gender}>
                                    <h3 className="font-bold text-lg mb-4" style={{ color: themeColor }}>{gender} {bottlesLabel} ({bottlesInGroup.length})</h3>
                                    {effectiveViewMode('bottles') === 'cards' ? (
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                            {bottlesInGroup.map(bottle => <BottleCard key={bottle.id} bottle={bottle} />)}
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead>
                                                    <tr>
                                                        <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase">{t('size')}</th>
                                                        <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase">{t('stock')}</th>
                                                        <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase">{t('actions')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {bottlesInGroup.map(bottle => (
                                                        <tr key={bottle.id}>
                                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{bottle.size}</td>
                                                            <td className="px-4 py-3 text-sm text-gray-500">
                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${safeCoerceNumber(bottle.actualstock) < 10 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                                                    {bottle.actualstock} {t('units')}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-right">
                                                                <div className="flex gap-2 justify-end">
                                                                    {isAdmin && <IconButton icon="edit" color="blue" onClick={() => setEditingSingleItem({ table: 'bottles', data: bottle })} size="sm" />}
                                                                    <StockAdjustButton item={bottle} onDone={() => showAlert(t('stockUpdated'))} />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </Card>
                            ))}
                        </div>
                    )}

                </div>
            )}

            {subTab === 'Oils' && (
                <div className="space-y-6">
                    <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Icon path={ICONS_MAP['stock']} className="w-5 h-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder={`${t('search')} ${oilsLabel}...`}
                                value={oilSearch}
                                onChange={(e) => setOilSearch(e.target.value)}
                                className={`w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 focus:bg-white rounded-xl text-sm transition-all duration-300 focus:ring-2 focus:ring-${primaryColor}-500 focus:border-${primaryColor}-500 outline-none`}
                            />
                            {oilSearch && (
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                    <button onClick={() => setOilSearch('')} className="p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none">
                                        <Icon path={ICONS_MAP['close']} className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="relative" ref={oilFilterRef}>
                            <button
                                onClick={() => setShowOilFilters(!showOilFilters)}
                                className={`p-2.5 rounded-xl border transition-all duration-200 ${showOilFilters ? `bg-${primaryColor}-50 border-${primaryColor}-200 text-${primaryColor}-600 shadow-sm` : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300 hover:bg-gray-50'}`}
                                title={t('filters')}
                            >
                                <Icon path={ICONS_MAP['filter']} className="w-5 h-5" />
                            </button>

                            {showOilFilters && (
                                <div className={`absolute top-full mt-2 ${isRTL ? 'left-0' : 'right-0'} bg-white border border-gray-100 rounded-xl shadow-xl z-50 p-4 min-w-[200px] space-y-4`}>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{t('category')}</label>
                                        <select value={oilFilterType} onChange={e => setOilFilterType(e.target.value)} className={`w-full p-2 border border-gray-200 rounded-lg text-sm ${FOCUSED_INPUT_STYLE}`}>
                                            <option value="All">{t('all')}</option>
                                            {([...new Set(((data.stockSettings.oilCategories && data.stockSettings.oilCategories.length > 0)
                                                ? data.stockSettings.oilCategories
                                                : ['Men', 'Women', 'Unisex']))].filter(t => t !== 'All')).map(type => <option key={type} value={type}>{type}</option>)}
                                        </select>
                                    </div>
                                    <div className="pt-2 border-t border-gray-100 flex justify-end">
                                        <button onClick={() => setShowOilFilters(false)} className={`text-sm font-bold text-${primaryColor}-600 hover:underline`}>{t('done')}</button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {isAdmin && <Button onClick={() => setAddOilModalOpen(true)} color={primaryColor} className="whitespace-nowrap px-4 py-2.5 rounded-xl shadow-sm"><Icon path={ICONS_MAP['add']} className="w-5 h-5 sm:mr-1.5" /><span className="hidden sm:inline">{t('add')}</span></Button>}
                    </div>
                    {Object.entries(oilsGrouped).map(([type, oilsInCategory]) => {
                        const filteredInCategory = filteredOils.filter(o => o.oiltype === type);
                        if (filteredInCategory.length === 0) return null;
                        return (
                            <Card key={type}>
                                <h3 className="font-bold text-lg mb-4" style={{ color: themeColor }}>{type} {oilsLabel} ({filteredInCategory.length})</h3>
                                {effectiveViewMode('oils') === 'cards' ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                        {filteredInCategory.map(oil => <OilCard key={oil.oil} oil={oil} />)}
                                    </div>
                                ) : renderTable('oils', [{ key: 'oil', label: t('name') }, { key: 'vendor', label: 'Vendor', isCalculated: true, content: (row) => data.vendors?.find(v => v.id === row.vendor_id)?.name || '-' }, { key: 'price', label: t('pricePerMl'), isCalculated: true, content: (row) => formatCurrency(row.price, generalSettings.currency) }, { key: 'actualstock', label: `${t('stock')} (${t('ml')})` }, { key: 'totalordered', label: `${t('totalOrdered')} (${t('ml')})` }, { key: 'qty_5ml', label: '5ml', isCalculated: true, content: (row) => row.oil_per_5ml || '-' }, { key: 'qty_20ml', label: '20ml', isCalculated: true, content: (row) => row.oil_per_20ml || '-' }, { key: 'qty_30ml', label: '30ml', isCalculated: true, content: (row) => row.oil_per_30ml || '-' }, { key: 'qty_50ml', label: '50ml', isCalculated: true, content: (row) => row.oil_per_50ml || '-' }, { key: 'qty_100ml', label: '100ml', isCalculated: true, content: (row) => row.oil_per_100ml || '-' }, {
                                    key: 'adjust', label: t('adjustStock'), isCalculated: true, content: (row) => <StockAdjustButton item={row} onDone={async (newStock) => {
                                        let updatedOil;
                                        setData(prev => {
                                            const newData = { ...prev };
                                            const index = newData.oils.findIndex(o => o.oil === row.oil);
                                            if (index > -1) {
                                                newData.oils[index].actualstock = newStock;
                                                updatedOil = newData.oils[index];
                                            }
                                            return newData;
                                        });
                                        if (updatedOil) {
                                            await syncSingleItem('oils', updatedOil);
                                            checkLowStockThreshold('oils', row.oil, newStock);
                                        }
                                        showAlert('Stock updated');
                                    }} />
                                }, {
                                    key: 'actions', label: t('actions'), content: (row) => <div className="flex gap-2">
                                        <button onClick={() => setEditingSingleItem({ table: 'oils', data: row })} className="text-blue-500 hover:underline">{t('edit')}</button>
                                        <button onClick={() => handleDeleteOil(row.oil)} className="text-red-500 hover:underline">{t('delete')}</button>
                                    </div>
                                }], filteredInCategory)}
                            </Card>
                        );
                    })}
                </div>
            )}

            {subTab === 'Products' && (
                <>
                    <div className="mb-4 flex items-center gap-3">
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5 text-gray-400">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder={t('searchProductNameOrOil')}
                                value={productSearch}
                                onChange={(e) => setProductSearch(e.target.value)}
                                className={`w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 focus:bg-white rounded-xl text-sm transition-all duration-300 focus:ring-2 focus:ring-${settings?.primaryColor || 'indigo'}-500 focus:border-${settings?.primaryColor || 'indigo'}-500 outline-none`}
                            />
                            {productSearch && (
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                    <button
                                        onClick={() => setProductSearch('')}
                                        className="p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none"
                                        title="Clear search"
                                    >
                                        <Icon path={ICONS_MAP['close']} className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="relative" ref={filterRef}>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`p-2.5 rounded-xl border transition-all duration-200 ${showFilters ? `bg-${settings?.primaryColor || 'indigo'}-50 border-${settings?.primaryColor || 'indigo'}-200 text-${settings?.primaryColor || 'indigo'}-600 shadow-sm` : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300 hover:bg-gray-50'}`}
                                title={t('filters')}
                            >
                                <Icon path={ICONS_MAP['filter']} className="w-5 h-5" />
                            </button>

                            {showFilters && (
                                <div className={`absolute top-full mt-2 ${isRTL ? 'left-0' : 'right-0'} bg-white border border-gray-100 rounded-xl shadow-xl z-50 p-4 min-w-[200px] space-y-4`}>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{t('size')}</label>
                                        <select value={productFilterSize} onChange={e => setProductFilterSize(e.target.value)} className={`w-full p-2 border border-gray-200 rounded-lg text-sm ${FOCUSED_INPUT_STYLE}`}>
                                            {productSizes.map(size => <option key={size} value={size}>{size === 'All' ? t('all') : size}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{t('category')}</label>
                                        <select value={productFilterGender} onChange={e => setProductFilterGender(e.target.value)} className={`w-full p-2 border border-gray-200 rounded-lg text-sm ${FOCUSED_INPUT_STYLE}`}>
                                            {([...new Set(allGenders.map(normalizeGenderLabel))].filter(g => g && g.toLowerCase() !== 'all genders')).map(gender => (
                                                <option key={gender} value={gender}>{gender === 'All' ? t('all') : gender}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="pt-2 border-t border-gray-100 flex justify-end">
                                        <button onClick={() => setShowFilters(false)} className={`text-sm font-bold text-${settings?.primaryColor || 'indigo'}-600 hover:underline`}>{t('done')}</button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {isAdmin && (
                            <Button
                                onClick={() => setBulkGenerateModalOpen(true)}
                                color={settings?.primaryColor || 'indigo'}
                                className="whitespace-nowrap rounded-xl shadow-md px-4"
                            >
                                <Icon path={ICONS_MAP['add']} className="w-5 h-5 sm:mr-1.5" />
                                <span className="hidden sm:inline">{t('generate')}</span>
                            </Button>
                        )}
                    </div>
                    <div className="space-y-6">
                        {/* Filter out empty groups (like 'Unisex' if no products are in it) */}
                        {Object.entries(productsGrouped).filter(([, prods]) => prods.length > 0).sort(([a], [b]) => {
                            const knownOrder = ['For Him', 'For Her', 'Testers'];
                            const aIndex = knownOrder.indexOf(a);
                            const bIndex = knownOrder.indexOf(b);
                            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
                            if (aIndex !== -1) return -1;
                            if (bIndex !== -1) return 1;
                            return a.localeCompare(b);
                        }).map(([type, prods]) => (
                            <Card key={type}>
                                <h3 className="font-bold text-lg mb-2" style={{ color: themeColor }}>{type} Products ({prods.length}) {productFilterSize !== 'All' && `(${productFilterSize})`}</h3>
                                {effectiveViewMode('products') === 'cards' ? <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">{prods.map(product => <ProductCard key={product.product} product={product} />)}</div> : renderTable('products', [
                                    { key: 'product', label: t('name') },
                                    { key: 'gender', label: t('category') },
                                    { key: 'size', label: t('size') },
                                    { key: 'imageurl', label: t('imageUrl') }, // Added Image URL column
                                    { key: 'price', label: t('price'), isCalculated: true, content: (row) => formatCurrency(row.price, generalSettings.currency) },
                                    { key: 'costproduct', label: t('cost'), isCalculated: true, content: (row) => formatCurrency(getProductCost(row), generalSettings.currency) },
                                    { key: 'qtysold', label: t('qtySold') },
                                    { key: 'buildable', label: t('buildableStock'), isCalculated: true, content: (row) => <span className="font-bold" style={{ color: themeColor }}>{calculateBuildableStock(row)}</span> },
                                    { key: 'oil', label: t('oil') },
                                    { key: 'qtyoilinbottle', label: 'Oil Qty', isCalculated: true, content: (row) => {
                                        const oilInfo = data.oils?.find(o => o.oil === row.oil);
                                        if (oilInfo && row.size) {
                                            const sizeStr = row.size.replace(/[^0-9]/g, '');
                                            if (sizeStr && oilInfo[`oil_per_${sizeStr}ml`] !== undefined && oilInfo[`oil_per_${sizeStr}ml`] !== null) {
                                                return oilInfo[`oil_per_${sizeStr}ml`];
                                            }
                                        }
                                        return row.qtyoilinbottle;
                                    }}
                                ], prods)}
                            </Card>
                        ))}
                    </div>
                </>
            )}

            {/* Add Oil Modal */}
            {addOilModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h2 className="text-xl font-bold text-gray-800">{t('addNewOil')}</h2>
                            <IconButton icon="close" color="gray" onClick={() => setAddOilModalOpen(false)} tooltip={t('close')} />
                        </div>
                        <div className="p-6 overflow-y-auto">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">{t('oilName')}</label>
                                <input type="text" value={newOilName} onChange={e => setNewOilName(e.target.value)} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">{t('vendor') || 'Vendor'}</label>
                                <select value={newOilVendor} onChange={e => setNewOilVendor(e.target.value)} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                                    <option value="">{t('selectVendor') || 'Select Vendor'}</option>
                                    {data.vendors?.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">{t('pricePerMl')} ({getCurrencySymbol(data.generalSettings.currency)})</label>
                                <input type="number" value={newOilPrice} onChange={e => setNewOilPrice(e.target.value)} min="0" step="0.01" className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">{t('oilType')}</label>
                                <select value={newOilType} onChange={e => setNewOilType(e.target.value)} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                                    {data.stockSettings.oilCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">{t('initialStockMl')}</label>
                                <input type="number" value={newOilStock} onChange={e => setNewOilStock(e.target.value)} min="0" className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                            </div>

                            <div className="border-t pt-4 mt-4">
                                <h3 className="text-sm font-bold text-gray-800 mb-3">{t('oilPerBottleDefaults') || 'Oil Qty per Bottle Size (ml)'}</h3>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase">5ml</label>
                                        <input type="number" value={newOilPer5ml} onChange={e => setNewOilPer5ml(parseInt(e.target.value))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase">20ml</label>
                                        <input type="number" value={newOilPer20ml} onChange={e => setNewOilPer20ml(parseInt(e.target.value))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase">30ml</label>
                                        <input type="number" value={newOilPer30ml} onChange={e => setNewOilPer30ml(parseInt(e.target.value))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase">50ml</label>
                                        <input type="number" value={newOilPer50ml} onChange={e => setNewOilPer50ml(parseInt(e.target.value))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase">100ml</label>
                                        <input type="number" value={newOilPer100ml} onChange={e => setNewOilPer100ml(parseInt(e.target.value))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
                            <Button onClick={() => setAddOilModalOpen(false)} color="gray">{t('cancel')}</Button>
                            <Button onClick={async () => {
                                if (newOilName && newOilPrice && newOilStock) {
                                    const newOil = {
                                        oil: newOilName,
                                        price: parseFloat(newOilPrice),
                                        actualstock: parseInt(newOilStock),
                                        totalordered: 0,
                                        oiltype: newOilType,
                                        vendor_id: newOilVendor || null,
                                        oil_per_5ml: newOilPer5ml,
                                        oil_per_20ml: newOilPer20ml,
                                        oil_per_30ml: newOilPer30ml,
                                        oil_per_50ml: newOilPer50ml,
                                        oil_per_100ml: newOilPer100ml
                                    };
                                    setData(prev => ({
                                        ...prev,
                                        oils: [...prev.oils, newOil]
                                    }));

                                    // Sync to Supabase
                                    console.log('🔄 Syncing new oil to Supabase...');
                                    await syncSingleItem('oils', newOil);

                                    showAlert(t('oilAddedSuccess'));
                                    setAddOilModalOpen(false);
                                    setNewOilName('');
                                    setNewOilPrice('');
                                    setNewOilType('Unisex');
                                    setNewOilStock('');
                                    setNewOilVendor('');
                                    setNewOilPer5ml(1);
                                    setNewOilPer20ml(5);
                                    setNewOilPer30ml(9);
                                    setNewOilPer50ml(15);
                                    setNewOilPer100ml(30);
                                }
                            }} color="green">{t('addOil')}</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Bottle Modal */}
            {addBottleModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h2 className="text-xl font-bold text-gray-800">{t('addNewBottle')}</h2>
                            <IconButton icon="close" color="gray" onClick={() => setAddBottleModalOpen(false)} tooltip={t('close')} />
                        </div>
                        <div className="p-6 overflow-y-auto">
                            {/* 1. Gender/Type - Select First */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">{t('genderType')}</label>
                                <select
                                    value={newBottleGender}
                                    onChange={e => {
                                        setNewBottleGender(e.target.value);
                                        setNewBottleSize(''); // Reset size when gender changes
                                    }}
                                    className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}
                                >
                                    <option value="">{t('selectGenderType')}</option>
                                    {Object.keys(data.stockSettings.bottleVariants).map(gender => (
                                        <option key={gender} value={gender}>{gender.charAt(0).toUpperCase() + gender.slice(1)}</option>
                                    ))}
                                </select>
                            </div>

                            {/* 2. Size - Filtered by selected gender */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">{t('size')}</label>
                                <select
                                    value={newBottleSize}
                                    onChange={e => setNewBottleSize(e.target.value)}
                                    className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}
                                    disabled={!newBottleGender}
                                >
                                    <option value="">{t('selectSize')}</option>
                                    {newBottleGender && data.stockSettings.bottleVariants[newBottleGender]?.map(size => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </select>
                            </div>

                            {/* 3. Auto-generated ID Display */}
                            {newBottleSize && newBottleGender && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700">{t('generatedId')}</label>
                                    <input
                                        type="text"
                                        value={`${newBottleSize}${newBottleGender}`.toLowerCase().replace(/\s+/g, '')}
                                        disabled
                                        className="w-full p-2 border rounded-md bg-gray-100 text-gray-600"
                                    />
                                </div>
                            )}

                            {/* 4. Initial Stock */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">{t('initialStockUnits')}</label>
                                <input type="number" value={newBottleStock} onChange={e => setNewBottleStock(e.target.value)} min="0" className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                            </div>

                            {/* 5. Total Cost */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">{t('totalCostPerBottle')} ({getCurrencySymbol(data.generalSettings.currency)})</label>
                                <input type="number" value={newBottleTotalCost} onChange={e => setNewBottleTotalCost(e.target.value)} min="0" step="0.01" className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                            </div>

                            {/* 7. Image URL */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">{t('imageUrlOptional')}</label>
                                <input type="text" value={newBottleImageUrl} onChange={e => setNewBottleImageUrl(e.target.value)} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} placeholder="https://example.com/image.jpg" />
                            </div>
                        </div>
                        <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
                            <Button onClick={() => setAddBottleModalOpen(false)} color="gray">{t('cancel')}</Button>
                            <Button onClick={async () => {
                                if (newBottleSize && newBottleGender && newBottleStock && newBottleTotalCost) {
                                    // Generate ID: {size}{gender} in lowercase with no spaces
                                    const bottleId = `${newBottleSize}${newBottleGender}`.toLowerCase().replace(/\s+/g, '');

                                    // Check if bottle with this ID already exists
                                    const exists = data.bottles.some(b => b.id === bottleId);
                                    if (exists) {
                                        showAlert(t('bottleExists'));
                                        return;
                                    }

                                    // Capitalize first letter of gender to match existing categories
                                    const capitalizedGender = newBottleGender.charAt(0).toUpperCase() + newBottleGender.slice(1);

                                    const newBottle = {
                                        id: bottleId,
                                        size: newBottleSize,
                                        gender: capitalizedGender,
                                        actualstock: parseInt(newBottleStock),
                                        totalcostbottle: parseFloat(newBottleTotalCost),
                                        totalordered: 0,
                                        imageurl: newBottleImageUrl
                                    };

                                    setData(prev => ({
                                        ...prev,
                                        bottles: [...prev.bottles, newBottle]
                                    }));

                                    // Sync to Supabase
                                    console.log('🔄 Syncing new bottle to Supabase...');
                                    await syncSingleItem('bottles', newBottle);

                                    showAlert(`${bottlesLabel.slice(0, -1)} added successfully.`);
                                    setAddBottleModalOpen(false);
                                    setNewBottleSize('');
                                    setNewBottleGender('');
                                    setNewBottleStock('');
                                    setNewBottleTotalCost('');
                                    setNewBottleImageUrl('');
                                }
                            }} color="green" disabled={!newBottleSize || !newBottleGender || !newBottleStock || !newBottleTotalCost}>{t('addBottle')}</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Generate Products Modal */}
            {bulkGenerateModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h2 className="text-xl font-bold text-gray-800">Generate Product</h2>
                            <IconButton icon="close" color="gray" onClick={() => setBulkGenerateModalOpen(false)} tooltip="Close" />
                        </div>
                        <div className="p-6 overflow-y-auto">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Select Oil</label>
                                <select value={selectedOil} onChange={e => setSelectedOil(e.target.value)} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                                    <option value="">Select an oil</option>
                                    {data.oils.map(o => <option key={o.oil} value={o.oil}>{o.oil} ({o.oiltype})</option>)}
                                </select>
                            </div>
                            {selectedOil && (
                                <>
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700">Select Categories (multi-select)</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {uniqueCategories.map(cat => (
                                                <label key={cat} className="flex items-center p-2 border rounded cursor-pointer hover:bg-gray-100">
                                                    <input type="checkbox" checked={selectedCategories.includes(cat)} onChange={e => {
                                                        if (e.target.checked) {
                                                            setSelectedCategories([...selectedCategories, cat]);
                                                        } else {
                                                            setSelectedCategories(selectedCategories.filter(c => c !== cat));
                                                        }
                                                    }} className="mr-2" />
                                                    {cat}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    {selectedCategories.length > 0 && (
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700">Select Sizes per Category</label>
                                            {selectedCategories.map(category => {
                                                const availableSizes = getSizesForCategory(category);
                                                return (
                                                    <div key={category} className="mb-4">
                                                        <p className="text-sm font-medium mb-2">{category}</p>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {availableSizes.map(size => (
                                                                <label key={size} className="flex items-center p-2 border rounded cursor-pointer hover:bg-gray-100">
                                                                    <input type="checkbox" checked={(selectedSizesPerCategory[category] || []).includes(size)} onChange={e => {
                                                                        const current = selectedSizesPerCategory[category] || [];
                                                                        if (e.target.checked) {
                                                                            setSelectedSizesPerCategory(prev => ({ ...prev, [category]: [...current, size] }));
                                                                        } else {
                                                                            setSelectedSizesPerCategory(prev => ({ ...prev, [category]: current.filter(s => s !== size) }));
                                                                        }
                                                                    }} className="mr-2" />
                                                                    {size}
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}
                            {selectedOil && selectedCategories.length > 0 && (
                                <div className="mb-4">
                                    <p className="text-sm text-gray-600">Variants to create: {productsToGenerate.length}</p>
                                    {productsToGenerate.length === 0 && <p className="text-sm text-gray-500">All selected variants already exist.</p>}
                                </div>
                            )}

                            {/* Validation Errors */}
                            {!selectedOil && <p className="text-red-500 text-sm mb-2">Please select an oil type.</p>}
                            {selectedOil && selectedCategories.length === 0 && <p className="text-red-500 text-sm mb-2">Please select at least one category.</p>}
                            {selectedCategories.map(category => (
                                (selectedSizesPerCategory[category] || []).length === 0 && (
                                    <p key={category} className="text-red-500 text-sm mb-2">Please select at least one size for {category}.</p>
                                )
                            ))}
                        </div>
                        <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
                            <Button onClick={() => setBulkGenerateModalOpen(false)} color="gray">Cancel</Button>
                            <Button onClick={async () => {
                                const isValid = selectedOil && selectedCategories.length > 0 && selectedCategories.every(cat => (selectedSizesPerCategory[cat] || []).length > 0);
                                if (isValid && productsToGenerate.length > 0) {
                                    setData(prev => {
                                        return { ...prev, products: [...prev.products, ...productsToGenerate] };
                                    });

                                    // Sync to Supabase
                                    console.log('🔄 Syncing generated products to Supabase...');
                                    await syncMultipleItems('products', productsToGenerate);

                                    showAlert(`${productsToGenerate.length} variants generated.`);
                                    setBulkGenerateModalOpen(false);
                                    setSelectedOil('');
                                    setSelectedCategories([]);
                                    setSelectedSizesPerCategory({});
                                }
                            }} color="green" disabled={!(selectedOil && selectedCategories.length > 0 && selectedCategories.every(cat => (selectedSizesPerCategory[cat] || []).length > 0) && productsToGenerate.length > 0)}>Confirm</Button>
                        </div>
                    </div>
                </div>
            )}

            {subTab === 'Vendors' && (
                <div className="space-y-6">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold text-gray-800">{t('vendorManagement') || 'Vendor Management'}</h2>
                        {isAdmin && (
                            <Button onClick={() => setEditingSingleItem({ table: 'vendors', data: { id: `v${Date.now()}`, name: '' } })} color={primaryColor} className="whitespace-nowrap px-4 py-2.5 rounded-xl shadow-sm">
                                <Icon path={ICONS_MAP['add']} className="w-5 h-5 sm:mr-1.5" />
                                <span className="hidden sm:inline">{t('add') || 'Add Vendor'}</span>
                            </Button>
                        )}
                    </div>
                    <Card>
                        {renderTable('vendors', [
                            { key: 'name', label: t('vendorName') || 'Vendor Name' },
                            {
                                key: 'actions', label: t('actions') || 'Actions', content: (row) => <div className="flex gap-2">
                                    <button onClick={() => setEditingSingleItem({ table: 'vendors', data: row })} className="text-blue-500 hover:underline">{t('edit')}</button>
                                    <button onClick={() => handleDeleteVendor(row.id, row.name)} className="text-red-500 hover:underline">{t('delete') || 'Delete'}</button>
                                </div>
                            }
                        ], data.vendors || [])}
                    </Card>
                </div>
            )}
        </div>
    );
}

function CustomersView({ data, setData, setView, handleFileUpload, handleDeleteCustomer, shippingFees, settings, generalSettings, setEditingSingleItem, onCustomerClick, showAlert }) {
    const { t, language, isRTL } = useLanguage();
    const [subTab, setSubTab] = useState('Customers');
    const [filterRank, setFilterRank] = useState('All');
    const [filterState, setFilterState] = useState('All');
    const [customerSearch, setCustomerSearch] = useState('');
    const [shippingSearch, setShippingSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const filterRef = useRef(null);
    const [viewMode, setViewMode] = useState(() => {
        try {
            const saved = localStorage.getItem('customersViewMode');
            return saved ? JSON.parse(saved) : { customers: 'auto', shipping: 'auto' };
        } catch {
            return { customers: 'auto', shipping: 'auto' };
        }
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const primaryColor = settings?.primaryColor || 'indigo';
    const colorMap = {
        indigo: { text: '#4F46E5' },
        blue: { text: '#2563EB' },
        green: { text: '#059669' },
        purple: { text: '#7C3AED' },
        pink: { text: '#DB2777' },
        orange: { text: '#EA580C' },
        red: { text: '#DC2626' },
        yellow: { text: '#CA8A04' },
        violet: { text: COLOR_PALETTE.violet },
        teal: { text: '#0D9488' },
        cyan: { text: '#0891B2' },
        amber: { text: '#D97706' },
        lime: { text: '#65A30D' },
        fuchsia: { text: '#C026D3' },
        rose: { text: '#E11D48' },
    };
    const themeColor = colorMap[primaryColor]?.text || colorMap.indigo.text;

    const effectiveViewMode = (subTab) => {
        const mode = viewMode[subTab.toLowerCase()] || 'auto';
        return mode === 'auto' ? (isMobile ? 'cards' : 'table') : mode;
    };

    const toggleViewMode = (subTab) => {
        const current = viewMode[subTab.toLowerCase()] || 'auto';
        const newMode = current === 'table' ? 'cards' : current === 'cards' ? 'auto' : 'table';
        const newViewMode = { ...viewMode, [subTab.toLowerCase()]: newMode };
        setViewMode(newViewMode);
        localStorage.setItem('customersViewMode', JSON.stringify(newViewMode));
    };

    const shippingMap = useMemo(() => new Map(shippingFees.map(s => [`${s.state?.trim()}-${s.city?.trim()}`, s])), [shippingFees]);
    const allShippingStates = useMemo(() => [...new Set(shippingFees.map(s => s.state))], [shippingFees]);
    const filterStates = ['All', ...allShippingStates];

    const CustomerCard = ({ customer, onCustomerClick }) => {
        const shippingFee = Math.floor(safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees));
        const rank = getCustomerRank(customer, data.orders, t, primaryColor);

        return (
            <Card className="flex flex-col justify-between h-full hover:shadow-lg transition-shadow">
                <div className="text-center">
                    <h3
                        className="font-bold text-lg cursor-pointer hover:underline text-gray-900"
                        onClick={() => {
                            if (onCustomerClick) {
                                onCustomerClick(customer);
                            }
                        }}
                    >
                        {customer.name}
                    </h3>
                    <p className="text-sm text-gray-500">{customer.mobile}</p>
                </div>
                <div className="mt-4 text-center">
                    <p className="text-sm text-gray-500">{t('location')}</p>
                    <p className="font-semibold text-gray-700">{customer.city}, {customer.state}</p>
                    {customer.address && <p className="text-sm text-gray-600 mt-1">{customer.address}</p>}
                </div>
                <div className="mt-2 text-center border-t pt-2">
                    <p className="text-sm text-gray-500">{t('shippingFee')}</p>
                    <p className="font-semibold text-gray-700">{getCurrencySymbol(data.generalSettings.currency)}{shippingFee}</p>
                </div>
                <div className="mt-2 text-center border-t pt-2">
                    <p className="text-sm text-gray-500">{t('rank')}</p>
                    <Badge color={`bg-${rank.color}-100 text-${rank.color}-800`}>{rank.text}</Badge>
                </div>
                {customer.cancels?.length > 0 && (
                    <div className="mt-2 bg-yellow-100 p-2 rounded-md text-yellow-800 text-sm flex items-center justify-center gap-2">
                        <Icon path={ICONS_MAP['warning']} className="w-5 h-5" />
                        <span>{t('cancelled')}: {customer.cancels.length} {t('times')}</span>
                    </div>
                )}
                <div className="mt-2 text-center flex justify-center gap-2">
                    <Button onClick={() => setEditingSingleItem({ table: 'customers', data: customer })} size="sm" color={primaryColor}>{t('edit')}</Button>
                </div>
            </Card>
        );
    };

    const ShippingCard = ({ rule }) => (
        <Card className="flex flex-col justify-between h-full hover:shadow-lg transition-shadow">
            <div className="text-center">
                <h3 className="font-bold text-lg text-gray-800">{rule.state}</h3>
                <p className="text-sm text-gray-500">{rule.city}</p>
            </div>
            <div className="mt-4 text-center">
                <p className="text-sm text-gray-500">{t('shippingFee')}</p>
                <p className="text-xl font-extrabold" style={{ color: themeColor }}>{getCurrencySymbol(data.generalSettings.currency)}{Math.floor(safeCoerceNumber(rule.fees))}</p>
            </div>
            <div className="mt-2 text-center flex justify-center gap-2">
                <Button onClick={() => setEditingSingleItem({ table: 'shipping', data: rule })} size="sm" color={primaryColor}>{t('edit')}</Button>
            </div>
        </Card>
    );

    const filteredCustomers = useMemo(() => {
        let filtered = data.customers;
        if (filterRank !== 'All') {
            filtered = filtered.filter(c => {
                const rank = getCustomerRank(c, data.orders, t, primaryColor);
                return rank.type === filterRank;
            });
        }
        if (filterState !== 'All') {
            filtered = filtered.filter(c => c.state === filterState);
        }
        if (customerSearch.trim()) {
            const searchNorm = normalizeSearch(customerSearch);
            filtered = filtered.filter(c =>
                (c.name && normalizeSearch(c.name).includes(searchNorm)) ||
                (c.mobile && c.mobile.includes(customerSearch.trim()))
            );
        }
        return filtered;
    }, [data.customers, data.orders, filterRank, filterState, customerSearch, primaryColor, t]);

    const customersByState = useMemo(() => {
        return filteredCustomers.reduce((acc, customer) => {
            const state = customer.state || 'N/A';
            if (!acc[state]) acc[state] = [];
            acc[state].push(customer);
            return acc;
        }, {});
    }, [filteredCustomers]);

    const filteredShipping = useMemo(() => {
        let filtered = data.shipping;
        if (filterState !== 'All') {
            filtered = filtered.filter(s => s.state === filterState);
        }
        if (shippingSearch.trim()) {
            const searchNorm = normalizeSearch(shippingSearch);
            filtered = filtered.filter(s =>
                (s.state && normalizeSearch(s.state).includes(searchNorm)) ||
                (s.city && normalizeSearch(s.city).includes(searchNorm))
            );
        }
        return filtered;
    }, [data.shipping, shippingSearch, filterState]);

    const CustomerRow = ({ customer }) => {
        const shippingFee = Math.floor(safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees));
        const rank = getCustomerRank(customer, data.orders, t, primaryColor);

        return (
            <tr key={customer.id} className="hover:bg-gray-50 border-t border-gray-100">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <span
                        className="cursor-pointer hover:underline text-gray-900"
                        onClick={() => {
                            if (onCustomerClick) {
                                onCustomerClick(customer);
                            }
                        }}
                    >
                        {customer.name}
                    </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.mobile}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.state}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.city}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center font-semibold text-gray-700">
                    {getCurrencySymbol(data.generalSettings.currency)}{shippingFee}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <Badge color={`bg-${rank.color}-100 text-${rank.color}-800`}>{rank.text}</Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                    <div className="flex justify-end items-center gap-2">
                        {customer.cancels?.length > 0 && (
                            <div className="bg-yellow-100 px-2 py-1 rounded text-yellow-800 text-xs flex items-center gap-1">
                                <Icon path={ICONS_MAP['warning']} className="w-4 h-4" />{customer.cancels.length}
                            </div>
                        )}
                        <IconButton icon="edit" color={primaryColor} onClick={() => setEditingSingleItem({ table: 'customers', data: customer })} tooltip={t('editCustomer')} />
                        <IconButton icon="trash" color="red" onClick={() => handleDeleteCustomer(customer.id)} tooltip={t('deleteCustomer')} />
                    </div>
                </td>
            </tr>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center gap-4">
                <h1 className="text-2xl font-bold text-gray-800">{t('customersAndShipping')}</h1>
                <div className="flex gap-2">
                    {subTab === 'Customers' && (
                        <>
                            <Button onClick={() => toggleViewMode('customers')} color="gray" size="sm" className="px-2" title={t('cards')}>
                                <Icon path={effectiveViewMode('customers') === 'table' ? ICONS_MAP['table'] : ICONS_MAP['cards']} className="w-5 h-5 text-gray-600" />
                            </Button>
                            <AdminOnly>
                                <Button onClick={() => document.getElementById('csv-upload-customers').click()} color="gray" size="sm">
                                    <Icon path={ICONS_MAP['upload']} className="w-4 h-4" />
                                </Button>
                            </AdminOnly>
                            <input id="csv-upload-customers" type="file" accept=".csv" className="hidden" onChange={(e) => handleFileUpload(e, 'customers')} />
                            <Button onClick={() => setView('New Customer')} color={primaryColor} size="sm">
                                <Icon path={ICONS_MAP['add']} className="w-4 h-4 mr-1" />
                                {language === 'ar' ? t('add') : 'Add New'}
                            </Button>
                        </>
                    )}
                    {subTab === 'Shipping' && (
                        <>
                            <Button onClick={() => toggleViewMode('shipping')} color="gray" size="sm" className="px-2" title={t('cards')}>
                                <Icon path={effectiveViewMode('shipping') === 'table' ? ICONS_MAP['table'] : ICONS_MAP['cards']} className="w-5 h-5 text-gray-600" />
                            </Button>
                            <AdminOnly>
                                <Button onClick={() => setEditingSingleItem({ table: 'shipping', data: { id: `s${Date.now()}`, state: '', city: '', fees: 0 } })} color={primaryColor} size="sm">
                                    <Icon path={ICONS_MAP['add']} className="w-4 h-4 mr-1" />
                                    {t('addCity')}
                                </Button>
                            </AdminOnly>
                            <AdminOnly>
                                <Button onClick={() => document.getElementById('csv-upload-shipping').click()} color="gray" size="sm">
                                    <Icon path={ICONS_MAP['upload']} className="w-4 h-4" />
                                </Button>
                            </AdminOnly>
                            <input id="csv-upload-shipping" type="file" accept=".csv" className="hidden" onChange={(e) => handleFileUpload(e, 'shipping')} />
                        </>
                    )}
                </div>
            </div>

            <div className="mb-6">
                <div className="inline-flex rounded-xl shadow-sm overflow-hidden border border-gray-100">
                    {[
                        { key: 'Customers', label: t('customers') },
                        { key: 'Shipping', label: t('shipping') }
                    ].map(({ key, label }, idx, arr) => (
                        <button
                            key={key}
                            onClick={() => setSubTab(key)}
                            className={`px-6 py-2.5 text-sm font-bold transition-all duration-200 ${subTab === key
                                ? `bg-${primaryColor}-600 text-white shadow-lg`
                                : 'bg-white text-gray-500 hover:bg-gray-50'
                                } ${idx !== arr.length - 1 ? 'border-r border-gray-100' : ''}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {subTab === 'Customers' && (
                <div>
                    <div className="flex flex-row gap-3 items-center mb-6">
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Icon path={ICONS_MAP['customers']} className="w-5 h-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder={t('searchCustomer')}
                                value={customerSearch}
                                onChange={(e) => setCustomerSearch(e.target.value)}
                                className={`w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 focus:bg-white rounded-xl text-sm transition-all duration-300 focus:ring-2 focus:ring-${primaryColor}-500 focus:border-${primaryColor}-500 outline-none`}
                            />
                            {customerSearch && (
                                <button
                                    onClick={() => setCustomerSearch('')}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                >
                                    <Icon path={ICONS_MAP['close']} className="w-5 h-5 text-gray-500" />
                                </button>
                            )}
                        </div>

                        <div className="relative" ref={filterRef}>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`p-2.5 rounded-xl border transition-all duration-200 ${showFilters ? `bg-${primaryColor}-50 border-${primaryColor}-200 text-${primaryColor}-600` : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50'}`}
                            >
                                <Icon path={ICONS_MAP['filter']} className="w-5 h-5" />
                            </button>

                            {showFilters && (
                                <div className={`absolute top-full mt-2 ${isRTL ? 'left-0' : 'right-0'} bg-white border border-gray-100 rounded-xl shadow-xl z-50 p-4 min-w-[240px] space-y-4`}>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">{t('rank')}</label>
                                        <select value={filterRank} onChange={e => setFilterRank(e.target.value)} className={`w-full p-2 border border-gray-200 rounded-lg text-sm ${FOCUSED_INPUT_STYLE}`}>
                                            <option value="All">{t('all')}</option>
                                            {['New', 'Returning', 'Regular', 'VIP', 'Elite'].map(r => (
                                                <option key={r} value={r}>
                                                    {t(r.toLowerCase() + 'Rank') || r}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">{t('state')}</label>
                                        <select value={filterState} onChange={e => setFilterState(e.target.value)} className={`w-full p-2 border border-gray-200 rounded-lg text-sm ${FOCUSED_INPUT_STYLE}`}>
                                            {filterStates.map(state => <option key={state} value={state}>{state}</option>)}
                                        </select>
                                    </div>
                                    <div className="pt-2 border-t flex justify-end">
                                        <button onClick={() => setShowFilters(false)} className={`text-sm font-bold text-${primaryColor}-600 hover:underline`}>{t('done')}</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {effectiveViewMode('customers') === 'cards' ? (
                        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {Object.entries(customersByState).map(([state, customersInState]) => (
                                <React.Fragment key={state}>
                                    <div className="col-span-full">
                                        <h3 className="font-bold text-lg mb-2" style={{ color: themeColor }}>
                                            {state} ({customersInState.length})
                                        </h3>
                                    </div>
                                    {customersInState.map(customer => <CustomerCard key={customer.id} customer={customer} onCustomerClick={onCustomerClick} />)}
                                </React.Fragment>
                            ))}
                        </div>
                    ) : (
                        <Card>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('name')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('mobile')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('state')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('city')}</th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('shippingFee')}</th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('rank')}</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {Object.entries(customersByState).map(([state, customersInState]) => (
                                            <React.Fragment key={state}>
                                                <tr className="bg-gray-50">
                                                    <td colSpan="7" className="px-6 py-2 text-sm font-bold" style={{ color: themeColor }}>
                                                        {state} ({customersInState.length})
                                                    </td>
                                                </tr>
                                                {customersInState.map(customer => <CustomerRow key={customer.id} customer={customer} />)}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    )}
                </div>
            )}

            {subTab === 'Shipping' && (
                <div className="space-y-6">
                    <div className="flex flex-row gap-3 items-center mb-6">
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Icon path={ICONS_MAP['shipped']} className="w-5 h-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder={t('searchShipping')}
                                value={shippingSearch}
                                onChange={(e) => setShippingSearch(e.target.value)}
                                className={`w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 focus:bg-white rounded-xl text-sm transition-all duration-300 focus:ring-2 focus:ring-${primaryColor}-500 focus:border-${primaryColor}-500 outline-none`}
                            />
                            {shippingSearch && (
                                <button
                                    onClick={() => setShippingSearch('')}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                >
                                    <Icon path={ICONS_MAP['close']} className="w-5 h-5 text-gray-500" />
                                </button>
                            )}
                        </div>

                        <div className="relative" ref={filterRef}>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`p-2.5 rounded-xl border transition-all duration-200 ${showFilters ? `bg-${primaryColor}-50 border-${primaryColor}-200 text-${primaryColor}-600` : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50'}`}
                            >
                                <Icon path={ICONS_MAP['filter']} className="w-5 h-5" />
                            </button>

                            {showFilters && (
                                <div className={`absolute top-full mt-2 ${isRTL ? 'left-0' : 'right-0'} bg-white border border-gray-100 rounded-xl shadow-xl z-50 p-4 min-w-[240px] space-y-4`}>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">{t('state')}</label>
                                        <select value={filterState} onChange={e => setFilterState(e.target.value)} className={`w-full p-2 border border-gray-200 rounded-lg text-sm ${FOCUSED_INPUT_STYLE}`}>
                                            {filterStates.map(state => <option key={state} value={state}>{state}</option>)}
                                        </select>
                                    </div>
                                    <div className="pt-2 border-t flex justify-end">
                                        <button onClick={() => setShowFilters(false)} className={`text-sm font-bold text-${primaryColor}-600 hover:underline`}>{t('done')}</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {effectiveViewMode('shipping') === 'cards' ? (
                        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {filteredShipping.map((rule, idx) => (
                                <ShippingCard key={rule.id || idx} rule={rule} />
                            ))}
                        </div>
                    ) : (
                        <Card>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('state')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('city')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('shippingFeesColumn')}</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {(() => {
                                            const shippingByState = filteredShipping.reduce((acc, rule) => {
                                                const s = rule.state || 'N/A';
                                                if (!acc[s]) acc[s] = [];
                                                acc[s].push(rule);
                                                return acc;
                                            }, {});

                                            return Object.entries(shippingByState).map(([state, rules]) => (
                                                <React.Fragment key={state}>
                                                    <tr className="bg-gray-50">
                                                        <td colSpan="4" className="px-6 py-2 text-sm font-bold" style={{ color: themeColor }}>
                                                            {state} ({rules.length})
                                                        </td>
                                                    </tr>
                                                    {rules.map((rule, idx) => (
                                                        <tr key={rule.id || idx} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 text-sm text-gray-700">{rule.state}</td>
                                                            <td className="px-6 py-4 text-sm text-gray-700">{rule.city}</td>
                                                            <td className="px-6 py-4 text-sm text-gray-700 font-semibold">{getCurrencySymbol(generalSettings.currency)}{Math.floor(rule.fees)}</td>
                                                            <td className="px-6 py-4 text-right text-sm">
                                                                <div className="flex justify-end gap-2">
                                                                    <IconButton icon="edit" color={primaryColor} onClick={() => setEditingSingleItem({ table: 'shipping', data: rule })} tooltip={t('editRule')} />
                                                                    <AdminOnly>
                                                                        <IconButton icon="trash" color="red" onClick={async () => {
                                                                            if (window.confirm(`${t('deleteRule')}?`)) {
                                                                                setData(prev => ({
                                                                                    ...prev,
                                                                                    shipping: prev.shipping.filter(s => s.id !== rule.id)
                                                                                }));
                                                                                await supabaseTracked.from('shipping').delete().eq('id', rule.id);
                                                                                showAlert('Shipping rule deleted.');
                                                                            }
                                                                        }} tooltip={t('deleteRule')} />
                                                                    </AdminOnly>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </React.Fragment>
                                            ));
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
}


function OldSettingsView({ data, setData, showAlert, syncSettings }) {
    // Icons provided from the global ICON_OPTIONS constant
    const [iconMap, setIconMap] = useState(data.settings.statusIcons);
    const [logoUrl, setLogoUrl] = useState(data.settings.logoUrl);
    const [primaryColor, setPrimaryColor] = useState(data.settings.primaryColor);

    // Status color mapping for display/edit
    const [statusColors, setStatusColors] = useState(data.settings.statusColors);

    // Store settings
    const [storeName, setStoreName] = useState(data.generalSettings.storeName);
    const [currency, setCurrency] = useState(data.generalSettings.currency);

    // Supabase settings
    const [supabaseUrl, setSupabaseUrl] = useState(() => {
        const settings = localStorage.getItem('lilac_supabase_settings');
        return settings ? JSON.parse(settings).url || '' : '';
    });
    const [supabaseKey, setSupabaseKey] = useState(() => {
        const settings = localStorage.getItem('lilac_supabase_settings');
        return settings ? JSON.parse(settings).key || '' : '';
    });
    const [supabaseEnabled, setSupabaseEnabled] = useState(() => {
        const settings = localStorage.getItem('lilac_supabase_settings');
        return settings ? JSON.parse(settings).enabled || false : false;
    });

    // Get all unique icon keys (for dropdown options)
    const allIconKeys = useMemo(() => CUSTOM_ICONS.map(i => i.key), []);
    // Map of key to object for easy lookup
    const iconKeyMap = useMemo(() => new Map(CUSTOM_ICONS.map(i => [i.key, i])), []);


    const handleSaveSettings = async () => {
        setData(prev => ({
            ...prev,
            settings: {
                ...prev.settings, // Keep any other settings
                logoUrl,
                primaryColor,
                statusIcons: iconMap,
                statusColors: statusColors,
            },
            generalSettings: {
                storeName,
                currency
            }
        }));
        localStorage.setItem('lilac_supabase_settings', JSON.stringify({ url: supabaseUrl, key: supabaseKey, enabled: supabaseEnabled }));

        // Save store name, logo, and primary color to localStorage for login page
        localStorage.setItem('storeName', storeName);
        localStorage.setItem('logoUrl', logoUrl);
        localStorage.setItem('primaryColor', primaryColor);
        console.log('💾 Saved store branding to localStorage:', { storeName, logoUrl, primaryColor });

        // Sync to Supabase
        console.log('🔄 Syncing settings to Supabase...');
        await syncSettings();

        showAlert('Settings saved! You may need to refresh the application to fully apply color/icon changes.');
    };

    const handleExport = (type, tableKey) => {
        if (!window.Papa) {
            showAlert("Data export unavailable: Parsing library not loaded.");
            return;
        }

        if (type === 'json') {
            exportJson(data, 'lilac_valley_crm_export');
            showAlert('Full data export successful!');
        } else if (type === 'csv') {
            let dataToExport = [];
            let fileName = '';

            switch (tableKey) {
                case 'orders':
                    dataToExport = data.orders.map(o => {
                        const customer = data.customers.find(c => c.id === o.customerid);
                        const shippingMap = new Map(data.shipping.map(s => [`${s.state?.trim()}-${s.city?.trim()}`, s]));
                        const shippingFee = customer ? (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;
                        return {
                            orderid: o.orderid,
                            customerid: o.customerid,
                            status: o.status,
                            total: safeCoerceNumber(o.total).toFixed(2),
                            discount: safeCoerceNumber(o.discount).toFixed(2),
                            shipping_fee: Math.floor(shippingFee),
                            payment: o.payment,
                            date: new Date(o.date).toLocaleDateString(),
                            items_json: JSON.stringify(o.items.map(i => `${i.pid} x ${i.qty}${i.used ? ' (Used)' : ''}`)),
                        };
                    });
                    fileName = 'lilac_valley_orders';
                    break;
                case 'customers':
                    dataToExport = data.customers;
                    fileName = 'lilac_valley_customers';
                    break;
                case 'products':
                    dataToExport = data.products;
                    fileName = 'lilac_valley_products';
                    break;
                case 'oils':
                    dataToExport = data.oils;
                    fileName = 'lilac_valley_oils';
                    break;
                case 'bottles':
                    dataToExport = data.bottles;
                    fileName = 'lilac_valley_bottles';
                    break;
                default:
                    showAlert('Invalid table selected for CSV export.');
                    return;
            }

            exportCsv(dataToExport, fileName);
            showAlert(`${tableKey.charAt(0).toUpperCase() + tableKey.slice(1)} exported to CSV (Excel format)!`);
        }
    };

    const handleJsonImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const importedData = JSON.parse(event.target.result);
                setData(currentData => {
                    const newData = { ...currentData };
                    Object.keys(importedData).forEach(key => {
                        if (Array.isArray(importedData[key])) {
                            newData[key] = importedData[key];
                        } else if (key === 'returnedPool' && typeof importedData[key] === 'object') {
                            newData[key] = { ...newData[key] };
                            Object.keys(importedData[key]).forEach(poolKey => {
                                newData[key][poolKey] = (newData[key][poolKey] || 0) + safeCoerceNumber(importedData[key][poolKey]);
                            });
                        } else {
                            newData[key] = importedData[key];
                        }
                    });
                    return newData;
                });
                showAlert('Imported JSON successfully.');
            } catch (error) {
                showAlert('Error importing JSON: ' + error.message);
            }
        };
        reader.readAsText(file);
        e.target.value = null;
    };

    // Placeholder function for user management 
    const handleAddUser = () => {
        showAlert('User management is a placeholder function requiring backend integration (Firestore/Auth) for production use.');
    };

    const handleIconChange = (status, iconKey) => {
        setIconMap(prev => ({ ...prev, [status]: iconKey }));
    }
    const handleColorChange = (status, color) => {
        setStatusColors(prev => ({ ...prev, [status]: color }));
    }

    // Swatch component for color selection
    const ColorSwatch = ({ colorName, currentColor, onChange }) => (
        <button
            type="button"
            className={`w-full h-8 flex items-center justify-center border-2 rounded-md transition-all ${currentColor === colorName ? `border-${colorName}-700 ring-2 ring-${colorName}-500` : 'border-gray-300'}`}
            onClick={() => onChange(colorName)}
        >
            <span className={`w-full h-full bg-${colorName}-600 block rounded-[4px]`}></span>
        </button>
    );

    return (
        <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-6">Settings & Data Management</h1>

            {/* UI Customization */}
            <Card className="space-y-6">
                <h2 className={`text-xl font-bold text-${primaryColor}-700`}>App Customization</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="storeName" className="block text-sm font-medium text-gray-700">Store Name</label>
                        <input type="text" id="storeName" value={storeName} onChange={e => setStoreName(e.target.value)} className={`mt-1 block w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                        <select value={currency} onChange={e => setCurrency(e.target.value)} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                            <option value="GBP">GBP ({getCurrencySymbol('GBP')})</option>
                            <option value="USD">USD ({getCurrencySymbol('USD')})</option>
                            <option value="EGP">EGP (EGP)</option>
                            <option value="SAR">SAR (SAR)</option>
                            <option value="AED">AED (AED)</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label htmlFor="logoUrl" className="block text-sm font-medium text-gray-700">Logo Image URL</label>
                        <input type="text" id="logoUrl" value={logoUrl} onChange={e => setLogoUrl(e.target.value)} className={`mt-1 block w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                        <p className="mt-2 text-sm text-gray-500">Current Logo Preview: <img src={logoUrl} alt="Logo Preview" className="w-10 h-10 inline-block rounded-full ml-2" onError={(e) => e.target.src = initialData.settings.logoUrl} /></p>
                    </div>
                    {/* Primary Color Selector with Color Swatches */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color (Nav/Buttons)</label>
                        <div className="grid grid-cols-4 gap-2">
                            {(() => {
                                const colorMap = {
                                    indigo: { bg: '#4F46E5', hover: '#4338CA', border: '#3730A3', ring: '#6366F1' },
                                    blue: { bg: '#2563EB', hover: '#1D4ED8', border: '#1E3A8A', ring: '#3B82F6' },
                                    green: { bg: '#059669', hover: '#047857', border: '#064E3B', ring: '#10B981' },
                                    purple: { bg: '#7C3AED', hover: '#6D28D9', border: '#5B21B6', ring: '#8B5CF6' },
                                    pink: { bg: '#DB2777', hover: '#BE185D', border: '#9F1239', ring: '#EC4899' },
                                    orange: { bg: '#EA580C', hover: '#C2410C', border: '#9A3412', ring: '#F97316' },
                                    red: { bg: '#DC2626', hover: '#B91C1C', border: '#991B1B', ring: '#EF4444' },
                                    yellow: { bg: '#CA8A04', hover: '#A16207', border: '#854D0E', ring: '#EAB308' },
                                };
                                return TAILWIND_COLORS.map(c => {
                                    const colors = colorMap[c];
                                    const isSelected = primaryColor === c;
                                    return (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => setPrimaryColor(c)}
                                            className="h-10 rounded-md border-2 transition-all"
                                            style={{
                                                backgroundColor: colors.bg,
                                                borderColor: isSelected ? colors.border : '#D1D5DB',
                                                boxShadow: isSelected ? `0 0 0 2px ${colors.ring}` : 'none'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = colors.hover;
                                                e.currentTarget.style.borderColor = colors.border;
                                                e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.ring}`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = colors.bg;
                                                e.currentTarget.style.borderColor = isSelected ? colors.border : '#D1D5DB';
                                                e.currentTarget.style.boxShadow = isSelected ? `0 0 0 2px ${colors.ring}` : 'none';
                                            }}
                                            title={c.charAt(0).toUpperCase() + c.slice(1)}
                                        />
                                    );
                                });
                            })()}
                        </div>
                        <p className="mt-2 text-sm text-gray-500">Selected: <span className={`font-bold text-${primaryColor}-600`}>{primaryColor.charAt(0).toUpperCase() + primaryColor.slice(1)}</span></p>
                    </div>
                </div>
                <div className="pt-4 border-t flex justify-end">
                    <Button onClick={handleSaveSettings} color="green"><Icon path={ICONS_MAP['save']} className="w-5 h-5" /> Save Settings</Button>
                </div>
            </Card>

            {/* Status Customization */}
            <Card className="space-y-4">
                <h2 className={`text-xl font-bold text-${primaryColor}-700`}>Order Status Visuals</h2>
                <p className="text-sm text-gray-600">Customize the icons and colors used on the Orders Board. **Click 'Save UI Settings' above to save changes.**</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.keys(statusColors).map(status => (
                        <div key={status} className="p-3 border rounded-lg bg-gray-50">
                            <h3 className="font-semibold capitalize mb-2 flex items-center">
                                <Icon path={ICONS_MAP[iconMap[status]]} className={`w-5 h-5 mr-2 text-${statusColors[status]}-600`} />
                                {status}
                            </h3>
                            <div className="space-y-2">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500">Icon</label>
                                    <select value={iconMap[status]} onChange={e => handleIconChange(status, e.target.value)} className={`w-full p-1 border rounded-md text-sm ${FOCUSED_INPUT_STYLE}`}>
                                        {allIconKeys.map(key => (
                                            <option key={key} value={key}>
                                                {iconKeyMap.get(key)?.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500">Color</label>
                                    <select value={statusColors[status]} onChange={e => handleColorChange(status, e.target.value)} className={`w-full p-1 border rounded-md text-sm ${FOCUSED_INPUT_STYLE}`}>
                                        {TAILWIND_COLORS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            {/* Data Export */}
            <Card className="space-y-6">
                <h2 className={`text-xl font-bold text-${primaryColor}-700`}>Data Export</h2>
                <p className="text-gray-600">Export your current session data for backup and analysis.</p>

                {/* Export All Tables */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {Object.keys(data).filter(key => ['orders', 'customers', 'products', 'oils', 'bottles'].includes(key)).map(tableKey => (
                        <Button key={tableKey} onClick={() => handleExport('csv', tableKey)} color="gray" size="sm" className="justify-center">
                            Export {tableKey.charAt(0).toUpperCase() + tableKey.slice(1)} (CSV)
                        </Button>
                    ))}
                </div>

                <div className="flex gap-4 pt-4 border-t">
                    <Button onClick={() => handleExport('json')} color="gray" className="flex-1 justify-center">Export Full Data (JSON)</Button>
                    <Button onClick={() => document.getElementById('json-import').click()} color="gray" className="flex-1 justify-center">Import JSON</Button>
                    <input id="json-import" type="file" accept=".json" className="hidden" onChange={handleJsonImport} />
                </div>
            </Card>

            {/* User Management */}
            <Card className="space-y-6">
                <h2 className={`text-xl font-bold text-${primaryColor}-700`}>User Management (Placeholder)</h2>
                <p className="text-gray-600">Add users to collaborate on this app. Note: Actual persistent user management requires dedicated backend services (like Firebase Auth) not included in this single-file app structure.</p>
                <Button onClick={handleAddUser} color="purple"><Icon path={ICONS_MAP['add']} className="w-5 h-5" /> Add New User</Button>
            </Card>

            {/* Stock Management Settings */}
            <Card className="space-y-6">
                <h2 className={`text-xl font-bold text-${primaryColor}-700`}>Stock Management Settings</h2>
                <p className="text-gray-600">Configure thresholds, categories, and labels for stock management.</p>

                {/* Inventory Labels */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Inventory Labels</h3>
                    <p className="text-sm text-gray-600 mb-3">Customize the names used throughout the app for your inventory items.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Bottles Label</label>
                            <input
                                type="text"
                                value={data.stockSettings.inventoryLabels?.bottles || 'Bottles'}
                                onChange={e => setData(prev => ({
                                    ...prev,
                                    stockSettings: {
                                        ...prev.stockSettings,
                                        inventoryLabels: {
                                            ...prev.stockSettings.inventoryLabels,
                                            bottles: e.target.value
                                        }
                                    }
                                }))}
                                className={`mt-1 block w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}
                                placeholder="e.g., Bottles, Containers, Packages"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Oils Label</label>
                            <input
                                type="text"
                                value={data.stockSettings.inventoryLabels?.oils || 'Oils'}
                                onChange={e => setData(prev => ({
                                    ...prev,
                                    stockSettings: {
                                        ...prev.stockSettings,
                                        inventoryLabels: {
                                            ...prev.stockSettings.inventoryLabels,
                                            oils: e.target.value
                                        }
                                    }
                                }))}
                                className={`mt-1 block w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}
                                placeholder="e.g., Oils, Materials, Ingredients"
                            />
                        </div>
                    </div>
                </div>

                {/* Low Stock Thresholds */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Low Stock Thresholds</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Low Stock Threshold - {data.stockSettings.inventoryLabels?.oils || 'Oils'} (ml)</label>
                            <input type="number" value={data.stockSettings.lowStockThresholds.oils} onChange={e => setData(prev => ({ ...prev, stockSettings: { ...prev.stockSettings, lowStockThresholds: { ...prev.stockSettings.lowStockThresholds, oils: parseInt(e.target.value) || 50 } } }))} min="0" className={`mt-1 block w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Low Stock Threshold - {data.stockSettings.inventoryLabels?.bottles || 'Bottles'} (units)</label>
                            <input type="number" value={data.stockSettings.lowStockThresholds.bottles} onChange={e => setData(prev => ({ ...prev, stockSettings: { ...prev.stockSettings, lowStockThresholds: { ...prev.stockSettings.lowStockThresholds, bottles: parseInt(e.target.value) || 10 } } }))} min="0" className={`mt-1 block w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                        </div>
                    </div>
                </div>
            </Card>

            {/* Supabase Settings */}
            <Card className="space-y-6">
                <h2 className={`text-xl font-bold text-${data.settings?.primaryColor || 'indigo'}-700`}>Supabase Settings (Future)</h2>
                <p className="text-gray-600">Configure Supabase for future backend integration. These settings are stored locally and not yet used.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="supabaseUrl" className="block text-sm font-medium text-gray-700">Supabase URL</label>
                        <input type="text" id="supabaseUrl" value={supabaseUrl} onChange={e => setSupabaseUrl(e.target.value)} className={`mt-1 block w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                    </div>
                    <div>
                        <label htmlFor="supabaseKey" className="block text-sm font-medium text-gray-700">Anon Key</label>
                        <input type="text" id="supabaseKey" value={supabaseKey} onChange={e => setSupabaseKey(e.target.value)} className={`mt-1 block w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                    </div>
                </div>
                <div className="flex items-center">
                    <input type="checkbox" id="supabaseEnabled" checked={supabaseEnabled} onChange={e => setSupabaseEnabled(e.target.checked)} className={`h-4 w-4 text-${data.settings?.primaryColor || 'indigo'}-600 border-gray-300 rounded focus:ring-${data.settings?.primaryColor || 'indigo'}-500`} />
                    <label htmlFor="supabaseEnabled" className="ml-2 block text-sm text-gray-900">Enable Supabase (future)</label>
                </div>
            </Card>
        </div>
    );
}


function SummaryView({ orders, products, customers, bottles, oils, shippingFees, calculateBuildableStock, setData, data, showAlert, syncObjectData, setShowLowStockAlert, onCustomerClick, pendingWebsiteOrders, setShowPendingWebsiteModal }) {
    const { t, isRTL } = useLanguage();
    const [period, setPeriod] = useState('All Time');
    const [startDate, setStartDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    });

    // Fix Recharts tooltips sticking on mobile
    useEffect(() => {
        const handleTouch = (e) => {
            if (!e.target.closest('.recharts-wrapper')) {
                const charts = document.querySelectorAll('.recharts-wrapper');
                charts.forEach(c => c.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true })));
            }
        };
        document.addEventListener('touchstart', handleTouch, { passive: true });
        return () => document.removeEventListener('touchstart', handleTouch);
    }, []);

    const [adsModalOpen, setAdsModalOpen] = useState(false);
    const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
    const [adsMonth, setAdsMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [adsAmount, setAdsAmount] = useState('');
    // Multi-select filter for bottle genders (null = all)
    const [bottleGenderFilter, setBottleGenderFilter] = useState(null); // null means ALL selected

    // Get primary color from settings
    const primaryColor = data.settings?.primaryColor || 'indigo';

    // Color mapping derived dynamically
    const themeColors = generateColorVariants(COLOR_PALETTE[primaryColor] || COLOR_PALETTE.indigo);

    // Get unique categories from bottles for dynamic chart buttons
    const availableCategories = useMemo(() => {
        const categories = [...new Set(bottles.map(b => b.gender))].filter(Boolean).sort();
        return categories;
    }, [bottles]);

    const [topProductsCategory, setTopProductsCategory] = useState(() => {
        // Default to first available category
        const cats = [...new Set(bottles.map(b => b.gender))].filter(Boolean).sort();
        return cats[0] || 'For Him';
    });

    // Fixed font size and maxWidth for chart labels to prevent changes on re-render
    const chartLabelFontSize = useMemo(() => {
        return Math.max(10, Math.min(13, window.innerWidth * 0.015));
    }, []); // Empty dependency array - calculate once and never change

    const chartLabelMaxWidth = useMemo(() => {
        return Math.max(60, calcPerTickWidth(window.innerWidth * 0.8, 12)); // Use reasonable default for max items
    }, []); // Empty dependency array - calculate once and never change

    const filteredOrders = useMemo(() => {
        if (period === 'All Time') {
            return orders;
        }
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999); // Include the entire end date
        return orders.filter(o => {
            const orderDate = new Date(o.date);
            return orderDate >= start && orderDate <= end;
        });
    }, [orders, period, startDate, endDate]);

    const prodMap = useMemo(() => new Map(products.map(p => [p.product, p])), [products]);
    const oilMap = useMemo(() => new Map(oils.map(o => [o.oil, o])), [oils]);
    const bottleMap = useMemo(() => new Map(bottles.map(b => [b.id, b])), [bottles]);
    const shippingMap = useMemo(() => new Map(shippingFees.map(s => [`${s.state?.trim()}-${s.city?.trim()}`, s])), [shippingFees]);
    const custMap = useMemo(() => new Map(customers.map(c => [c.id, c])), [customers]);

    const getAdsSum = (adsLedger, period, startDate, endDate) => {
        if (period === 'All Time') {
            return Object.values(adsLedger).flat().reduce((sum, amount) => sum + amount, 0);
        }
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return Object.entries(adsLedger).reduce((sum, [month, amounts]) => {
            const monthDate = new Date(month + '-01');
            if (monthDate >= start && monthDate <= end) {
                return sum + amounts.reduce((s, v) => s + v, 0);
            }
            return sum;
        }, 0);
    };

    const adsSum = getAdsSum(data.adsLedger, period, startDate, endDate);

    const kpis = useMemo(() => {
        // Active orders are in the pipeline, not completed or cancelled/returned
        const activeOrdersInPipeline = filteredOrders.filter(o => ['ordered', 'shipped', 'delivered'].includes(o.status));
        // Successful orders are those that are completed, which determine Revenue, Cost, and Profit
        const successfulOrders = filteredOrders.filter(o => o.status === 'completed');

        let revenue = 0; // Total Revenue: includes Visa orders in shipped/delivered/completed stages
        let shippedRevenue = 0; // Shipped Revenue: only shipped + delivered orders
        let costOfSoldGoods = 0;
        let costOfShippedGoods = 0;
        let shippingSum = 0;

        filteredOrders.forEach(order => {
            const customer = custMap.get(order.customerid);
            const isShippedOrLater = ['shipped', 'delivered', 'completed'].includes(order.status);
            const isShippedOrDelivered = ['shipped', 'delivered'].includes(order.status);
            const isCompleted = order.status === 'completed';
            const isVisa = order.payment === 'Visa';
            const isCash = order.payment === 'Cash';

            // Calculate Shipping Cost (deducted from gross profit calculation)
            if (customer) {
                shippingSum += (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0);
            }

            // Calculate Total Revenue: Completed orders + Visa orders in shipped/delivered stages
            if (isCompleted || (isVisa && isShippedOrLater)) {
                const currentShippingFee = customer ? (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;
                revenue += safeCoerceNumber(order.total) - currentShippingFee;
            }

            // Calculate Shipped Revenue: Only shipped + delivered orders (not completed)
            if (isShippedOrDelivered && isCash) {
                const currentShippingFee = customer ? (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;
                shippedRevenue += safeCoerceNumber(order.total) - currentShippingFee;
            }

            // Calculate COGS (Cost of Goods Sold/Shipped)
            let orderCost = 0;
            if (order.cost !== undefined && order.cost > 0) {
                orderCost = order.cost;
            } else {
                (order.items || []).forEach(item => {
                    const productInfo = prodMap.get(item.pid);
                    if (!productInfo) return;

                    // Calculate product cost dynamically using oil and bottle costs
                    const oilInfo = oilMap.get(productInfo.oil);
                    let bottleId;
                    if (productInfo.product?.toLowerCase().includes('tester')) { bottleId = `tester`; }
                    else { bottleId = `${productInfo.size}${productInfo.gender?.toLowerCase().replace(/ /g, '')}`; }

                    const bottleInfo = bottleMap.get(bottleId);
                    if (!oilInfo || !bottleInfo) return;

                    const oilCost = safeCoerceNumber(oilInfo.price) * safeCoerceNumber(productInfo.qtyoilinbottle);
                    const bottleCost = safeCoerceNumber(bottleInfo.totalcostbottle);
                    const lineItemCost = oilCost + bottleCost;

                    // If item is 'used', subtract bottle cost from COGS
                    orderCost += ((lineItemCost - (item.used ? bottleCost : 0)) * safeCoerceNumber(item.qty));
                });
            }

            if (isCompleted) {
                costOfSoldGoods += orderCost;
            }
            if (isShippedOrLater && !isCompleted) {
                costOfShippedGoods += orderCost;
            }
        });

        const grossProfit = revenue - costOfSoldGoods; // Gross Profit uses Revenue (Total - Shipping) and Cost of Sold Goods
        const profitAfterAds = grossProfit - adsSum;

        return {
            ordersCount: activeOrdersInPipeline.length,
            revenue,
            shippedRevenue,
            costOfSoldGoods,
            costOfShippedGoods,
            shippingSum,
            grossProfit,
            profitAfterAds,
            successfulOrdersCount: successfulOrders.length
        };
    }, [filteredOrders, prodMap, adsSum, oilMap, bottleMap, shippingMap, custMap]);

    // Previous period orders for trend calculation
    const prevPeriodOrders = useMemo(() => {
        if (period === 'All Time') return [];
        const start = new Date(startDate);
        const end = new Date(endDate);
        const durationMs = end.getTime() - start.getTime();
        const prevEnd = new Date(start.getTime() - 1);
        const prevStart = new Date(prevEnd.getTime() - durationMs);
        prevEnd.setHours(23, 59, 59, 999);
        return orders.filter(o => {
            const d = new Date(o.date);
            return d >= prevStart && d <= prevEnd;
        });
    }, [orders, period, startDate, endDate]);

    const prevKpis = useMemo(() => {
        const successfulOrders = prevPeriodOrders.filter(o => o.status === 'completed');
        const activeOrdersInPipeline = prevPeriodOrders.filter(o => ['ordered', 'shipped', 'delivered'].includes(o.status));
        let revenue = 0;
        prevPeriodOrders.forEach(order => {
            const customer = custMap.get(order.customerid);
            const isShippedOrLater = ['shipped', 'delivered', 'completed'].includes(order.status);
            const isCompleted = order.status === 'completed';
            const isVisa = order.payment === 'Visa';
            if (isCompleted || (isVisa && isShippedOrLater)) {
                const sf = customer ? (safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;
                revenue += safeCoerceNumber(order.total) - sf;
            }
        });
        return {
            successfulOrdersCount: successfulOrders.length,
            ordersCount: activeOrdersInPipeline.length,
            revenue
        };
    }, [prevPeriodOrders, custMap, shippingMap]);

    const calcTrend = (current, previous) => {
        if (previous === 0) return current > 0 ? { text: '+100%', up: true } : { text: '0%', up: true };
        const pct = ((current - previous) / Math.abs(previous) * 100).toFixed(0);
        return { text: `${pct >= 0 ? '+' : ''}${pct}%`, up: pct >= 0 };
    };

    const lowStockOils = useMemo(() => oils.filter(o => safeCoerceNumber(o.actualstock) < data.stockSettings.lowStockThresholds.oils), [oils, data.stockSettings.lowStockThresholds.oils]);
    const lowStockBottles = useMemo(() => bottles.filter(b => safeCoerceNumber(b.actualstock) < data.stockSettings.lowStockThresholds.bottles), [bottles, data.stockSettings.lowStockThresholds.bottles]);

    // Get available bottle sizes from bottles data
    const availableBottleSizes = useMemo(() => {
        const sizes = new Set();
        bottles.forEach(b => {
            if (b.size) sizes.add(b.size);
        });

        // Sort sizes in a logical order
        const sizeOrder = ['5ml', '20ml', '30ml', '50ml', '100ml', 'tester'];
        return Array.from(sizes).sort((a, b) => {
            const indexA = sizeOrder.indexOf(a);
            const indexB = sizeOrder.indexOf(b);
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return a.localeCompare(b);
        });
    }, [bottles]);

    // Group products by oil and aggregate by bottle size (dynamic based on available sizes)
    const topSellingProductsGrouped = useMemo(() => {
        let filtered = [...products];

        // Filter by category
        filtered = filtered.filter(p => p.gender === topProductsCategory);

        // Group by oil and aggregate quantities by bottle size
        const oilGroups = {};
        filtered.forEach(p => {
            const oil = p.oil || 'Unknown';
            if (!oilGroups[oil]) {
                // Initialize with all available bottle sizes
                const sizeObj = {};
                availableBottleSizes.forEach(size => {
                    sizeObj[size] = 0;
                });
                oilGroups[oil] = {
                    oil,
                    ...sizeObj,
                    total: 0,
                };
            }

            const size = p.size || '';
            const qty = safeCoerceNumber(p.qtysold);

            // Only count if size is in available sizes
            if (availableBottleSizes.includes(size)) {
                oilGroups[oil][size] += qty;
                oilGroups[oil].total += qty;
            }
        });

        // Convert to array and sort by total quantity
        return Object.values(oilGroups)
            .sort((a, b) => b.total - a.total)
            .slice(0, 7); // Top 7 oils
    }, [products, topProductsCategory, availableBottleSizes]);
    // Top customers based on successful orders
    const topCustomers = useMemo(() => {
        return [...customers].map(customer => {
            const completedOrdersCount = orders.filter(order => order.customerid === customer.id && order.status === 'completed').length;
            return { ...customer, completedOrdersCount };
        }).sort((a, b) => b.completedOrdersCount - a.completedOrdersCount).slice(0, 7);
    }, [customers, orders]);

    const topSellingSizes = useMemo(() => {
        // Generate bottle types dynamically from available bottles in stock
        const allBottleTypes = [];

        bottles.forEach(bottle => {
            if (bottle.size && bottle.gender) {
                // Create display name based on size and gender
                let displayName = `${bottle.size} ${t(bottle.gender.toLowerCase().replace(/ /g, '')) || bottle.gender}`;

                allBottleTypes.push({
                    size: bottle.size,
                    gender: bottle.gender,
                    displayName: displayName
                });
            }
        });

        // Calculate quantities for each bottle type
        const bottleData = allBottleTypes.map(bottleType => {
            const qty = data.products
                .filter(p => {
                    // Match size and gender
                    const sizeMatch = p.size === bottleType.size;
                    const genderMatch = p.gender === bottleType.gender ||
                        (bottleType.gender === 'Testers' && p.product?.toLowerCase().includes('tester'));
                    return sizeMatch && genderMatch;
                })
                .reduce((sum, p) => sum + safeCoerceNumber(p.qtysold), 0);

            return {
                size: bottleType.size,
                gender: bottleType.gender,
                displayName: bottleType.displayName,
                qty
            };
        });

        // Sort by quantity (highest first)
        return bottleData.sort((a, b) => b.qty - a.qty);
    }, [data.products, bottles]);

    const chartData = useMemo(() => {
        if (period === 'All Time') {
            // Group by month for all time
            const monthGroups = {};
            orders.forEach(o => {
                const d = new Date(o.date);
                const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                if (!monthGroups[monthKey]) monthGroups[monthKey] = [];
                monthGroups[monthKey].push(o);
            });

            return Object.entries(monthGroups).sort(([a], [b]) => a.localeCompare(b)).map(([month, monthOrders]) => {
                const totalOrders = monthOrders.filter(o => isCountableOrderStatus(o.status)).length;
                const completedOrders = monthOrders.filter(o => o.status === 'completed');
                const netRevenue = completedOrders.reduce((sum, o) => {
                    const customer = custMap.get(o.customerid);
                    const shippingFee = customer ? safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0 : 0;
                    return sum + safeCoerceNumber(o.total) - shippingFee;
                }, 0);
                const grossProfit = completedOrders.reduce((sum, o) => {
                    const customer = custMap.get(o.customerid);
                    const shippingFee = customer ? safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0 : 0;
                    const cost = (o.cost !== undefined && o.cost > 0) ? o.cost : o.items.reduce((itemSum, item) => {
                        const productInfo = prodMap.get(item.pid);
                        if (!productInfo) return itemSum;

                        // Calculate product cost dynamically using oil and bottle costs
                        const oilInfo = oilMap.get(productInfo.oil);
                        let bottleId;
                        if (productInfo.product?.toLowerCase().includes('tester')) bottleId = 'tester';
                        else bottleId = `${productInfo.size}${productInfo.gender?.toLowerCase().replace(/ /g, '')}`;
                        const bottleInfo = bottleMap.get(bottleId);
                        if (!oilInfo || !bottleInfo) return itemSum;

                        const oilCost = safeCoerceNumber(oilInfo.price) * safeCoerceNumber(productInfo.qtyoilinbottle);
                        const bottleCost = safeCoerceNumber(bottleInfo.totalcostbottle);
                        const lineCost = (oilCost + bottleCost) - (item.used ? bottleCost : 0);
                        return itemSum + lineCost * safeCoerceNumber(item.qty);
                    }, 0);
                    return sum + (safeCoerceNumber(o.total) - shippingFee - cost);
                }, 0);
                return { month, totalOrders, netRevenue, grossProfit };
            }).filter(d => d.totalOrders > 0 || d.netRevenue > 0 || d.grossProfit > 0);
        } else {
            // For date range periods, group by month within the range
            const start = new Date(startDate);
            const end = new Date(endDate);
            const monthGroups = {};

            filteredOrders.forEach(o => {
                const d = new Date(o.date);
                const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                if (!monthGroups[monthKey]) monthGroups[monthKey] = [];
                monthGroups[monthKey].push(o);
            });

            return Object.entries(monthGroups).sort(([a], [b]) => a.localeCompare(b)).map(([month, monthOrders]) => {
                const totalOrders = monthOrders.filter(o => isCountableOrderStatus(o.status)).length;
                const completedOrders = monthOrders.filter(o => o.status === 'completed');
                const netRevenue = completedOrders.reduce((sum, o) => {
                    const customer = custMap.get(o.customerid);
                    const shippingFee = customer ? safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0 : 0;
                    return sum + safeCoerceNumber(o.total) - shippingFee;
                }, 0);
                const grossProfit = completedOrders.reduce((sum, o) => {
                    const customer = custMap.get(o.customerid);
                    const shippingFee = customer ? safeCoerceNumber(shippingMap.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0 : 0;
                    const cost = (o.cost !== undefined && o.cost > 0) ? o.cost : o.items.reduce((itemSum, item) => {
                        const productInfo = prodMap.get(item.pid);
                        if (!productInfo) return itemSum;

                        // Calculate product cost dynamically using oil and bottle costs
                        const oilInfo = oilMap.get(productInfo.oil);
                        let bottleId;
                        if (productInfo.product?.toLowerCase().includes('tester')) bottleId = 'tester';
                        else bottleId = `${productInfo.size}${productInfo.gender?.toLowerCase().replace(/ /g, '')}`;
                        const bottleInfo = bottleMap.get(bottleId);
                        if (!oilInfo || !bottleInfo) return itemSum;

                        const oilCost = safeCoerceNumber(oilInfo.price) * safeCoerceNumber(productInfo.qtyoilinbottle);
                        const bottleCost = safeCoerceNumber(bottleInfo.totalcostbottle);
                        const lineCost = (oilCost + bottleCost) - (item.used ? bottleCost : 0);
                        return itemSum + lineCost * safeCoerceNumber(item.qty);
                    }, 0);
                    return sum + (safeCoerceNumber(o.total) - shippingFee - cost);
                }, 0);
                return { month, totalOrders, netRevenue, grossProfit };
            }).filter(d => d.totalOrders > 0 || d.netRevenue > 0 || d.grossProfit > 0);
        }
    }, [period, startDate, endDate, orders, filteredOrders, isCountableOrderStatus, custMap, shippingMap, prodMap, oilMap, bottleMap]);


    const KPI_Card = ({ title, value, isCurrency = true, color, className = '', trend, trendUp, icon, subtext }) => (
        <div className={`bg-white p-4 rounded-[1.25rem] shadow-sm border border-gray-100 flex flex-col justify-between relative ${className}`}>
            <div className="flex items-center gap-3 mb-4">
                {icon && (
                    <div className={`w-9 h-9 flex-shrink-0 rounded-xl bg-${primaryColor}-50 flex items-center justify-center text-${primaryColor}-600 shadow-sm border border-${primaryColor}-100`}>
                        {icon}
                    </div>
                )}
                {/* Title remains text-lg and themed color as requested */}
                <h3 className="font-bold text-lg" style={{ color: themeColors.text }}>{title}</h3>
            </div>

            {/* Trend moved to bottom-right corner and size scaled down on mobile */}
            {trend && (
                <div className={`absolute bottom-3 md:bottom-4 ${isRTL ? 'left-3 md:left-4' : 'right-3 md:right-4'} flex items-center gap-1 text-[10px] md:text-xs font-extrabold px-1.5 md:px-2.5 py-0.5 md:py-1 rounded-md md:rounded-lg ${trendUp ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'} shadow-sm`}>
                    <Icon path={ICONS_MAP[trendUp ? 'trendUp' : 'trendDown']} className="w-3 h-3 md:w-3.5 md:h-3.5" />
                    <span>{trend}</span>
                </div>
            )}

            <div>
                <div className={`flex items-end gap-2 ${isRTL ? 'pr-2 pl-14' : 'pl-2 pr-14'}`}>
                    <p className="text-2xl font-extrabold text-gray-900 break-words leading-none" style={{ color: color || '#111827' }}>
                        {isCurrency ? formatCurrency(value, data.generalSettings.currency) : value}
                    </p>
                </div>
                {subtext && <div className={`mt-1 ${isRTL ? 'pr-2 pl-14' : 'pl-2 pr-14'}`}>{subtext}</div>}
            </div>
        </div>
    );

    const periodLabels = {
        'This Month': t('thisMonth'),
        'This Year': t('thisYear'),
        'All Time': t('allTime'),
        'Custom Range': t('customRange')
    };

    return (
        <div>

            <div className="flex items-center justify-between gap-3 mb-4">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">{t('dashboard')}</h1>
                <div className="relative">
                    <button
                        onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 bg-white shadow-sm hover:bg-gray-50 text-sm font-semibold text-gray-700 transition-colors"
                    >
                        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        {periodLabels[period]}
                        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {showPeriodDropdown && (
                        <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 z-50 p-3`}>
                            <div className="grid grid-cols-2 gap-2 mb-3">
                                {['This Month', 'This Year', 'All Time', 'Custom Range'].map(p => (
                                    <button
                                        key={p}
                                        onClick={() => {
                                            setPeriod(p);
                                            if (p === 'This Month') {
                                                const now = new Date();
                                                setStartDate(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
                                                setEndDate(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]);
                                            } else if (p === 'This Year') {
                                                const now = new Date();
                                                setStartDate(new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0]);
                                                setEndDate(new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0]);
                                            }
                                            if (p !== 'Custom Range') setShowPeriodDropdown(false);
                                        }}
                                        className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${period === p ? 'text-white shadow-md' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-100'}`}
                                        style={period === p ? { backgroundColor: themeColors.bg } : {}}
                                    >
                                        {periodLabels[p]}
                                    </button>
                                ))}
                            </div>
                            {(period === 'Custom Range' || period !== 'All Time') && (
                                <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
                                    <div className="flex gap-2 items-center">
                                        <label className="text-xs font-medium text-gray-500 w-10">{t('from')}:</label>
                                        <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPeriod('Custom Range'); }} className="flex-1 p-1.5 border rounded-lg text-xs" />
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        <label className="text-xs font-medium text-gray-500 w-10">{t('to')}:</label>
                                        <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPeriod('Custom Range'); }} className="flex-1 p-1.5 border rounded-lg text-xs" />
                                    </div>
                                    <button onClick={() => setShowPeriodDropdown(false)} className="mt-1 w-full py-1.5 rounded-lg text-xs font-bold text-white transition-colors" style={{ backgroundColor: themeColors.bg }}>{t('apply')}</button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <KPI_Card
                    title={t('successfulOrders')}
                    value={kpis.successfulOrdersCount}
                    isCurrency={false}
                    trend={period !== 'All Time' ? calcTrend(kpis.successfulOrdersCount, prevKpis.successfulOrdersCount).text : null}
                    trendUp={period !== 'All Time' ? calcTrend(kpis.successfulOrdersCount, prevKpis.successfulOrdersCount).up : true}
                    icon={<Icon path={ICONS_MAP['check']} className="w-5 h-5" />}
                />

                <KPI_Card
                    title={t('activeOrders')}
                    value={kpis.ordersCount}
                    isCurrency={false}
                    trend={period !== 'All Time' ? calcTrend(kpis.ordersCount, prevKpis.ordersCount).text : null}
                    trendUp={period !== 'All Time' ? calcTrend(kpis.ordersCount, prevKpis.ordersCount).up : true}
                    icon={<Icon path={ICONS_MAP['dashboard']} className="w-5 h-5" />}
                />

                {/* Total Revenue - full width on mobile */}
                <div className="col-span-2 md:col-span-1">
                    <KPI_Card
                        title={t('totalRevenue')}
                        value={kpis.revenue}
                        isCurrency={true}
                        trend={period !== 'All Time' ? calcTrend(kpis.revenue, prevKpis.revenue).text : null}
                        trendUp={period !== 'All Time' ? calcTrend(kpis.revenue, prevKpis.revenue).up : true}
                        icon={<Icon path={ICONS_MAP['cash']} className="w-5 h-5" />}
                        subtext={
                            kpis.shippedRevenue > 0 ? (
                                <span className="inline-block text-[11px] font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md border border-blue-100">
                                    {t('shipped')} {t('revenue')}: {formatCurrency(kpis.shippedRevenue, data.generalSettings.currency)}
                                </span>
                            ) : null
                        }
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {/* Cost of Goods */}
                <div className="bg-white p-5 rounded-[1.5rem] shadow-sm border border-gray-100 flex flex-col">
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-3" style={{ color: themeColors.text }}>
                        <div className={`w-9 h-9 flex-shrink-0 rounded-xl bg-${primaryColor}-50 flex items-center justify-center text-${primaryColor}-600 shadow-sm border border-${primaryColor}-100`}>
                            <Icon path={ICONS_MAP['delivered']} className="w-5 h-5" />
                        </div>
                        {t('costOfGoods')}
                    </h3>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center p-3.5 rounded-2xl bg-emerald-50/40 border border-emerald-100 shadow-sm">
                            <div>
                                <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">{t('soldCompleted')}</p>
                                <p className="text-2xl font-extrabold text-emerald-900 leading-none">{formatCurrency(kpis.costOfSoldGoods, data.generalSettings.currency)}</p>
                            </div>
                        </div>
                        <div className="flex justify-between items-center p-3.5 rounded-2xl bg-[#FFFEF5] border border-amber-100 shadow-sm">
                            <div>
                                <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-1">{t('shipped')}</p>
                                <p className="text-2xl font-extrabold text-amber-900 leading-none">{formatCurrency(kpis.costOfShippedGoods, data.generalSettings.currency)}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Ads Spend */}
                <div className="bg-white p-5 rounded-[1.5rem] shadow-sm border border-gray-100 flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-lg flex items-center gap-3" style={{ color: themeColors.text }}>
                            <div className={`w-9 h-9 flex-shrink-0 rounded-xl bg-${primaryColor}-50 flex items-center justify-center text-${primaryColor}-600 shadow-sm border border-${primaryColor}-100`}>
                                <Icon path={ICONS_MAP['dashboard']} className="w-5 h-5" />
                            </div>
                            {t('adsSpend')}
                        </h3>
                        <Button onClick={() => setAdsModalOpen(true)} color={primaryColor} size="sm" className="rounded-full !px-4 !py-1 text-xs">{t('addCost')}</Button>
                    </div>
                    <div className="flex items-center gap-3 mb-4 pl-1">
                        <p className={`text-2xl font-extrabold`} style={{ color: themeColors.text }}>{formatCurrency(adsSum, data.generalSettings.currency)}</p>
                    </div>
                    <div className="mt-2 text-left flex-1 border-t pt-2 border-gray-50">
                        <h4 className="text-xs uppercase tracking-wider font-bold text-gray-400 mb-2">{t('history')}</h4>
                        <div className="max-h-32 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                            {Object.entries(data.adsLedger).filter(([m]) => {
                                if (period === 'This Month') return m === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
                                if (period === 'This Year') return m.startsWith(`${new Date().getFullYear()}`);
                                if (period === 'Custom Range') {
                                    const monthDate = new Date(m + '-01');
                                    return monthDate >= new Date(startDate) && monthDate <= new Date(endDate);
                                }
                                return true;
                            }).flatMap(([month, amounts]) => amounts.map((amount, idx) => ({ month, amount, idx }))).map(({ month, amount, idx }) => (
                                <div key={`${month}-${idx}`} className="flex justify-between items-center text-sm p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                    <span className="font-bold text-gray-700">{monthLabel(month)} <span className="text-gray-400 font-normal ml-2">{formatCurrency(amount, data.generalSettings.currency)}</span></span>
                                    <button onClick={async () => {
                                        let updatedAdsLedger;
                                        let monthDeleted = false;
                                        setData(prev => {
                                            const newLedger = { ...prev.adsLedger };
                                            newLedger[month] = newLedger[month].filter((_, i) => i !== idx);
                                            if (newLedger[month].length === 0) {
                                                delete newLedger[month];
                                                monthDeleted = true;
                                            }
                                            updatedAdsLedger = newLedger;
                                            return { ...prev, adsLedger: newLedger };
                                        });

                                        try {
                                            if (monthDeleted) {
                                                const { error } = await supabaseTracked.from('ads_ledger').delete().eq('month', month);
                                                if (error) throw error;
                                            } else {
                                                await syncObjectData('ads_ledger', updatedAdsLedger, 'month', 'amount');
                                            }
                                            showAlert('Removed ads entry.');
                                        } catch (err) {
                                            showAlert(`Failed to sync delete to database: ${err.message}`);
                                        }
                                    }} className="text-red-400 hover:text-red-600">
                                        <Icon path={ICONS_MAP['trash']} className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Profit Margin Card */}
                <div className="bg-white p-5 rounded-[1.5rem] shadow-sm border border-gray-100 flex flex-col">
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-3" style={{ color: themeColors.text }}>
                        <div className={`w-9 h-9 flex-shrink-0 rounded-xl bg-${primaryColor}-50 flex items-center justify-center text-${primaryColor}-600 shadow-sm border border-${primaryColor}-100`}>
                            <Icon path={ICONS_MAP['cash']} className="w-5 h-5" />
                        </div>
                        {t('profitMargin')}
                    </h3>
                    <div className="flex flex-col gap-4">
                        <div className="flex-1 rounded-2xl p-4 flex flex-col justify-center shadow-sm border border-gray-200" style={{ background: '#F8F8F8' }}>
                            <div className="flex justify-between items-center mb-1">
                                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">{t('grossProfit')}</h3>
                                <span className="text-sm font-extrabold bg-gray-160 text-gray-800 px-2 py-1 rounded-md shadow-sm">{kpis.revenue > 0 ? ((kpis.grossProfit / kpis.revenue) * 100).toFixed(0) : 0}%</span>
                            </div>
                            <p className="text-2xl font-extrabold text-gray-900">{formatCurrency(kpis.grossProfit, data.generalSettings.currency)}</p>
                        </div>
                        <div className="flex-1 rounded-2xl p-4 flex flex-col justify-center shadow-sm" style={{ background: kpis.profitAfterAds < 0 ? '#FFF5F5' : '#F0F9FF', border: kpis.profitAfterAds < 0 ? '1px solid #FECACA' : '1px solid #BFDBFE' }}>
                            <div className="flex justify-between items-center mb-1">
                                <p className={`text-xs font-bold uppercase tracking-wider ${kpis.profitAfterAds < 0 ? 'text-red-800' : 'text-blue-800'}`}>{t('profitAfterAds')}</p>
                                <span className={`text-sm font-extrabold px-2 py-1 rounded-md shadow-sm ${kpis.profitAfterAds < 0 ? 'bg-red-160 text-red-900' : 'bg-blue-160 text-blue-900'}`}>{kpis.revenue > 0 ? ((kpis.profitAfterAds / kpis.revenue) * 100).toFixed(0) : 0}%</span>
                            </div>
                            <p className={`text-2xl font-extrabold ${kpis.profitAfterAds < 0 ? 'text-red-900' : 'text-blue-900'}`}>{formatCurrency(kpis.profitAfterAds, data.generalSettings.currency)}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* First Row: Low Stock Alerts, Net Revenue, Total Orders */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {data.generalSettings?.showLowStock !== false && (<div className={`bg-white p-5 rounded-[1.5rem] shadow-sm border border-${primaryColor}-100 lg:col-span-1`}>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-lg flex items-center gap-3" style={{ color: themeColors.text }}>
                            <div className={`w-9 h-9 flex-shrink-0 rounded-xl bg-${primaryColor}-50 flex items-center justify-center text-${primaryColor}-600 shadow-sm border border-${primaryColor}-100`}>
                                <Icon path={ICONS_MAP['warning']} className="w-5 h-5" />
                            </div>
                            {t('lowStockAlerts')}
                        </h3>
                        <span onClick={() => setShowLowStockAlert(true)} className={`text-sm font-semibold text-${primaryColor}-600 cursor-pointer hover:underline`}>{t('viewAll') || 'View All'}</span>
                    </div>

                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                        {(lowStockOils.length === 0 && lowStockBottles.length === 0) && <p className="text-gray-500 text-center py-4">{t('noLowStockAlerts')}</p>}

                        {lowStockOils.length > 0 && (
                            <div>
                                <h4 className="text-xs font-bold text-red-700 uppercase tracking-wider mb-3 px-3 py-1.5 rounded-lg bg-red-50">{t('oilsThreshold', { threshold: data.stockSettings.lowStockThresholds.oils })}</h4>
                                <div className="space-y-3">
                                    {lowStockOils.map(o => {
                                        const threshold = data.stockSettings.lowStockThresholds.oils;
                                        const current = safeCoerceNumber(o.actualstock);
                                        const percent = Math.min(100, Math.max(0, (current / threshold) * 80));
                                        const isCritical = current <= (threshold * 0.5);

                                        return (
                                            <div key={o.oil} className="rounded-xl p-4 shadow-md" style={{ backgroundColor: '#fcf9f9', boxShadow: '0 4px 12px rgba(0,0,0,0.10)' }}>
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-bold text-gray-900 text-sm">{o.oil}</span>
                                                    <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${isCritical ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                                                        {isCritical ? t('critical') : t('low')}
                                                    </span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mb-2 overflow-hidden">
                                                    <div className={`h-2 rounded-full ${isCritical ? 'bg-red-500' : 'bg-amber-500'}`} style={{ width: `${percent}%` }}></div>
                                                </div>
                                                <div className="flex justify-between text-xs text-gray-600 font-medium">
                                                    <span className={isCritical ? 'text-red-600 font-bold' : ''}>{current} {t('ml')} {t('remaining')}</span>
                                                    <span>{t('threshold')}: {threshold} {t('ml')}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {lowStockBottles.length > 0 && (
                            <div>
                                <h4 className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-3 px-3 py-1.5 rounded-lg bg-amber-50">{t('bottlesThreshold', { threshold: data.stockSettings.lowStockThresholds.bottles })}</h4>
                                <div className="space-y-3">
                                    {lowStockBottles.map(b => {
                                        const threshold = data.stockSettings.lowStockThresholds.bottles;
                                        const current = safeCoerceNumber(b.actualstock);
                                        const percent = Math.min(100, Math.max(0, (current / threshold) * 80));
                                        const isCritical = current <= (threshold * 0.5);

                                        return (
                                            <div key={b.id} className="rounded-xl p-4 shadow-md" style={{ backgroundColor: '#fcf9f9', boxShadow: '0 4px 12px rgba(0,0,0,0.10)' }}>
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-bold text-gray-900 text-sm">{b.size} {b.gender}</span>
                                                    <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${isCritical ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                                                        {isCritical ? t('critical') : t('low')}
                                                    </span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mb-2 overflow-hidden">
                                                    <div className={`h-2 rounded-full ${isCritical ? 'bg-red-500' : 'bg-amber-500'}`} style={{ width: `${percent}%` }}></div>
                                                </div>
                                                <div className="flex justify-between text-xs text-gray-600 font-medium">
                                                    <span className={isCritical ? 'text-red-600 font-bold' : ''}>{current} {t('units')} {t('remaining')}</span>
                                                    <span>{t('threshold')}: {threshold}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>)}
                {data.generalSettings?.showRevenue !== false && (<Card className="!p-0 overflow-hidden">
                    <div className="px-4 pt-4 pb-2 relative">
                        <div className="flex items-center">
                            <h3 className={`font-bold text-lg flex items-center gap-3 ${isRTL ? 'pl-16' : 'pr-16'}`} style={{ color: themeColors.text }}>
                                <div className={`w-9 h-9 flex-shrink-0 rounded-xl bg-${primaryColor}-50 flex items-center justify-center text-${primaryColor}-600 shadow-sm border border-${primaryColor}-100`}>
                                    <Icon path={ICONS_MAP['cash']} className="w-5 h-5" />
                                </div>
                                {t('netRevenue')}
                            </h3>
                            {(() => {
                                const revenueFiltered = chartData.filter(d => d.netRevenue > 0);
                                if (revenueFiltered.length < 2) return null;
                                const current = revenueFiltered[revenueFiltered.length - 1].netRevenue;
                                const previous = revenueFiltered[revenueFiltered.length - 2].netRevenue;
                                if (previous === 0) return null;
                                const pct = ((current - previous) / Math.abs(previous)) * 100;
                                const trendUp = pct >= 0;
                                return (
                                    <div className={`absolute bottom-2 ${isRTL ? 'left-4' : 'right-4'} flex items-center gap-1.5 text-xs font-extrabold px-2.5 py-1 rounded-lg ${trendUp ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'} shadow-sm`}>
                                        <Icon path={ICONS_MAP[trendUp ? 'trendUp' : 'trendDown']} className="w-3.5 h-3.5" />
                                        <span>{Math.abs(pct).toFixed(1)}%</span>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                    <div dir="ltr">
                        <NetRevenueSparkline
                            chartData={chartData.filter(d => d.netRevenue > 0).slice(-8)}
                            currency={data.generalSettings.currency}
                            name={t('netRevenue')}
                        />

                    </div>
                </Card>)}
                {data.generalSettings?.showOrders !== false && (<Card className="!p-0 overflow-hidden">
                    <div className="px-4 pt-4 pb-2 relative">
                        <div className="flex items-center">
                            <h3 className={`font-bold text-lg flex items-center gap-3 ${isRTL ? 'pl-16' : 'pr-16'}`} style={{ color: themeColors.text }}>
                                <div className={`w-9 h-9 flex-shrink-0 rounded-xl bg-${primaryColor}-50 flex items-center justify-center text-${primaryColor}-600 shadow-sm border border-${primaryColor}-100`}>
                                    <Icon path={ICONS_MAP['ordered']} className="w-5 h-5" />
                                </div>
                                {t('totalOrders')}
                            </h3>
                            {(() => {
                                const ordersFiltered = chartData.filter(d => d.totalOrders > 0);
                                if (ordersFiltered.length < 2) return null;
                                const current = ordersFiltered[ordersFiltered.length - 1].totalOrders;
                                const previous = ordersFiltered[ordersFiltered.length - 2].totalOrders;
                                if (previous === 0) return null;
                                const pct = ((current - previous) / Math.abs(previous)) * 100;
                                const trendUp = pct >= 0;
                                return (
                                    <div className={`absolute bottom-2 ${isRTL ? 'left-4' : 'right-4'} flex items-center gap-1.5 text-xs font-extrabold px-2.5 py-1 rounded-lg ${trendUp ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'} shadow-sm`}>
                                        <Icon path={ICONS_MAP[trendUp ? 'trendUp' : 'trendDown']} className="w-3.5 h-3.5" />
                                        <span>{Math.abs(pct).toFixed(1)}%</span>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                    <div dir="ltr" className="px-2">
                        <ResponsiveContainer width="100%" height={380}>
                            <AreaChart data={chartData.filter(d => d.totalOrders > 0).slice(-8)} margin={{ bottom: 75, top: 10, right: 20, left: 60 }}>
                                <defs>
                                    <linearGradient id="ordersGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <XAxis
                                    dataKey="month"
                                    tick={<CustomXAxisTick
                                        fontSize={11}
                                        angle={-45}
                                        formatter={monthLabel}
                                    />}
                                    tickMargin={10}
                                    interval={0}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]}
                                    hide={true}
                                />
                                <Tooltip />
                                <Area
                                    type="monotone"
                                    dataKey="totalOrders"
                                    stroke="#3b82f6"
                                    strokeWidth={3}
                                    fill="url(#ordersGradient)"
                                    name={t('totalOrders')}
                                    dot={{ fill: '#3b82f6', r: 4, strokeWidth: 0 }}
                                    activeDot={{ r: 6, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </Card>)}
            </div>

            {/* Second Row: Top Selling Products, Top Selling Bottles, Top Customers */}
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {data.generalSettings?.showTopProducts !== false && (<Card className="lg:col-span-1 !p-0 overflow-hidden">
                    <div className="px-4 pt-4 pb-2">
                        <h3 className="font-bold text-lg mb-4 flex items-center gap-3" style={{ color: themeColors.text }}>
                            <div className={`w-9 h-9 flex-shrink-0 rounded-xl bg-${primaryColor}-50 flex items-center justify-center text-${primaryColor}-600 shadow-sm border border-${primaryColor}-100`}>
                                <Icon path={ICONS_MAP['stock']} className="w-5 h-5" />
                            </div>
                            {t('topSellingProducts')}
                        </h3>
                        <div className="flex gap-2 flex-wrap">
                            {availableCategories.map(category => (
                                <button
                                    key={category}
                                    onClick={() => setTopProductsCategory(category)}
                                    className="px-4 py-2 rounded-md font-medium transition-all"
                                    style={{
                                        backgroundColor: topProductsCategory === category ? themeColors.bg : '#F3F4F6',
                                        color: topProductsCategory === category ? '#FFFFFF' : '#374151',
                                        boxShadow: topProductsCategory === category ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (topProductsCategory !== category) {
                                            e.currentTarget.style.backgroundColor = '#E5E7EB';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (topProductsCategory !== category) {
                                            e.currentTarget.style.backgroundColor = '#F3F4F6';
                                        }
                                    }}
                                >
                                    {t(category.toLowerCase().replace(/ /g, '')) || t(category) || category}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div dir="ltr" className="px-2">
                        <TopProductsGroupedChart data={topSellingProductsGrouped} availableSizes={availableBottleSizes} height={470} />
                    </div>
                </Card>)}
                {data.generalSettings?.showTopBottles !== false && (<Card className="!p-0 overflow-hidden">
                    <div className="px-4 pt-4 pb-2">
                        <h3 className="font-bold text-lg mb-4 flex items-center gap-3" style={{ color: themeColors.text }}>
                            <div className={`w-9 h-9 flex-shrink-0 rounded-xl bg-${primaryColor}-50 flex items-center justify-center text-${primaryColor}-600 shadow-sm border border-${primaryColor}-100`}>
                                <Icon path={ICONS_MAP['stock']} className="w-5 h-5" />
                            </div>
                            {t('topSellingBottles')}
                        </h3>
                        {/* Multi-select gender filter tabs */}
                        {(() => {
                            const allGenders = [...new Set(topSellingSizes.map(s => s.gender).filter(Boolean))];
                            return (
                                <div className="flex gap-2 flex-wrap">
                                    <button
                                        onClick={() => setBottleGenderFilter(null)}
                                        className="px-3 py-1.5 rounded-md text-xs font-semibold transition-all"
                                        style={{
                                            backgroundColor: bottleGenderFilter === null ? themeColors.bg : '#F3F4F6',
                                            color: bottleGenderFilter === null ? '#FFFFFF' : '#374151',
                                        }}
                                    >
                                        {t('all')}
                                    </button>
                                    {allGenders.map(gender => {
                                        const isActive = Array.isArray(bottleGenderFilter) && bottleGenderFilter.includes(gender);
                                        return (
                                            <button
                                                key={gender}
                                                onClick={() => {
                                                    setBottleGenderFilter(prev => {
                                                        if (prev === null) return [gender];
                                                        const arr = Array.isArray(prev) ? prev : [];
                                                        if (arr.includes(gender)) {
                                                            const next = arr.filter(g => g !== gender);
                                                            return next.length === 0 ? null : next;
                                                        }
                                                        return [...arr, gender];
                                                    });
                                                }}
                                                className="px-3 py-1.5 rounded-md text-xs font-semibold transition-all"
                                                style={{
                                                    backgroundColor: isActive ? themeColors.bg : '#F3F4F6',
                                                    color: isActive ? '#FFFFFF' : '#374151',
                                                }}
                                            >
                                                {t(gender.toLowerCase().replace(/ /g, '')) || t(gender) || gender}
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                    </div>
                    <div dir="ltr" className="px-2">
                        <BottleSizesHorizontalChart
                            data={bottleGenderFilter === null
                                ? topSellingSizes
                                : topSellingSizes.filter(s => bottleGenderFilter.includes(s.gender))
                            }
                        />
                    </div>
                </Card>)}
                {data.generalSettings?.showTopCustomers !== false && (<Card className="lg:col-span-1 !p-0 overflow-hidden">
                    <div className="px-4 pt-4 pb-2">
                        <h3 className="font-bold text-lg mb-4 flex items-center gap-3" style={{ color: themeColors.text }}>
                            <div className={`w-9 h-9 flex-shrink-0 rounded-xl bg-${primaryColor}-50 flex items-center justify-center text-${primaryColor}-600 shadow-sm border border-${primaryColor}-100`}>
                                <Icon path={ICONS_MAP['customers']} className="w-5 h-5" />
                            </div>
                            {t('topCustomers')}
                        </h3>
                    </div>
                    <div dir="ltr" className="px-3 pb-4">
                        <TopCustomersFunnel
                            data={topCustomers.map(c => ({ name: c.name, orders: c.completedOrdersCount, customer: c }))}
                            onCustomerClick={onCustomerClick}
                        />
                    </div>
                </Card>)}
            </div>

            {adsModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-96">
                        <h2 className="text-xl font-bold mb-4">{t('addAdsCost')}</h2>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">{t('month')}</label>
                            <select value={adsMonth} onChange={e => setAdsMonth(e.target.value)} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                                {getRecentMonths(24).reverse().map(month => <option key={month} value={month}>{monthLabel(month)}</option>)}
                            </select>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">{t('amount')} ({getCurrencySymbol(data.generalSettings.currency)})</label>
                            <input type="number" value={adsAmount} onChange={e => setAdsAmount(e.target.value)} min="0" step="0.01" className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button onClick={() => setAdsModalOpen(false)} color="gray">{t('cancel')}</Button>
                            <Button onClick={async () => {
                                if (parseFloat(adsAmount) > 0) {
                                    let updatedAdsLedger;
                                    setData(prev => {
                                        updatedAdsLedger = {
                                            ...prev.adsLedger,
                                            [adsMonth]: [...(prev.adsLedger[adsMonth] || []), parseFloat(adsAmount)]
                                        };
                                        return {
                                            ...prev,
                                            adsLedger: updatedAdsLedger
                                        };
                                    });
                                    setAdsModalOpen(false);
                                    setAdsAmount('');

                                    // Sync to Supabase
                                    console.log('🔄 Syncing ads ledger to Supabase...');
                                    await syncObjectData('ads_ledger', updatedAdsLedger, 'month', 'amount');

                                    showAlert(t('adsCostAdded'));
                                }
                            }} color="green">{t('save')}</Button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}


// --- Main App Component ---

// Load data FROM Supabase
const loadFromSupabase = async () => {
    try {
        console.log('📥 Loading data from Supabase...');

        const [
            customersRes,
            productsRes,
            ordersRes,
            oilsRes,
            bottlesRes,
            shippingRes,
            returnedPoolRes,
            adsLedgerRes,
            settingsRes,
            vendorsRes
        ] = await Promise.all([
            supabase.from('customers').select('*'),
            supabase.from('products').select('*'),
            supabase.from('orders').select('*'),
            supabase.from('oils').select('*'),
            supabase.from('bottles').select('*'),
            supabase.from('shipping').select('*'),
            supabase.from('returned_pool').select('*'),
            supabase.from('ads_ledger').select('*'),
            supabase.from('app_settings').select('*'),
            supabase.from('vendors').select('*')
        ]);

        const newData = { ...initialData };

        // Load array data
        if (customersRes.data && customersRes.data.length > 0) {
            console.log(`✅ Loaded ${customersRes.data.length} customers`);
            newData.customers = customersRes.data;
        }
        if (productsRes.data && productsRes.data.length > 0) {
            console.log(`✅ Loaded ${productsRes.data.length} products`);
            newData.products = productsRes.data;
        }
        if (ordersRes.data && ordersRes.data.length > 0) {
            console.log(`✅ Loaded ${ordersRes.data.length} orders`);
            newData.orders = ordersRes.data;
        }
        if (oilsRes.data && oilsRes.data.length > 0) {
            console.log(`✅ Loaded ${oilsRes.data.length} oils`);
            newData.oils = oilsRes.data;
        }
        if (bottlesRes.data && bottlesRes.data.length > 0) {
            console.log(`✅ Loaded ${bottlesRes.data.length} bottles`);
            newData.bottles = bottlesRes.data;
        }
        if (shippingRes.data && shippingRes.data.length > 0) {
            console.log(`✅ Loaded ${shippingRes.data.length} shipping entries`);
            newData.shipping = shippingRes.data;
        }
        if (vendorsRes.data && vendorsRes.data.length > 0) {
            console.log(`✅ Loaded ${vendorsRes.data.length} vendors`);
            newData.vendors = vendorsRes.data;
        }

        // Convert returned_pool array to object
        if (returnedPoolRes.data && returnedPoolRes.data.length > 0) {
            console.log(`✅ Loaded ${returnedPoolRes.data.length} returned pool items`);
            newData.returnedPool = {};
            returnedPoolRes.data.forEach(item => {
                newData.returnedPool[item.product_name] = item.quantity;
            });
        }

        // Convert ads_ledger array to object (with arrays for compatibility)
        if (adsLedgerRes.data && adsLedgerRes.data.length > 0) {
            console.log(`✅ Loaded ${adsLedgerRes.data.length} ads ledger entries`);
            newData.adsLedger = {};
            adsLedgerRes.data.forEach(item => {
                // Store as array for compatibility with app's data structure
                // The app expects: { "2024-01": [100, 200], "2024-02": [150] }
                // But Supabase stores total: { month: "2024-01", amount: 300 }
                // So we convert back to array with single value
                newData.adsLedger[item.month] = [item.amount];
            });
        }

        // Load settings
        if (settingsRes.data && settingsRes.data.length > 0) {
            console.log(`✅ Loaded ${settingsRes.data.length} settings`);
            settingsRes.data.forEach(setting => {
                switch (setting.setting_key) {
                    case 'general_settings':
                        newData.generalSettings = setting.setting_value;
                        // Save store name to localStorage for login page
                        if (setting.setting_value?.storeName) {
                            localStorage.setItem('storeName', setting.setting_value.storeName);
                            console.log('💾 Saved store name to localStorage:', setting.setting_value.storeName);
                        }
                        break;
                    case 'ui_settings':
                        if (!newData.settings) newData.settings = { ...initialData.settings };
                        newData.settings.logoUrl = setting.setting_value.logoUrl;
                        newData.settings.primaryColor = setting.setting_value.primaryColor;
                        // Save logo URL and primary color to localStorage for login page
                        if (setting.setting_value?.logoUrl) {
                            localStorage.setItem('logoUrl', setting.setting_value.logoUrl);
                            console.log('💾 Saved logo URL to localStorage:', setting.setting_value.logoUrl);
                        }
                        if (setting.setting_value?.primaryColor) {
                            localStorage.setItem('primaryColor', setting.setting_value.primaryColor);
                            console.log('💾 Saved primary color to localStorage:', setting.setting_value.primaryColor);
                        }
                        break;
                    case 'status_config':
                        if (!newData.settings) newData.settings = { ...initialData.settings };
                        newData.settings.statusIcons = setting.setting_value.statusIcons;
                        newData.settings.statusColors = setting.setting_value.statusColors;
                        break;
                    case 'stock_settings':
                        newData.stockSettings = setting.setting_value;
                        break;
                }
            });
        }

        console.log('✅ All data loaded from Supabase successfully');
        return newData;
    } catch (err) {
        console.error('❌ Error loading from Supabase:', err);
        return null;
    }
};

// Define valid columns for each table (based on Supabase schema)
const VALID_COLUMNS = {
    customers: ['id', 'name', 'mobile', 'email', 'state', 'city', 'orders', 'cancels', 'completed', 'status'],
    products: ['id', 'product', 'gender', 'size', 'price', 'qtyoilinbottle', 'oil', 'qtysold', 'costproduct', 'imageurl'],
    orders: ['id', 'orderid', 'customerid', 'customer', 'items', 'total', 'payment', 'discount', 'status', 'date', 'notes', 'attachment', 'cost', 'woo_order_id', 'wooOrderData'],
    oils: ['id', 'oil', 'price', 'actualstock', 'totalordered', 'oiltype', 'vendor_id', 'oil_per_5ml', 'oil_per_20ml', 'oil_per_30ml', 'oil_per_50ml', 'oil_per_100ml'],
    bottles: ['id', 'size', 'gender', 'actualstock', 'totalcostbottle', 'totalordered', 'imageurl'],
    shipping: ['id', 'state', 'city', 'fees'],
    returned_pool: ['id', 'product_name', 'quantity'],
    ads_ledger: ['id', 'month', 'amount'],
    app_settings: ['id', 'setting_key', 'setting_value'],
    vendors: ['id', 'name', 'created_at']
};

// Define which tables should remove non-UUID IDs
const TABLES_WITH_UUID_IDS = ['customers', 'products', 'orders', 'oils', 'shipping', 'returned_pool', 'ads_ledger', 'app_settings', 'vendors'];

// Define unique conflict columns for each table
const CONFLICT_COLUMNS = {
    customers: 'id',      // Will use insert for non-UUID IDs
    products: 'product',  // Unique product name
    orders: 'orderid',    // Unique order ID
    oils: 'oil',          // Unique oil name
    bottles: 'id',        // Keep bottle IDs as-is (they're strings like 'tester', '50mlforher')
    shipping: 'id',       // Use id for conflict (will handle composite unique separately)
    returned_pool: 'product_name',
    ads_ledger: 'month',
    app_settings: 'setting_key',
    vendors: 'id'
};

// Sync data TO Supabase (with tracking)
const syncToSupabase = async (tableName, data) => {
    try {
        if (!data || data.length === 0) return;

        console.log(`🔄 [SYNC] Syncing ${data.length} items to ${tableName}...`);

        const validColumns = VALID_COLUMNS[tableName] || [];

        for (const item of data) {
            let normalizedItem = { ...item };

            // Step 1: Remove invalid columns (empty string keys, undefined)
            // IMPORTANT: Keep null values - they are valid in databases (e.g., status field)
            Object.keys(normalizedItem).forEach(key => {
                if (!key || key.trim() === '' || normalizedItem[key] === undefined) {
                    delete normalizedItem[key];
                }
            });

            // Step 2: Filter to only valid columns for this table
            if (validColumns.length > 0) {
                const originalKeys = Object.keys(normalizedItem);
                const filteredItem = {};
                validColumns.forEach(col => {
                    if (normalizedItem.hasOwnProperty(col)) {
                        filteredItem[col] = normalizedItem[col];
                    }
                });

                // Log filtered columns for debugging
                const removedKeys = originalKeys.filter(k => !validColumns.includes(k));
                if (removedKeys.length > 0) {
                    console.log(`[SYNC] Filtered out invalid columns for ${tableName}:`, removedKeys);
                }

                normalizedItem = filteredItem;
            }

            // Step 3: Handle ID validation and removal for UUID tables
            const shouldRemoveId = TABLES_WITH_UUID_IDS.includes(tableName) &&
                tableName !== 'bottles'; // Bottles use string IDs

            if (shouldRemoveId && normalizedItem.id && typeof normalizedItem.id === 'string') {
                // Check if it's NOT a valid UUID (UUIDs are 36 chars with dashes)
                const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(normalizedItem.id);
                if (!isValidUUID) {
                    console.log(`[SYNC] Removing non-UUID ID from ${tableName}: ${normalizedItem.id}`);
                    delete normalizedItem.id;
                }
            }

            // Step 4: Get the conflict column for upsert
            const onConflictOption = CONFLICT_COLUMNS[tableName] || 'id';

            // Step 5: Type conversions and data normalization
            switch (tableName) {
                case 'customers':
                    if (normalizedItem.orders !== undefined) normalizedItem.orders = parseInt(normalizedItem.orders) || 0;
                    if (normalizedItem.completed !== undefined) normalizedItem.completed = parseInt(normalizedItem.completed) || 0;
                    if (normalizedItem.cancels !== undefined) {
                        if (typeof normalizedItem.cancels === 'string') {
                            normalizedItem.cancels = normalizedItem.cancels.split(',').map(s => s.trim()).filter(Boolean);
                        } else if (!Array.isArray(normalizedItem.cancels)) {
                            normalizedItem.cancels = [];
                        }
                    }
                    break;

                case 'products':
                    if (normalizedItem.price !== undefined) normalizedItem.price = parseFloat(normalizedItem.price) || 0;
                    if (normalizedItem.qtysold !== undefined) normalizedItem.qtysold = parseInt(normalizedItem.qtysold) || 0;
                    if (normalizedItem.costproduct !== undefined) normalizedItem.costproduct = parseFloat(normalizedItem.costproduct) || 0;
                    if (normalizedItem.qtyoilinbottle !== undefined) normalizedItem.qtyoilinbottle = parseFloat(normalizedItem.qtyoilinbottle) || 0;
                    break;

                case 'orders':
                    if (normalizedItem.total !== undefined) normalizedItem.total = parseFloat(normalizedItem.total) || 0;
                    if (normalizedItem.discount !== undefined) normalizedItem.discount = parseFloat(normalizedItem.discount) || 0;
                    if (normalizedItem.items !== undefined) {
                        if (typeof normalizedItem.items === 'string') {
                            try {
                                normalizedItem.items = JSON.parse(normalizedItem.items);
                            } catch (e) {
                                normalizedItem.items = [];
                            }
                        } else if (!Array.isArray(normalizedItem.items)) {
                            normalizedItem.items = [];
                        }
                    }
                    // Handle customerid - convert to UUID if needed
                    if (normalizedItem.customerid && typeof normalizedItem.customerid === 'string') {
                        const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(normalizedItem.customerid);
                        if (!isValidUUID) {
                            console.log(`[SYNC] Warning: Order has non-UUID customerid: ${normalizedItem.customerid}`);
                            // Set to null if not a valid UUID - will need to be fixed later
                            normalizedItem.customerid = null;
                        }
                    }
                    // Add customer name field if not present (for easier querying/exporting)
                    if (!normalizedItem.customer && normalizedItem.customerName) {
                        normalizedItem.customer = normalizedItem.customerName;
                    }
                    // CRITICAL: Ensure status field is always included and valid
                    if (!normalizedItem.status || normalizedItem.status.trim() === '') {
                        normalizedItem.status = 'ordered'; // Default to 'ordered' if missing
                    }
                    console.log(`[SYNC] Order ${normalizedItem.orderid} status: ${normalizedItem.status}, customer: ${normalizedItem.customer || 'N/A'}`);
                    break;

                case 'oils':
                    if (normalizedItem.price !== undefined) normalizedItem.price = parseFloat(normalizedItem.price) || 0;
                    if (normalizedItem.actualstock !== undefined) normalizedItem.actualstock = parseInt(normalizedItem.actualstock) || 0;
                    if (normalizedItem.totalordered !== undefined) normalizedItem.totalordered = parseInt(normalizedItem.totalordered) || 0;
                    if (normalizedItem.oil_per_5ml !== undefined) normalizedItem.oil_per_5ml = parseInt(normalizedItem.oil_per_5ml) || 0;
                    if (normalizedItem.oil_per_20ml !== undefined) normalizedItem.oil_per_20ml = parseInt(normalizedItem.oil_per_20ml) || 0;
                    if (normalizedItem.oil_per_30ml !== undefined) normalizedItem.oil_per_30ml = parseInt(normalizedItem.oil_per_30ml) || 0;
                    if (normalizedItem.oil_per_50ml !== undefined) normalizedItem.oil_per_50ml = parseInt(normalizedItem.oil_per_50ml) || 0;
                    if (normalizedItem.oil_per_100ml !== undefined) normalizedItem.oil_per_100ml = parseInt(normalizedItem.oil_per_100ml) || 0;
                    break;

                case 'bottles':
                    if (normalizedItem.actualstock !== undefined) normalizedItem.actualstock = parseInt(normalizedItem.actualstock) || 0;
                    if (normalizedItem.totalcostbottle !== undefined) normalizedItem.totalcostbottle = parseFloat(normalizedItem.totalcostbottle) || 0;
                    if (normalizedItem.totalordered !== undefined) normalizedItem.totalordered = parseInt(normalizedItem.totalordered) || 0;
                    break;

                case 'shipping':
                    if (normalizedItem.fees !== undefined) normalizedItem.fees = parseFloat(normalizedItem.fees) || 0;
                    break;
            }

            // Use tracked supabase client to show operations in sync panel
            let result;

            // Special handling for shipping table (has composite unique constraint on state, city)
            if (tableName === 'shipping') {
                // First, try to find existing record by state and city
                const { data: existingShipping } = await supabaseTracked
                    .from('shipping')
                    .select('id')
                    .eq('state', normalizedItem.state)
                    .eq('city', normalizedItem.city)
                    .maybeSingle();

                if (existingShipping) {
                    // Update existing record
                    console.log(`[SYNC] Updating existing shipping for ${normalizedItem.state}, ${normalizedItem.city}`);
                    normalizedItem.id = existingShipping.id; // Use existing ID
                    result = await supabaseTracked
                        .from(tableName)
                        .upsert(normalizedItem, { onConflict: 'id' })
                        .select();
                } else {
                    // Insert new record (remove id to let DB generate UUID)
                    console.log(`[SYNC] Inserting new shipping for ${normalizedItem.state}, ${normalizedItem.city}`);
                    delete normalizedItem.id; // Let database generate UUID
                    result = await supabaseTracked
                        .from(tableName)
                        .insert(normalizedItem)
                        .select();
                }
            } else {
                // Standard upsert for other tables
                console.log(`[SYNC] Upserting to ${tableName}:`, tableName === 'orders' ? { orderid: normalizedItem.orderid, status: normalizedItem.status, customer: normalizedItem.customer, onConflict: onConflictOption } : { item: normalizedItem, onConflict: onConflictOption });
                result = await supabaseTracked
                    .from(tableName)
                    .upsert(normalizedItem, { onConflict: onConflictOption })
                    .select();
            }

            const { data: upsertData, error } = result;
            if (error) {
                console.error(`❌ [SYNC] Error syncing ${tableName}:`, error);
                console.error(`❌ [SYNC] Failed item:`, normalizedItem);
                throw new Error(`Failed to sync ${tableName}: ${error.message || JSON.stringify(error)}`);
            } else {
                console.log(`✅ [SYNC] Synced item to ${tableName}:`, tableName === 'orders' && upsertData?.[0] ? { orderid: upsertData[0].orderid, status: upsertData[0].status, customer: upsertData[0].customer } : upsertData);
            }
        }

        console.log(`✅ [SYNC] Completed syncing ${data.length} items to ${tableName}`);
    } catch (err) {
        console.error(`❌ [SYNC] Sync error for ${tableName}:`, err);
        throw err; // Re-throw so caller can handle it
    }
};

const syncObjectToSupabase = async (tableName, obj, keyField, valueField = 'amount') => {
    try {
        if (!obj || Object.keys(obj).length === 0) return;

        console.log(`🔄 [SYNC] Syncing object to ${tableName}...`);

        for (const [key, value] of Object.entries(obj)) {
            // Handle arrays (for ads_ledger which stores multiple amounts per month)
            if (Array.isArray(value)) {
                // Sum all amounts for this key
                const totalAmount = value.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
                const item = {
                    [keyField]: key,
                    [valueField]: totalAmount
                };

                console.log(`[SYNC] Upserting to ${tableName}:`, item, `onConflict: ${keyField}`);
                const { error } = await supabaseTracked
                    .from(tableName)
                    .upsert(item, { onConflict: keyField });
                if (error) {
                    console.error(`❌ [SYNC] Error syncing ${tableName}:`, error);
                    console.error(`❌ [SYNC] Failed item:`, item);
                    throw new Error(`Failed to sync ${tableName}: ${error.message || JSON.stringify(error)}`);
                } else {
                    console.log(`✅ [SYNC] Synced ${key} to ${tableName} (total: ${totalAmount})`);
                }
            } else {
                // Handle single values (for returned_pool)
                const item = {
                    [keyField]: key,
                    [valueField]: valueField === 'quantity' ? parseInt(value) || 0 : parseFloat(value) || 0
                };

                console.log(`[SYNC] Upserting to ${tableName}:`, item, `onConflict: ${keyField}`);
                const { error } = await supabaseTracked
                    .from(tableName)
                    .upsert(item, { onConflict: keyField });
                if (error) {
                    console.error(`❌ [SYNC] Error syncing ${tableName}:`, error);
                    console.error(`❌ [SYNC] Failed item:`, item);
                    throw new Error(`Failed to sync ${tableName}: ${error.message || JSON.stringify(error)}`);
                } else {
                    console.log(`✅ [SYNC] Synced ${key} to ${tableName}`);
                }
            }
        }

        console.log(`✅ [SYNC] Completed syncing object to ${tableName}`);
    } catch (err) {
        console.error(`❌ [SYNC] Sync error for ${tableName}:`, err);
        throw err; // Re-throw so caller can handle it
    }
};


const LeadsView = ({ primaryColor }) => (
    <div className="p-6 bg-white rounded-lg shadow h-full flex items-center justify-center flex-col text-gray-400">
        <Icon path={ICONS_MAP['customers']} className={`w-16 h-16 mb-4 text-${primaryColor}-300`} />
        <h2 className="text-2xl font-bold mb-2 text-gray-700">Leads Management</h2>
        <p>Leads functionality is currently under development.</p>
    </div>
);

const BottomNavItem = ({ label, icon, currentView, setView, target, primaryColor }) => {
    const isActive = currentView === target;
    return (
        <button
            onClick={() => setView(target)}
            className={`flex flex-col items-center justify-center w-full py-2 space-y-1 ${isActive ? `text-${primaryColor}-600` : 'text-gray-400 hover:text-gray-600'}`}
        >
            <Icon path={ICONS_MAP[icon] || ICONS_MAP['dashboard']} className={`w-6 h-6 ${isActive ? 'scale-110 transition-transform' : ''}`} />
            <span className="text-[10px] items-center font-medium truncate">{label || target}</span>
        </button>
    );
};

const DynamicTheme = ({ primaryColor }) => {
    const baseHex = COLOR_PALETTE[primaryColor] || COLOR_PALETTE.indigo;
    const variants = generateColorVariants(baseHex);

    if (!variants) return null;

    const css = `
        :root {
            --primary-50: ${variants[50]};
            --primary-100: ${variants[100]};
            --primary-200: ${variants[200]};
            --primary-300: ${variants[300]};
            --primary-400: ${variants[400]};
            --primary-500: ${variants[500]};
            --primary-600: ${variants[600]};
            --primary-700: ${variants[700]};
            --primary-800: ${variants[800]};
            --primary-900: ${variants[900]};
        }
        
        /* Background Overrides */
        .bg-${primaryColor}-50 { background-color: var(--primary-50) !important; }
        .bg-${primaryColor}-100 { background-color: var(--primary-100) !important; }
        .bg-${primaryColor}-200 { background-color: var(--primary-200) !important; }
        .bg-${primaryColor}-300 { background-color: var(--primary-300) !important; }
        .bg-${primaryColor}-400 { background-color: var(--primary-400) !important; }
        .bg-${primaryColor}-500 { background-color: var(--primary-500) !important; }
        .bg-${primaryColor}-600 { background-color: var(--primary-600) !important; }
        .bg-${primaryColor}-700 { background-color: var(--primary-700) !important; }
        .bg-${primaryColor}-800 { background-color: var(--primary-800) !important; }
        .bg-${primaryColor}-900 { background-color: var(--primary-900) !important; }
        
        /* Text Overrides */
        .text-${primaryColor}-50 { color: var(--primary-50) !important; }
        .text-${primaryColor}-100 { color: var(--primary-100) !important; }
        .text-${primaryColor}-200 { color: var(--primary-200) !important; }
        .text-${primaryColor}-300 { color: var(--primary-300) !important; }
        .text-${primaryColor}-400 { color: var(--primary-400) !important; }
        .text-${primaryColor}-500 { color: var(--primary-500) !important; }
        .text-${primaryColor}-600 { color: var(--primary-600) !important; }
        .text-${primaryColor}-700 { color: var(--primary-700) !important; }
        .text-${primaryColor}-800 { color: var(--primary-800) !important; }
        .text-${primaryColor}-900 { color: var(--primary-900) !important; }
        
        /* Border Overrides */
        .border-${primaryColor}-50 { border-color: var(--primary-50) !important; }
        .border-${primaryColor}-100 { border-color: var(--primary-100) !important; }
        .border-${primaryColor}-200 { border-color: var(--primary-200) !important; }
        .border-${primaryColor}-300 { border-color: var(--primary-300) !important; }
        .border-${primaryColor}-400 { border-color: var(--primary-400) !important; }
        .border-${primaryColor}-500 { border-color: var(--primary-500) !important; }
        .border-${primaryColor}-600 { border-color: var(--primary-600) !important; }
        .border-${primaryColor}-700 { border-color: var(--primary-700) !important; }
        
        /* Hover Overrides */
        .hover\\:bg-${primaryColor}-50:hover { background-color: var(--primary-50) !important; }
        .hover\\:bg-${primaryColor}-100:hover { background-color: var(--primary-100) !important; }
        .hover\\:bg-${primaryColor}-600:hover { background-color: var(--primary-600) !important; }
        .hover\\:bg-${primaryColor}-700:hover { background-color: var(--primary-700) !important; }
        .hover\\:text-${primaryColor}-600:hover { color: var(--primary-600) !important; }
        .hover\\:text-${primaryColor}-700:hover { color: var(--primary-700) !important; }
        
        /* Ring Overrides */
        .focus\\:ring-${primaryColor}-500:focus { --tw-ring-color: var(--primary-500) !important; }
        .focus\\:ring-${primaryColor}-600:focus { --tw-ring-color: var(--primary-600) !important; }
    `;

    return <style dangerouslySetInnerHTML={{ __html: css }} />;
};

export default function App() {
    const { user, loading, signOut, isAdmin } = useAuth();
    const { language, toggleLanguage, t, isRTL } = useLanguage();
    const {
        isSupported: isPushSupported,
        isSubscribed: isPushSubscribed,
        isLoading: isPushLoading,
        error: pushError,
        subscribe: subscribeToPush,
        unsubscribe: unsubscribeFromPush,
        sendTestNotification,
        clearError: clearPushError
    } = usePushNotifications(user?.id);
    const [showTopSettings, setShowTopSettings] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    // Map route to internal view string
    const getViewFromPath = () => {
        const p = location.pathname;
        if (p.startsWith('/orders/new')) return 'New Order';
        if (p.startsWith('/orders/deleted')) return 'Deleted Orders';
        if (p.startsWith('/orders')) return 'Orders Board';
        if (p.startsWith('/clients/new')) return 'New Customer';
        if (p.startsWith('/clients')) return 'Customers';
        if (p.startsWith('/inventory')) return 'Stock';
        if (p.startsWith('/leads')) return 'Leads';
        if (p.startsWith('/settings')) return 'Settings';
        return 'Dashboard';
    };
    const view = getViewFromPath();

    // Sync setView to routes so legacy references don't break
    const setView = (newView) => {
        if (newView === 'Stock') navigate('/inventory');
        else if (newView === 'Customers') navigate('/clients');
        else if (newView === 'Leads') navigate('/leads');
        else if (newView === 'Orders Board') navigate('/orders');
        else if (newView === 'Settings') navigate('/settings');
        else if (newView === 'New Order') navigate('/orders/new');
        else if (newView === 'New Customer') navigate('/clients/new');
        else if (newView === 'Deleted Orders') navigate('/orders/deleted');
        else navigate('/dashboard');
    };

    // State management
    const [data, setData] = useState(initialData);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '' });
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedCustomerForHistory, setSelectedCustomerForHistory] = useState(null);
    const [editingSingleItem, setEditingSingleItem] = useState(null);
    const [editData, setEditData] = useState({});
    const [isPapaLoaded, setIsPapaLoaded] = useState(false); // New state to track PapaParse loading
    const [isLoadingFromSupabase, setIsLoadingFromSupabase] = useState(true);
    const [showLowStockAlert, setShowLowStockAlert] = useState(false);
    const [lowStockAlertShownOnLogin, setLowStockAlertShownOnLogin] = useState(false);
    const [pendingWebsiteOrders, setPendingWebsiteOrders] = useState([]);
    const [pendingInstapayOrders, setPendingInstapayOrders] = useState([]);
    const [showPendingWebsiteModal, setShowPendingWebsiteModal] = useState(false);
    const [singleOrderToImport, setSingleOrderToImport] = useState(null);
    const [wooImportData, setWooImportData] = useState(null); // Passed down to NewOrderForm

    // Helper function to check if stock adjustment crossed the low stock threshold
    const checkLowStockThreshold = useCallback((itemType, itemId, newStock) => {
        const threshold = itemType === 'oils' ? data.stockSettings.lowStockThresholds.oils : data.stockSettings.lowStockThresholds.bottles;

        // Check if the new stock is below threshold
        if (safeCoerceNumber(newStock) < threshold) {
            // Show the low stock alert
            setShowLowStockAlert(true);
        }
    }, [data.stockSettings.lowStockThresholds]);

    // Ref for sidebar to detect clicks outside
    const sidebarRef = useRef(null);

    // Close sidebar when clicking outside on mobile
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Only handle on mobile (when sidebar is overlay)
            if (window.innerWidth >= 1024) return; // lg breakpoint

            if (isSidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                setSidebarOpen(false);
            }
        };

        if (isSidebarOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isSidebarOpen]);

    // Expose test function to window for browser console access
    useEffect(() => {
        window.testSupabase = async () => {
            console.log('='.repeat(60));
            console.log('SUPABASE CONNECTION TEST');
            console.log('='.repeat(60));
            const results = await testSupabaseConnection();
            console.log('\n' + '='.repeat(60));
            console.log('TEST COMPLETE');
            console.log('='.repeat(60));
            return results;
        };
        console.log('💡 Tip: Run window.testSupabase() in console to test database connection');
    }, []);

    useEffect(() => { if (editingSingleItem) setEditData({ ...editingSingleItem.data }); }, [editingSingleItem]);

    // Load data from Supabase on initial mount when user is authenticated
    useEffect(() => {
        if (!user || loading) {
            setIsLoadingFromSupabase(false);
            return;
        }

        const loadInitialData = async () => {
            try {
                console.log('📥 Loading initial data from Supabase...');
                setIsLoadingFromSupabase(true);

                const supabaseData = await loadFromSupabase();

                if (supabaseData) {
                    console.log('✅ Data loaded from Supabase successfully');
                    setData(supabaseData);

                    // Check for low stock alerts after login (only once)
                    if (!lowStockAlertShownOnLogin) {
                        const lowOils = supabaseData.oils.filter(o => safeCoerceNumber(o.actualstock) < supabaseData.stockSettings.lowStockThresholds.oils);
                        const lowBottles = supabaseData.bottles.filter(b => safeCoerceNumber(b.actualstock) < supabaseData.stockSettings.lowStockThresholds.bottles);

                        if (lowOils.length > 0 || lowBottles.length > 0) {
                            setShowLowStockAlert(true);
                            setLowStockAlertShownOnLogin(true);
                        }
                    }
                } else {
                    console.log('⚠️  No data in Supabase, using initial data');
                }
            } catch (error) {
                console.error('❌ Error loading data from Supabase:', error);
                showAlert('Failed to load data from database. Using local data.');
            } finally {
                setIsLoadingFromSupabase(false);
            }
        };

        loadInitialData();
    }, [user, loading]); // Only run when user/loading changes

    // Helper functions to sync specific items to Supabase
    const syncSingleItem = async (tableName, item) => {
        if (!user) {
            console.error(`❌ [SYNC] Cannot sync ${tableName} - user not authenticated`);
            throw new Error('Not authenticated');
        }
        try {
            console.log(`🔄 [SYNC] syncSingleItem called for ${tableName}`, tableName === 'orders' ? { orderid: item.orderid, status: item.status, customer: item.customer } : '');
            await syncToSupabase(tableName, [item]);
            console.log(`✅ [SYNC] syncSingleItem completed for ${tableName}`, tableName === 'orders' ? { orderid: item.orderid, status: item.status } : '');
        } catch (err) {
            console.error(`❌ [SYNC] Error in syncSingleItem for ${tableName}:`, err);
            throw err;
        }
    };

    const syncMultipleItems = async (tableName, items) => {
        if (!user) {
            console.error(`❌ [SYNC] Cannot sync ${tableName} - user not authenticated`);
            throw new Error('Not authenticated');
        }
        try {
            console.log(`🔄 [SYNC] syncMultipleItems called for ${tableName} (${items.length} items)`);
            await syncToSupabase(tableName, items);
            console.log(`✅ [SYNC] syncMultipleItems completed for ${tableName}`);
        } catch (err) {
            console.error(`❌ [SYNC] Error in syncMultipleItems for ${tableName}:`, err);
            throw err;
        }
    };

    const syncObjectData = async (tableName, obj, keyField, valueField) => {
        if (!user) {
            console.error(`❌ [SYNC] Cannot sync ${tableName} - user not authenticated`);
            throw new Error('Not authenticated');
        }
        try {
            console.log(`🔄 [SYNC] syncObjectData called for ${tableName}`);
            await syncObjectToSupabase(tableName, obj, keyField, valueField);
            console.log(`✅ [SYNC] syncObjectData completed for ${tableName}`);
        } catch (err) {
            console.error(`❌ [SYNC] Error in syncObjectData for ${tableName}:`, err);
            throw err;
        }
    };

    const syncSettings = async () => {
        if (!user) return;
        try {
            // Sync general settings
            if (data.generalSettings) {
                await supabaseTracked
                    .from('app_settings')
                    .upsert({
                        setting_key: 'general_settings',
                        setting_value: data.generalSettings
                    }, { onConflict: 'setting_key' });
            }

            // Sync UI settings (logoUrl, primaryColor)
            if (data.settings) {
                const uiSettings = {
                    logoUrl: data.settings.logoUrl,
                    primaryColor: data.settings.primaryColor
                };
                await supabaseTracked
                    .from('app_settings')
                    .upsert({
                        setting_key: 'ui_settings',
                        setting_value: uiSettings
                    }, { onConflict: 'setting_key' });

                // Sync status config (icons and colors)
                const statusConfig = {
                    statusIcons: data.settings.statusIcons,
                    statusColors: data.settings.statusColors
                };
                await supabaseTracked
                    .from('app_settings')
                    .upsert({
                        setting_key: 'status_config',
                        setting_value: statusConfig
                    }, { onConflict: 'setting_key' });
            }

            // Sync stock settings
            if (data.stockSettings) {
                await supabaseTracked
                    .from('app_settings')
                    .upsert({
                        setting_key: 'stock_settings',
                        setting_value: data.stockSettings
                    }, { onConflict: 'setting_key' });
            }
        } catch (err) {
            console.error('❌ [SYNC] Error syncing settings:', err);
            showAlert(`Sync failed for settings: ${err.message}`);
            throw err;
        }
    };

    const oilMap = useMemo(() => new Map(data.oils.map(o => [o.oil, o])), [data.oils]);
    // The bottle ID logic must be handled carefully. Testers use the 'tester' ID regardless of size/gender in CSV.
    const bottleMap = useMemo(() => new Map(data.bottles.map(b => [b.id || `${b.size}${b.gender?.toLowerCase().replace(/ /g, '')}`, b])), [data.bottles]);

    // Derived list of all customers who are NOT deleted. This is necessary for the New Order Form to work correctly
    const activeCustomers = useMemo(() => data.customers.filter(c => c.status !== 'deleted'), [data.customers]);

    // --- Business Logic Functions ---

    const getEffectiveOilQty = useCallback((product) => {
        if (!product) return 0;
        const oilInfo = oilMap.get(product.oil);
        if (oilInfo && product.size) {
            const sizeStr = product.size.replace(/[^0-9]/g, '');
            if (sizeStr) {
                const defaultField = `oil_per_${sizeStr}ml`;
                if (oilInfo[defaultField] !== undefined && oilInfo[defaultField] !== null) {
                    return parseFloat(oilInfo[defaultField]);
                }
            }
        }
        return safeCoerceNumber(product.qtyoilinbottle);
    }, [oilMap]);

    const calculateBuildableStock = useCallback((product) => {
        if (!product) return 0;
        const oilInfo = oilMap.get(product.oil);
        let bottleId;

        if (product.product?.toLowerCase().includes('tester')) {
            bottleId = `tester`;
        } else {
            bottleId = `${product.size}${product.gender?.toLowerCase().replace(/ /g, '')}`;
        }

        const bottleInfo = bottleMap.get(bottleId);
        const effectiveQty = getEffectiveOilQty(product);

        if (!oilInfo || !bottleInfo || !effectiveQty) return 0;
        const oilStock = safeCoerceNumber(oilInfo.actualstock);
        const oilPerBottle = safeCoerceNumber(effectiveQty);
        const bottleStock = safeCoerceNumber(bottleInfo.actualstock);

        if (oilPerBottle === 0) return bottleStock;

        const canMakeFromOil = Math.floor(oilStock / oilPerBottle);
        return Math.min(canMakeFromOil, bottleStock);

    }, [oilMap, bottleMap, getEffectiveOilQty]);

    const getProductCost = useCallback((product) => {
        if (!product) return 0;
        const oilInfo = oilMap.get(product.oil);
        let bottleId;

        if (product.product?.toLowerCase().includes('tester')) {
            bottleId = `tester`;
        } else {
            bottleId = `${product.size}${product.gender?.toLowerCase().replace(/ /g, '')}`;
        }

        const bottleInfo = bottleMap.get(bottleId);
        if (!oilInfo || !bottleInfo) return 0;

        const effectiveQty = getEffectiveOilQty(product);
        const oilCost = safeCoerceNumber(oilInfo.price) * safeCoerceNumber(effectiveQty);
        const bottleCost = safeCoerceNumber(bottleInfo.totalcostbottle);
        return oilCost + bottleCost;
    }, [oilMap, bottleMap, getEffectiveOilQty]);

    // Load PapaParse and Tailwind CSS on mount
    useEffect(() => {
        // Load PapaParse script: Changed CDN to prevent cross-origin script error
        if (!window.Papa) {
            const script = document.createElement('script');
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js"; // Switched to a standard CDN
            script.onload = () => {
                setIsPapaLoaded(true);
                console.log("PapaParse loaded successfully from new CDN.");
            };
            script.onerror = (e) => {
                console.error("Failed to load PapaParse from CDN:", e);
                // Fallback message, though already handled by isPapaLoaded check
            };
            document.head.appendChild(script);
        } else {
            setIsPapaLoaded(true);
        }

        // Load Tailwind CSS (if not present)
        if (!document.querySelector('script[src="https://cdn.tailwindcss.com"]')) {
            const script = document.createElement('script');
            script.src = "https://cdn.tailwindcss.com";
            script.async = true;
            document.head.appendChild(script);
        }
    }, []);

    // --- Data Handling ---

    const handleFileUpload = async (e, targetKey) => {
        if (!isPapaLoaded || !window.Papa) {
            // This alert remains necessary until PapaParse is guaranteed to be loaded.
            showAlert("Please wait, data parsing library is still loading.");
            return;
        }

        const file = e.target.files[0];
        if (!file) return;

        const Papa = window.Papa;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: header => header.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/gi, ''),
            complete: async (results) => {
                const parsedData = results.data;
                let processedData = [];
                let updatedReturnedPool = null;

                setData(currentData => {
                    let newData = JSON.parse(JSON.stringify(currentData)); // Deep copy for complex updates

                    if (targetKey === 'used') {
                        parsedData.forEach(item => {
                            const poolKey = item['productused'];
                            if (poolKey) newData.returnedPool[poolKey] = (newData.returnedPool[poolKey] || 0) + safeCoerceNumber(item.usedqty);
                        });
                        updatedReturnedPool = newData.returnedPool;
                    } else if (targetKey) {
                        processedData = parsedData.map((row, index) => {
                            let newRow = { ...row, id: row.id || `${targetKey.slice(0, 1)}${Date.now() + index}` };
                            if (targetKey === 'customers') {
                                newRow.orders = safeCoerceNumber(newRow.orders || 0);
                                newRow.cancels = newRow.cancels ? String(newRow.cancels).split(',').map(d => d.trim()).filter(Boolean) : [];
                                newRow.completed = safeCoerceNumber(newRow.completed || 0);
                            }
                            if (targetKey === 'shipping') { newRow.fees = safeCoerceNumber(newRow.fees || newRow['fees(£)'] || newRow['']); }
                            if (targetKey === 'products') {
                                newRow.qtysold = safeCoerceNumber(newRow.qtysold || 0);
                                newRow.costproduct = getProductCost(newRow); // Calculate initial cost
                            }
                            if (targetKey === 'bottles') {
                                newRow.actualstock = safeCoerceNumber(newRow.actualstock || 0);
                                newRow.totalordered = safeCoerceNumber(newRow.totalordered || 0);
                                newRow.totalcostbottle = safeCoerceNumber(newRow.totalcostbottle || 0);
                            }
                            if (targetKey === 'oils') {
                                newRow.actualstock = safeCoerceNumber(newRow.actualstock || 0);
                                newRow.totalordered = safeCoerceNumber(newRow.totalordered || 0);
                                newRow.price = safeCoerceNumber(newRow.price || 0);
                            }
                            if (targetKey === 'orders') {
                                if (newRow.itemsjson) {
                                    // Parse itemsjson array of "Product x qty" strings
                                    try {
                                        const itemsJson = JSON.parse(newRow.itemsjson);
                                        newRow.items = itemsJson.map(itemStr => {
                                            const match = itemStr.match(/^(.+?)\s*x\s*(\d+)$/);
                                            if (match) {
                                                return {
                                                    pid: match[1].trim(),
                                                    qty: parseInt(match[2]),
                                                    used: itemStr.toLowerCase().includes('used') || (newRow.package || '').toLowerCase().includes('used')
                                                };
                                            } else {
                                                return {
                                                    pid: itemStr.trim(),
                                                    qty: 1,
                                                    used: (newRow.package || '').toLowerCase().includes('used')
                                                };
                                            }
                                        });
                                    } catch (e) {
                                        // Fallback to single product if parsing fails
                                        const productName = String(newRow.product || '').trim();
                                        const totalQty = safeCoerceNumber(newRow.quantity);
                                        newRow.items = [{ pid: productName, qty: totalQty, used: (newRow.package || '').toLowerCase().includes('used') }];
                                    }
                                } else {
                                    // Fallback to single product
                                    const productName = String(newRow.product || '').trim();
                                    const totalQty = safeCoerceNumber(newRow.quantity);
                                    newRow.items = [{ pid: productName, qty: totalQty, used: (newRow.package || '').toLowerCase().includes('used') }];
                                }
                            }
                            return newRow;
                        });
                        newData[targetKey] = processedData;
                    }
                    return newData;
                });

                // Sync to Supabase after updating local state
                try {
                    console.log(`🔄 Syncing uploaded ${targetKey} data to Supabase...`);
                    if (targetKey === 'used' && updatedReturnedPool) {
                        await syncObjectData('returned_pool', updatedReturnedPool, 'product_name', 'quantity');
                    } else if (processedData.length > 0) {
                        await syncMultipleItems(targetKey, processedData);
                    }
                    console.log(`✅ Successfully synced ${targetKey} to Supabase`);
                    showAlert(`Successfully imported and synced data for ${targetKey}.`);
                } catch (err) {
                    console.error(`❌ Error syncing ${targetKey} to Supabase:`, err);
                    showAlert(`Data imported locally but failed to sync to database: ${err.message}`);
                }
            }
        });
        e.target.value = null;
    };

    const handleSaveOrder = async (newOrderData) => {
        // Clone current data synchronously from the component's closure
        let newData = JSON.parse(JSON.stringify(data));

        let newOrder;
        let updatedCustomer;
        let updatedOils = [];
        let updatedBottles = [];
        let updatedReturnedPool = null;
        let previousOilStocks = {};
        let previousBottleStocks = {};

        // Find customer to get their name
        const customer = newData.customers.find(c => c.id === newOrderData.customerid);

        // Generate unique Order ID by finding the maximum existing ID
        let maxId = 0;
        if (newData.orders && newData.orders.length > 0) {
            const orderNumbers = newData.orders.map(o => {
                const match = String(o?.orderid || '').match(/^#?o(\d+)$/i);
                return match ? parseInt(match[1], 10) : 0;
            });
            maxId = Math.max(0, ...orderNumbers);
        }
        const nextOrderId = `o${(maxId + 1).toString().padStart(3, '0')}`;

        newOrder = {
            ...newOrderData,
            orderid: nextOrderId,
            status: 'ordered',
            date: newOrderData.date,
            customer: customer?.name || null,  // Add customer name for easier querying/exporting
            woo_order_id: newOrderData.woo_order_id || null, // Explicitly carry over the woo order
            wooOrderData: newOrderData.wooOrderData || null,
        };

        // 1. Update Customer Order Count (The "orders" counter tracks total lifetime orders, regardless of status)
        const customerIndex = newData.customers.findIndex(c => c.id === newOrder.customerid);
        if (customerIndex > -1) {
            newData.customers[customerIndex].orders = (newData.customers[customerIndex].orders || 0) + 1;
            updatedCustomer = newData.customers[customerIndex];
        }

        // 2. Update Stock and Calculate Cost (Subtracting stock upon creation, regardless of initial status)
        let orderCost = 0;
        newOrder.items.forEach(item => {
            if (item.used) {
                newData.returnedPool[item.pid] = (newData.returnedPool[item.pid] || 0) - safeCoerceNumber(item.qty);
                updatedReturnedPool = newData.returnedPool;
            } else {
                const productInfo = newData.products.find(p => p.product === item.pid);
                if (productInfo) {
                    const oilIndex = newData.oils.findIndex(o => o.oil === productInfo.oil);
                    if (oilIndex > -1) {
                        // Store previous stock before update
                        previousOilStocks[newData.oils[oilIndex].oil] = safeCoerceNumber(newData.oils[oilIndex].actualstock);

                        const oilNeeded = safeCoerceNumber(productInfo.qtyoilinbottle) * safeCoerceNumber(item.qty);
                        newData.oils[oilIndex].actualstock = safeCoerceNumber(newData.oils[oilIndex].actualstock) - oilNeeded;
                        newData.oils[oilIndex].totalordered = safeCoerceNumber(newData.oils[oilIndex].totalordered || 0) + oilNeeded;
                        updatedOils.push(newData.oils[oilIndex]);
                    }

                    let bottleId;
                    if (productInfo.product?.toLowerCase().includes('tester')) {
                        bottleId = `tester`;
                    } else {
                        bottleId = `${productInfo.size}${productInfo.gender?.toLowerCase().replace(/ /g, '')}`;
                    }
                    const bottleIndex = newData.bottles.findIndex(b => b.id === bottleId);
                    if (bottleIndex > -1) {
                        // Store previous stock before update
                        previousBottleStocks[newData.bottles[bottleIndex].id] = safeCoerceNumber(newData.bottles[bottleIndex].actualstock);

                        newData.bottles[bottleIndex].actualstock = safeCoerceNumber(newData.bottles[bottleIndex].actualstock) - safeCoerceNumber(item.qty);
                        newData.bottles[bottleIndex].totalordered = safeCoerceNumber(newData.bottles[bottleIndex].totalordered || 0) + safeCoerceNumber(item.qty);
                        updatedBottles.push(newData.bottles[bottleIndex]);
                    }
                    orderCost += safeCoerceNumber(productInfo.costproduct) * safeCoerceNumber(item.qty);
                }
            }
        });

        newOrder.cost = orderCost;

        // 3. Apply state
        newData.orders = [newOrder, ...newData.orders]; // Prepend so it shows at the top
        setData(newData);

        setView('Orders Board');
        showAlert(`Order #${newOrder.orderid} created successfully.`);

        // Check if any item crossed the low stock threshold
        const oilThreshold = newData.stockSettings.lowStockThresholds.oils;
        const bottleThreshold = newData.stockSettings.lowStockThresholds.bottles;

        let thresholdCrossed = false;

        updatedOils.forEach(oil => {
            const prevStock = previousOilStocks[oil.oil];
            const newStock = safeCoerceNumber(oil.actualstock);
            if (prevStock >= oilThreshold && newStock < oilThreshold) {
                thresholdCrossed = true;
            }
        });

        updatedBottles.forEach(bottle => {
            const prevStock = previousBottleStocks[bottle.id];
            const newStock = safeCoerceNumber(bottle.actualstock);
            if (prevStock >= bottleThreshold && newStock < bottleThreshold) {
                thresholdCrossed = true;
            }
        });

        if (thresholdCrossed) {
            setShowLowStockAlert(true);
        }

        // Sync to Supabase - Since state was prepared synchronously, variables are guaranteed populated
        try {
            console.log('🔄 Syncing new order to Supabase...', newOrder);

            if (!newOrder || !newOrder.orderid) {
                console.error("Critical Sync Error: newOrder object is badly formed!", newOrder);
                return;
            }

            const syncPromises = [syncSingleItem('orders', newOrder)];

            if (updatedCustomer) {
                syncPromises.push(syncSingleItem('customers', updatedCustomer));
            }

            if (updatedOils.length > 0) {
                syncPromises.push(syncMultipleItems('oils', updatedOils));
            }

            if (updatedBottles.length > 0) {
                syncPromises.push(syncMultipleItems('bottles', updatedBottles));
            }

            if (updatedReturnedPool) {
                // Update the array-based record for returned_pool instead
                syncPromises.push(syncObjectData('returned_pool', updatedReturnedPool, 'product_name', 'quantity'));
            }

            await Promise.all(syncPromises);
            console.log('✅ New order synced successfully');

            // Mark as imported in storefront DB if applicable
            if (newOrder.woo_order_id) {
                try {
                    await lilacvalleyClient.from('orders').update({ crm: 'imported' }).eq('id', newOrder.woo_order_id);
                    console.log('✅ Marked as imported in storefront database.');
                } catch (storefrontErr) {
                    console.warn('⚠️ Could not update storefront status:', storefrontErr);
                }
            }

        } catch (err) {
            console.error("❌ Failed to sync new order data to Supabase:", err);
            showAlert(`Failed to sync new order: ${err.message}`);
        }
    };

    const handleUpdateOrder = async (orderId, updatedOrder) => {
        setData(currentData => {
            // NOTE: Updating a live order's items/quantities is complex, as it would require rolling back and reapplying stock movements.
            // For now, only replacing the order and letting state changes handle stock/history.
            const newOrders = currentData.orders.map(o => o.orderid === orderId ? updatedOrder : o);

            // Simplified approach: Re-run stock/history logic if items *actually changed* or status changed.
            // Since this is a simple update, we trust the user made minor edits. If a major update is needed,
            // we should perform a rollback/re-apply (out of scope for this simple update, but noted).

            return { ...currentData, orders: newOrders };
        });
        setSelectedOrder(updatedOrder);
        showAlert(`Order #${orderId} updated.`);

        // Sync to Supabase
        console.log('🔄 Syncing updated order to Supabase...');
        await syncSingleItem('orders', updatedOrder);
    };

    const handleSaveCustomer = async (newCustomerData) => {
        // Prepare customer data WITHOUT the temporary ID (let database generate UUID)
        const customerDataForDB = {
            name: newCustomerData.name,
            mobile: newCustomerData.mobile,
            email: newCustomerData.email,
            state: newCustomerData.state,
            city: newCustomerData.city,
            address: newCustomerData.address || '',
            orders: 0,
            cancels: [],
            completed: 0,
            status: 'active'
        };

        // Sync to Supabase FIRST to get the UUID
        console.log('🔄 Syncing new customer to Supabase...', customerDataForDB);
        try {
            // Insert and get the returned customer with database-generated UUID
            const { data: syncedCustomers, error } = await supabaseTracked
                .from('customers')
                .insert(customerDataForDB)
                .select();

            if (error) {
                console.error('❌ Error syncing customer:', error);
                throw error;
            }

            // Get the customer with the database-generated UUID
            const customerWithUUID = syncedCustomers?.[0];

            if (!customerWithUUID) {
                throw new Error('Failed to get customer UUID from database');
            }

            console.log('✅ Customer synced with UUID:', customerWithUUID.id);

            // Update local state with the UUID from database
            setData({ ...data, customers: [...data.customers, customerWithUUID] });

            // If we are in a WooCommerce import flow, update the import data with the new customer ID
            if (wooImportData) {
                setWooImportData(prev => ({ ...prev, customerId: customerWithUUID.id }));
            }

            showAlert(`Customer ${customerWithUUID.name} added successfully.`);

            return customerWithUUID; // Return customer with UUID
        } catch (err) {
            console.error('❌ Error saving customer:', err);
            // Fallback: save locally even if sync fails with temporary ID
            const tempCustomer = { ...newCustomerData, id: `c${Date.now()}`, orders: 0, cancels: [], completed: 0 };
            setData({ ...data, customers: [...data.customers, tempCustomer] });
            showAlert(`Customer added locally but failed to sync: ${err.message}`);
            return tempCustomer;
        }
    };

    const handleCustomerUpdate = async (customerId, updatedData) => {
        const newCustomers = data.customers.map(c => c.id === customerId ? updatedData : c);
        setData({ ...data, customers: newCustomers });
        showAlert('Customer updated.');

        // Sync to Supabase
        console.log('🔄 Syncing updated customer to Supabase...');
        await syncSingleItem('customers', updatedData);
    };

    const handleDeleteVendor = async (vendorId, vendorName) => {
        if (!window.confirm(`Are you sure you want to delete vendor "${vendorName}"? This action cannot be undone.`)) {
            return;
        }
        try {
            console.log('?? Deleting vendor from Supabase...');
            const { error } = await supabaseTracked.from('vendors').delete().eq('id', vendorId);
            if (error) throw error;

            const newVendors = data.vendors.filter(v => v.id !== vendorId);
            setData(prev => ({ ...prev, vendors: newVendors }));

            console.log('? Vendor deleted successfully!');
            showAlert('Vendor deleted successfully.');
        } catch (err) {
            console.error('? Error deleting vendor:', err);
            showAlert('Error deleting vendor: ' + err.message);
        }
    };

    const handleDeleteOil = async (oilName) => {
        if (!window.confirm(`Are you sure you want to delete oil "${oilName}"? This action cannot be undone.`)) {
            return;
        }
        try {
            // Delete from Supabase FIRST
            console.log('🔄 Deleting oil from Supabase...');
            const { error } = await supabaseTracked.from('oils').delete().eq('oil', oilName);
            if (error) throw error;

            // Only update local state after successful deletion
            const newOils = data.oils.filter(o => o.oil !== oilName);
            setData({ ...data, oils: newOils });

            console.log('✅ Oil deleted successfully!');
            showAlert('Oil deleted successfully.');
        } catch (err) {
            console.error('❌ Error deleting oil:', err);
            showAlert(`Failed to delete oil: ${err.message}`);
            throw err;
        }
    };

    const handleDeleteBottle = async (bottleId) => {
        if (!window.confirm(`Are you sure you want to delete bottle "${bottleId}"? This action cannot be undone.`)) {
            return;
        }
        try {
            // Delete from Supabase FIRST
            console.log('🔄 Deleting bottle from Supabase...');
            const { error } = await supabaseTracked.from('bottles').delete().eq('id', bottleId);
            if (error) throw error;

            // Only update local state after successful deletion
            const newBottles = data.bottles.filter(b => b.id !== bottleId);
            setData({ ...data, bottles: newBottles });

            console.log('✅ Bottle deleted successfully!');
            showAlert('Bottle deleted successfully.');
        } catch (err) {
            console.error('❌ Error deleting bottle:', err);
            showAlert(`Failed to delete bottle: ${err.message}`);
            throw err;
        }
    };

    const handleDeleteCustomer = async (customerId) => {
        const customer = data.customers.find(c => c.id === customerId);
        if (!window.confirm(`Are you sure you want to delete customer "${customer?.name}"? This action cannot be undone.`)) {
            return;
        }
        try {
            // Delete from Supabase FIRST
            console.log('🔄 Deleting customer from Supabase...');
            const { error } = await supabaseTracked.from('customers').delete().eq('id', customerId);
            if (error) throw error;

            // Only update local state after successful deletion
            const newCustomers = data.customers.filter(c => c.id !== customerId);
            setData({ ...data, customers: newCustomers });

            console.log('✅ Customer deleted successfully!');
            showAlert('Customer deleted successfully.');
        } catch (err) {
            console.error('❌ Error deleting customer:', err);
            showAlert(`Failed to delete customer: ${err.message}`);
            throw err;
        }
    };

    const ORDER_STATUS_FLOW = ['ordered', 'shipped', 'delivered', 'completed'];

    const handleOrderAction = async (orderId, action) => {
        console.log(`🔵 [ORDER ACTION] Starting: orderId=${orderId}, action=${action}`);

        // STEP 1: Calculate what changes need to be made (WITHOUT updating UI yet)
        // We need to access current data synchronously
        const currentData = data;
        const orders = currentData.orders;
        const orderIndex = orders.findIndex(o => o.orderid === orderId);

        if (orderIndex === -1) {
            console.error(`❌ [ORDER ACTION] Order not found: ${orderId}`);
            showAlert('Order not found');
            return;
        }

        // Deep clone to avoid mutating state
        const newData = JSON.parse(JSON.stringify(currentData));
        let order = { ...newData.orders[orderIndex] };
        const originalStatus = order.status || 'ordered';
        console.log(`🔵 [ORDER ACTION] Original status: ${originalStatus}`);
        const customer = newData.customers.find(c => c.id === order.customerid);

        // Ensure customer name is always set
        if (customer && !order.customer) {
            order.customer = customer.name;
        }

        let updatedCustomer = null;
        let updatedProducts = [];
        let updatedReturnedPool = null;
        let orderDeleted = false;

        // Helper to handle stock/history reversal when moving out of a terminal/completed state
        const revertTerminalState = (statusToRevert) => {
            if (statusToRevert === 'returned') {
                order.items.forEach(item => {
                    newData.returnedPool[item.pid] = (newData.returnedPool[item.pid] || 0) - safeCoerceNumber(item.qty);
                });
                updatedReturnedPool = newData.returnedPool;
            }
            if (originalStatus === 'cancelled' && customer?.cancels.length > 0) {
                customer.cancels.pop();
                updatedCustomer = customer;
            }
        };

        const isCompletedStatus = originalStatus === 'completed';
        let reverseSaleCount = isCompletedStatus && !['return', 'cancel'].includes(action);

        // Apply the action to calculate new status
        switch (action) {
            case 'advance': {
                const i = ORDER_STATUS_FLOW.indexOf(order.status);
                console.log(`🔵 [ORDER ACTION] Advance: current index=${i}, status=${order.status}`);
                if (i < ORDER_STATUS_FLOW.length - 1) {
                    order.status = ORDER_STATUS_FLOW[i + 1];
                    console.log(`🔵 [ORDER ACTION] New status: ${order.status}`);
                } else {
                    console.log(`⚠️ [ORDER ACTION] Already at final status, cannot advance`);
                }
                break;
            }
            case 'back': {
                const i = ORDER_STATUS_FLOW.indexOf(order.status);
                if (i > 0) order.status = ORDER_STATUS_FLOW[i - 1];
                break;
            }
            case 'reopen': {
                revertTerminalState(originalStatus);
                order.status = 'ordered';
                break;
            }
            case 'complete':
                order.status = 'completed';
                break;
            case 'return': {
                if (isCompletedStatus) reverseSaleCount = true;
                order.status = 'returned';
                order.items.forEach(item => {
                    newData.returnedPool[item.pid] = (newData.returnedPool[item.pid] || 0) + safeCoerceNumber(item.qty);
                });
                updatedReturnedPool = newData.returnedPool;
                break;
            }
            case 'cancel': {
                if (isCompletedStatus) reverseSaleCount = true;
                order.status = 'cancelled';
                if (originalStatus !== 'cancelled') {
                    if (customer) {
                        if (!customer.cancels) customer.cancels = [];
                        // Save full order details with cancellation
                        customer.cancels.push({
                            orderid: order.orderid,
                            cancelDate: new Date().toISOString(),
                            date: order.date,
                            total: order.total,
                            payment: order.payment,
                            items: order.items
                        });
                        updatedCustomer = customer;
                    }
                }
                break;
            }
            case 'delete':
                order.status = 'deleted';
                break;
            case 'perm_delete':
                orderDeleted = true;
                break;
            default:
                break;
        }

        // Handle sold quantity updates
        const isNowCompleted = order.status === 'completed' && originalStatus !== 'completed';
        const wasCompleted = reverseSaleCount || (originalStatus === 'completed' && order.status !== 'completed');

        if (isNowCompleted || wasCompleted) {
            const modifier = isNowCompleted ? 1 : -1;
            order.items.forEach(item => {
                const productIndex = newData.products.findIndex(p => p.product === item.pid);
                if (productIndex > -1) {
                    newData.products[productIndex].qtysold = safeCoerceNumber(newData.products[productIndex].qtysold) + (modifier * safeCoerceNumber(item.qty));
                    updatedProducts.push(newData.products[productIndex]);
                }
            });
        }

        console.log(`🔵 [ORDER ACTION] Calculated changes:`, {
            orderid: order.orderid,
            newStatus: order.status,
            originalStatus,
            hasCustomerUpdate: !!updatedCustomer,
            productsCount: updatedProducts.length,
            hasReturnedPoolUpdate: !!updatedReturnedPool,
            isDeleted: orderDeleted
        });

        // STEP 2: Sync to database FIRST
        try {
            console.log('🔄 [SYNC] Syncing to database BEFORE updating UI...');

            const syncPromises = [];

            if (orderDeleted) {
                console.log('🔄 [SYNC] Deleting order from database...');
                syncPromises.push(
                    supabaseTracked.from('orders').delete().eq('orderid', orderId).then(({ error }) => {
                        if (error) {
                            console.error('❌ [SYNC] Error deleting order:', error);
                            throw new Error(`Failed to delete order: ${error.message}`);
                        }
                        console.log('✅ [SYNC] Order deleted from database');
                    })
                );
            } else {
                console.log('🔄 [SYNC] Upserting order to database:', { orderid: order.orderid, status: order.status, customer: order.customer });
                syncPromises.push(syncSingleItem('orders', order));
            }

            if (updatedCustomer) {
                console.log('🔄 [SYNC] Upserting customer to database');
                syncPromises.push(syncSingleItem('customers', updatedCustomer));
            }

            if (updatedProducts.length > 0) {
                console.log(`🔄 [SYNC] Upserting ${updatedProducts.length} products to database`);
                syncPromises.push(syncMultipleItems('products', updatedProducts));
            }

            if (updatedReturnedPool) {
                console.log('🔄 [SYNC] Upserting returned pool to database');
                syncPromises.push(syncObjectData('returned_pool', updatedReturnedPool, 'product_name', 'quantity'));
            }

            console.log(`🔄 [SYNC] Executing ${syncPromises.length} database operations...`);

            // Wait for ALL database operations to complete with timeout
            // If promises are stuck (e.g., due to backgrounding), timeout and show error
            const SYNC_TIMEOUT = 10000; // 10 seconds
            try {
                await Promise.race([
                    Promise.all(syncPromises),
                    new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('Sync timeout - operations took too long')), SYNC_TIMEOUT)
                    )
                ]);
                console.log('✅ [SYNC] All database operations completed successfully!');
            } catch (err) {
                if (err.message?.includes('timeout')) {
                    console.error('⚠️ [SYNC] Operations timed out after 10 seconds, likely due to backgrounding.');
                    console.error('⚠️ [SYNC] This usually happens when the browser suspends fetch requests.');
                    showAlert('Sync operation timed out. The browser may have suspended the request while the tab was hidden. Please try again.');
                    throw err;
                }
                console.error('❌ [SYNC] Error during database operations:', err);
                throw err;
            }

            // STEP 3: Only NOW update the UI (since database sync succeeded)
            console.log('🔄 [UI] Updating UI after successful database sync...');

            setData(currentData => {
                const newData = JSON.parse(JSON.stringify(currentData));
                const orderIndex = newData.orders.findIndex(o => o.orderid === orderId);

                if (orderDeleted) {
                    // Remove order from UI
                    newData.orders.splice(orderIndex, 1);
                    console.log('✅ [UI] Order removed from UI');
                } else if (orderIndex !== -1) {
                    // Update order in UI
                    newData.orders[orderIndex] = order;
                    console.log('✅ [UI] Order updated in UI:', { orderid: order.orderid, status: order.status });
                }

                // Update customer if changed
                if (updatedCustomer) {
                    const customerIndex = newData.customers.findIndex(c => c.id === updatedCustomer.id);
                    if (customerIndex !== -1) {
                        newData.customers[customerIndex] = updatedCustomer;
                        console.log('✅ [UI] Customer updated in UI');
                    }
                }

                // Update products if changed
                if (updatedProducts.length > 0) {
                    updatedProducts.forEach(updatedProduct => {
                        const productIndex = newData.products.findIndex(p => p.product === updatedProduct.product);
                        if (productIndex !== -1) {
                            newData.products[productIndex] = updatedProduct;
                        }
                    });
                    console.log(`✅ [UI] ${updatedProducts.length} products updated in UI`);
                }

                // Update returned pool if changed
                if (updatedReturnedPool) {
                    newData.returnedPool = updatedReturnedPool;
                    console.log('✅ [UI] Returned pool updated in UI');
                }

                return newData;
            });

            console.log('✅ [ORDER ACTION] Complete! Database synced and UI updated.');
            showAlert('Order updated successfully');

        } catch (err) {
            console.error('❌ [ORDER ACTION] Failed:', err);
            showAlert(`Failed to update order: ${err.message}`);
            // UI was never updated, so no rollback needed!
        }
    };

    const showAlert = useCallback((message) => {
        setAlert({ show: true, message });
        setTimeout(() => setAlert({ show: false, message: '' }), 3000);
    }, []);

    // Fetch and subscribe to pending Website Orders (lilacvalley.orders where status='processing')
    useEffect(() => {
        if (!user || loading) return;

        const fetchPendingOrders = async () => {
            try {
                // Use service role client to bypass RLS on lilacvalley schema
                const { data, error } = await lilacvalleyClient
                    .from('orders')
                    .select('*')
                    .in('status', ['processing', 'pending_instapay'])
                    .order('created_at', { ascending: false });

                if (error) throw error;

                const dismissedIds = JSON.parse(localStorage.getItem('dismissed_website_orders') || '[]');
                const filtered = (data || []).filter(o => !dismissedIds.includes(o.id) && o.crm !== 'imported' && o.crm !== 'dismissed');
                
                setPendingWebsiteOrders(filtered.filter(o => o.status === 'processing'));
                setPendingInstapayOrders(filtered.filter(o => o.status === 'pending_instapay'));
            } catch (err) {
                console.error('❌ Error fetching pending website orders:', err);
            }
        };

        fetchPendingOrders();

        // Real-time subscription on lilacvalley.orders
        const channel = lilacvalleyClient
            .channel('lilacvalley:orders:processing')
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'lilacvalley', table: 'orders' },
                (payload) => {
                    const status = payload.new.status;
                    if (status === 'processing' || status === 'pending_instapay') {
                        if (payload.new.crm === 'imported' || payload.new.crm === 'dismissed') {
                             setPendingWebsiteOrders(prev => prev.filter(o => o.id !== payload.new.id));
                             setPendingInstapayOrders(prev => prev.filter(o => o.id !== payload.new.id));
                             return;
                        }

                        const dismissedIds = JSON.parse(localStorage.getItem('dismissed_website_orders') || '[]');
                        if (!dismissedIds.includes(payload.new.id)) {
                            if (status === 'pending_instapay') {
                                 setTimeout(() => showAlert('💸 New Instapay Payment Pending! Check your account.'), 0);
                                 setPendingInstapayOrders(prev => [payload.new, ...prev.filter(o => o.id !== payload.new.id)]);
                            } else {
                                 setTimeout(() => showAlert('📦 New Website Order Ready to Import!'), 0);
                                 setPendingWebsiteOrders(prev => [payload.new, ...prev.filter(o => o.id !== payload.new.id)]);
                            }
                        }
                    }
                }
            )
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'lilacvalley', table: 'orders' },
                (payload) => {
                    const status = payload.new.status;
                    if (status === 'processing' || status === 'pending_instapay') {
                        if (payload.new.crm === 'imported' || payload.new.crm === 'dismissed') {
                             setPendingWebsiteOrders(prev => prev.filter(o => o.id !== payload.new.id));
                             setPendingInstapayOrders(prev => prev.filter(o => o.id !== payload.new.id));
                             return;
                        }

                        const dismissedIds = JSON.parse(localStorage.getItem('dismissed_website_orders') || '[]');
                        if (!dismissedIds.includes(payload.new.id)) {
                            if (status === 'pending_instapay') {
                                 setTimeout(() => showAlert('💸 Order shifted to Instapay! Verify payment.'), 0);
                                 setPendingInstapayOrders(prev => [payload.new, ...prev.filter(o => o.id !== payload.new.id)]);
                                 setPendingWebsiteOrders(prev => prev.filter(o => o.id !== payload.new.id));
                            } else {
                                 setTimeout(() => showAlert('📦 Order Ready to Import (Processing)!'), 0);
                                 setPendingWebsiteOrders(prev => [payload.new, ...prev.filter(o => o.id !== payload.new.id)]);
                                 setPendingInstapayOrders(prev => prev.filter(o => o.id !== payload.new.id));
                            }
                        }
                    } else {
                        setPendingWebsiteOrders(prev => prev.filter(o => o.id !== payload.new.id));
                        setPendingInstapayOrders(prev => prev.filter(o => o.id !== payload.new.id));
                    }
                }
            )
            .subscribe();

        return () => {
            lilacvalleyClient.removeChannel(channel);
        };
    }, [user, loading, showAlert]);

    // Memoize renderView to prevent unnecessary re-renders when switching tabs
    const renderView = useMemo(() => {
        if (!isPapaLoaded) {
            return <div className="text-center p-10 text-xl font-semibold text-gray-700">Loading data processing library... Please wait.</div>;
        }

        switch (view) {
            case 'Dashboard': return <SummaryView orders={data.orders} products={data.products} customers={data.customers} bottles={data.bottles} oils={data.oils} shippingFees={data.shipping} calculateBuildableStock={calculateBuildableStock} setData={setData} data={data} showAlert={showAlert} syncObjectData={syncObjectData} setShowLowStockAlert={setShowLowStockAlert} onCustomerClick={setSelectedCustomerForHistory} pendingWebsiteOrders={pendingWebsiteOrders} setShowPendingWebsiteModal={setShowPendingWebsiteModal} />;
            case 'Orders Board': return <OrdersBoard
                orders={data.orders}
                customers={activeCustomers}
                handleOrderAction={handleOrderAction}
                setView={setView}
                setSelectedOrder={setSelectedOrder}
                shippingFees={data.shipping}
                settings={data.settings}
                generalSettings={data.generalSettings}
                handleFileUpload={isAdmin ? handleFileUpload : undefined}
                setSelectedCustomerForHistory={setSelectedCustomerForHistory}
                pendingWebsiteOrders={pendingWebsiteOrders}
                onImportSingleOrder={(order) => {
                    setSingleOrderToImport(order);
                    setShowPendingWebsiteModal(true);
                }}
            />;
            case 'Deleted Orders': return <DeletedOrdersBoard orders={data.orders} customers={activeCustomers} handleOrderAction={handleOrderAction} setView={setView} setSelectedOrder={setSelectedOrder} shippingFees={data.shipping} settings={data.settings} generalSettings={data.generalSettings} handleFileUpload={isAdmin ? handleFileUpload : undefined} setSelectedCustomerForHistory={setSelectedCustomerForHistory} />;
            case 'New Order': return <NewOrderForm
                customers={activeCustomers}
                products={data.products}
                returnedPool={data.returnedPool}
                onSave={handleSaveOrder}
                setView={setView}
                bottles={data.bottles}
                oils={data.oils}
                shippingFees={data.shipping}
                calculateBuildableStock={calculateBuildableStock}
                onSaveCustomer={handleSaveCustomer}
                settings={data.settings}
                generalSettings={data.generalSettings}
                formatCurrency={formatCurrency}
                getProductCost={getProductCost}
                orders={data.orders}
                wooImportData={wooImportData}
                clearWooImportData={() => setWooImportData(null)}
            />;
            case 'New Customer': return <NewCustomerForm onSave={handleSaveCustomer} setView={setView} shippingFees={data.shipping} settings={data.settings} wooImportData={wooImportData} />;
            case 'Stock': return <StockView
                data={data}
                setData={setData}
                handleFileUpload={isAdmin ? handleFileUpload : undefined}
                getProductCost={getProductCost}
                calculateBuildableStock={calculateBuildableStock}
                settings={data.settings}
                generalSettings={data.generalSettings}
                showAlert={showAlert}
                setEditingSingleItem={setEditingSingleItem}
                formatCurrency={formatCurrency}
                syncSingleItem={syncSingleItem}
                syncMultipleItems={syncMultipleItems}
                syncObjectData={syncObjectData}
                handleDeleteOil={handleDeleteOil}
                handleDeleteBottle={handleDeleteBottle}
                handleDeleteVendor={handleDeleteVendor}
                checkLowStockThreshold={checkLowStockThreshold}
                isAdmin={isAdmin}
            />;
            case 'Customers': return <CustomersView data={data} setData={setData} setView={setView} handleFileUpload={isAdmin ? handleFileUpload : undefined} handleDeleteCustomer={handleDeleteCustomer} shippingFees={data.shipping} settings={data.settings} generalSettings={data.generalSettings} setEditingSingleItem={setEditingSingleItem} onCustomerClick={setSelectedCustomerForHistory} showAlert={showAlert} orders={data.orders} />;
            case 'Settings': return isAdmin ? <SettingsView data={data} setData={setData} showAlert={showAlert} getProductCost={getProductCost} syncSettings={syncSettings} syncMultipleItems={syncMultipleItems} syncObjectData={syncObjectData} /> : <div className="text-center p-10 text-xl font-semibold text-gray-700">Access Denied: Admin privileges required</div>;
            case 'Old Settings': return isAdmin ? <OldSettingsView data={data} setData={setData} showAlert={showAlert} syncSettings={syncSettings} /> : <div className="text-center p-10 text-xl font-semibold text-gray-700">Access Denied: Admin privileges required</div>;
            default: return <SummaryView orders={data.orders} products={data.products} customers={data.customers} bottles={data.bottles} oils={data.oils} shippingFees={data.shipping} calculateBuildableStock={calculateBuildableStock} setData={setData} data={data} showAlert={showAlert} syncObjectData={syncObjectData} setShowLowStockAlert={setShowLowStockAlert} />;
        }
    }, [view, isPapaLoaded, data, activeCustomers, isAdmin, handleOrderAction, setView, setSelectedOrder, setSelectedCustomerForHistory, handleSaveOrder, handleSaveCustomer, calculateBuildableStock, showAlert, setEditingSingleItem, formatCurrency, getProductCost, syncSingleItem, syncMultipleItems, syncObjectData, handleDeleteOil, handleDeleteBottle, handleDeleteVendor, handleFileUpload, handleCustomerUpdate, handleDeleteCustomer, syncSettings, setData]);

    const NavItem = ({ label, translationKey, icon, currentView, setView }) => {
        const iconName = data.settings.statusIcons[icon] || icon; // Get icon key from settings
        const isActive = currentView === label;
        const displayLabel = translationKey ? t(translationKey) : label;
        if (label === 'Leads') return null; // Remove Leads from nav
        return (
            <button onClick={() => { setView(label); setSidebarOpen(false); }} className={`flex items-center w-full px-4 py-3 text-left text-sm font-medium rounded-lg transition-colors ${isActive ? `bg-${data.settings.primaryColor}-700 text-white` : `text-${data.settings.primaryColor}-100 hover:bg-${data.settings.primaryColor}-500 hover:text-white`}`}>
                <Icon path={ICONS_MAP[iconName]} className={`w-5 h-5 ${isRTL ? 'ml-3' : 'mr-3'}`} /> {displayLabel}
            </button>
        );
    }

    // While AuthProvider is resolving the initial session (or restoring Remember Me),
    // show a spinner so we never flash the login page or redirect incorrectly.
    if (loading) {
        return (
            <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F3F4F6' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ width: '48px', height: '48px', border: '4px solid #E5E7EB', borderTopColor: '#4F46E5', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                    <p style={{ color: '#6B7280', fontSize: '14px', fontWeight: '500' }}>Loading…</p>
                </div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // Auth routing check
    if (!user) {
        if (location.pathname !== '/login') {
            return <Navigate to="/login" replace />;
        }
        return <Login onLogin={() => navigate('/dashboard')} />;
    } else if (location.pathname === '/login' || location.pathname === '/') {
        return <Navigate to="/dashboard" replace />;
    }

    // Show loading state while fetching initial data
    if (isLoadingFromSupabase) {
        return (
            <div className="flex h-screen items-center justify-center bg-gray-100">
                <div className="text-center">
                    <div className={`inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-${data.settings.primaryColor}-600 mb-4`}></div>
                    <p className="text-lg font-semibold text-gray-700">Loading data from database...</p>
                    <p className="text-sm text-gray-500 mt-2">Please wait</p>
                </div>
            </div>
        );
    }

    // Test function for Supabase sync panel
    const testSupabaseSync = async () => {
        const { customerServiceTracked } = await import('./services/customerServiceTracked');
        try {
            // Test fetch
            await customerServiceTracked.fetchCustomers();

            // Test insert
            const testCustomer = {
                id: `test_${Date.now()}`,
                name: 'Test Customer',
                mobile: '1234567890',
                email: 'test@example.com',
                state: 'Test State',
                city: 'Test City',
                orders: 0,
                completed: 0,
                cancels: [],
                status: 'active'
            };
            await customerServiceTracked.createCustomer(testCustomer);

            // Test update
            await customerServiceTracked.updateCustomer(testCustomer.id, { name: 'Updated Test Customer' });

            // Test delete
            await customerServiceTracked.deleteCustomer(testCustomer.id);

            alert('Supabase sync test completed! Check the sync panel.');
        } catch (error) {
            alert('Supabase sync test failed: ' + error.message);
        }
    };

    return (
        <div className="flex h-screen bg-gray-100 font-sans">
            <DynamicTheme primaryColor={data.settings.primaryColor} />
            {selectedOrder && <OrderDetailsModal order={selectedOrder} customer={activeCustomers.find(c => c.id === selectedOrder.customerid)} orders={data.orders} products={data.products} onUpdate={handleUpdateOrder} returnedPool={data.returnedPool} onClose={() => setSelectedOrder(null)} formatCurrency={(num) => formatCurrency(num, data.generalSettings.currency)} generalSettings={data.generalSettings} onCustomerClick={setSelectedCustomerForHistory} shippingFees={data.shipping} settings={data.settings} />}
            {selectedCustomerForHistory && <CustomerOrderHistoryModal customer={selectedCustomerForHistory} orders={data.orders} products={data.products} onClose={() => setSelectedCustomerForHistory(null)} generalSettings={data.generalSettings} settings={data.settings} onEditCustomer={(customer) => {
                setEditingSingleItem({ table: 'customers', data: customer });
                setSelectedCustomerForHistory(null);
            }} />}
            {alert.show && (<div className={`fixed top-5 ${isRTL ? 'left-5' : 'right-5'} bg-green-500 text-white py-2 px-4 rounded-lg shadow-lg z-50`}>{alert.message}</div>)}
            {/* Edit Single Item Modal */}
            {editingSingleItem && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h2 className="text-xl font-bold text-gray-800">{t('edit')} {editingSingleItem.table.slice(0, -1)} {t('item')}</h2>
                            <IconButton icon="close" color="gray" onClick={() => setEditingSingleItem(null)} tooltip={t('close')} />
                        </div>
                        <div className="p-6 overflow-y-auto">
                            {editingSingleItem.table === 'products' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('productName')}</label>
                                        <input type="text" value={editData.product || ''} onChange={e => setEditData(prev => ({ ...prev, product: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('category')}</label>
                                        <select value={editData.gender || ''} onChange={e => setEditData(prev => ({ ...prev, gender: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                                            {[...new Set(data.bottles.map(b => b.gender))].filter(Boolean).sort().map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('size')}</label>
                                        <select value={editData.size || ''} onChange={e => {
                                            const newSize = e.target.value;
                                            setEditData(prev => {
                                                const newData = { ...prev, size: newSize };
                                                const matchingOil = data.oils.find(o => o.oil === newData.oil);
                                                if (matchingOil && newSize) {
                                                    const sizeNum = parseInt(newSize);
                                                    const defaultField = `oil_per_${sizeNum}ml`;
                                                    if (matchingOil[defaultField]) {
                                                        newData.qtyoilinbottle = matchingOil[defaultField];
                                                    }
                                                }
                                                return newData;
                                            });
                                        }} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                                            {[...new Set(data.bottles.map(b => b.size))].filter(Boolean).sort().map(size => (
                                                <option key={size} value={size}>{size}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('productImage')}</label>
                                        {editData.imageurl && (
                                            <img src={editData.imageurl} alt="Current" className="w-20 h-20 rounded-lg object-cover border mb-2" onError={e => e.target.style.display = 'none'} />
                                        )}
                                        <ProductImagePicker
                                            currentUrl={editData.imageurl || ''}
                                            onSelect={url => setEditData(prev => ({ ...prev, imageurl: url }))}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('price')}</label>
                                        <input type="number" value={editData.price || ''} onChange={e => setEditData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('oilQty')}</label>
                                        <input type="number" value={editData.qtyoilinbottle || ''} onChange={e => setEditData(prev => ({ ...prev, qtyoilinbottle: parseFloat(e.target.value) || 0 }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('oil')}</label>
                                        <select value={editData.oil || ''} onChange={e => {
                                            const newOil = e.target.value;
                                            setEditData(prev => {
                                                const newData = { ...prev, oil: newOil };
                                                const matchingOil = data.oils.find(o => o.oil === newOil);
                                                if (matchingOil && newData.size) {
                                                    const sizeNum = parseInt(newData.size);
                                                    const defaultField = `oil_per_${sizeNum}ml`;
                                                    if (matchingOil[defaultField]) {
                                                        newData.qtyoilinbottle = matchingOil[defaultField];
                                                    }
                                                }
                                                return newData;
                                            });
                                        }} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                                            <option value="">{t('selectOil')}</option>
                                            {data.oils.map(o => <option key={o.oil} value={o.oil}>{o.oil}</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}
                            {editingSingleItem.table === 'oils' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('oilName')}</label>
                                        <input type="text" value={editData.oil || ''} onChange={e => setEditData(prev => ({ ...prev, oil: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('vendor') || 'Vendor'}</label>
                                        <select value={editData.vendor_id || ''} onChange={e => setEditData(prev => ({ ...prev, vendor_id: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                                            <option value="">{t('selectVendor') || 'Select Vendor'}</option>
                                            {data.vendors?.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('pricePerMl')}</label>
                                        <input type="number" value={editData.price || ''} onChange={e => setEditData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('stockMl')}</label>
                                        <input type="number" value={editData.actualstock || ''} onChange={e => setEditData(prev => ({ ...prev, actualstock: parseInt(e.target.value) || 0 }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('oilType')}</label>
                                        <select value={editData.oiltype || ''} onChange={e => setEditData(prev => ({ ...prev, oiltype: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                                            {data.stockSettings.oilCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                        </select>
                                    </div>
                                    <div className="border-t pt-4 mt-4">
                                        <h3 className="text-sm font-bold text-gray-800 mb-3">{t('oilPerBottleDefaults') || 'Oil Qty per Bottle Size (ml)'}</h3>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-500 uppercase">5ml</label>
                                                <input type="number" value={editData.oil_per_5ml || 0} onChange={e => setEditData(prev => ({ ...prev, oil_per_5ml: parseInt(e.target.value) }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-500 uppercase">20ml</label>
                                                <input type="number" value={editData.oil_per_20ml || 0} onChange={e => setEditData(prev => ({ ...prev, oil_per_20ml: parseInt(e.target.value) }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-500 uppercase">30ml</label>
                                                <input type="number" value={editData.oil_per_30ml || 0} onChange={e => setEditData(prev => ({ ...prev, oil_per_30ml: parseInt(e.target.value) }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-500 uppercase">50ml</label>
                                                <input type="number" value={editData.oil_per_50ml || 0} onChange={e => setEditData(prev => ({ ...prev, oil_per_50ml: parseInt(e.target.value) }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-500 uppercase">100ml</label>
                                                <input type="number" value={editData.oil_per_100ml || 0} onChange={e => setEditData(prev => ({ ...prev, oil_per_100ml: parseInt(e.target.value) }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {editingSingleItem.table === 'bottles' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('id')}</label>
                                        <input type="text" value={editData.id || ''} onChange={e => setEditData(prev => ({ ...prev, id: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('size')}</label>
                                        <input type="text" value={editData.size || ''} onChange={e => setEditData(prev => ({ ...prev, size: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('gender')}</label>
                                        <input type="text" value={editData.gender || ''} onChange={e => setEditData(prev => ({ ...prev, gender: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('stock')}</label>
                                        <input type="number" value={editData.actualstock || ''} onChange={e => setEditData(prev => ({ ...prev, actualstock: parseInt(e.target.value) || 0 }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('totalCost')}</label>
                                        <input type="number" value={editData.totalcostbottle || ''} onChange={e => setEditData(prev => ({ ...prev, totalcostbottle: parseFloat(e.target.value) || 0 }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('imageUrl')}</label>
                                        {editData.imageurl && (
                                            <img src={editData.imageurl} alt="Current" className="w-20 h-20 rounded-lg object-cover border mb-2" onError={e => e.target.style.display = 'none'} />
                                        )}
                                        <ProductImagePicker
                                            currentUrl={editData.imageurl || ''}
                                            bucket="Pictures"
                                            onSelect={url => setEditData(prev => ({ ...prev, imageurl: url }))}
                                        />
                                    </div>
                                </div>
                            )}
                            {editingSingleItem.table === 'customers' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('name')}</label>
                                        <input type="text" value={editData.name || ''} onChange={e => setEditData(prev => ({ ...prev, name: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('mobile')}</label>
                                        <input type="text" value={editData.mobile || ''} onChange={e => setEditData(prev => ({ ...prev, mobile: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('email')}</label>
                                        <input type="text" value={editData.email || ''} onChange={e => setEditData(prev => ({ ...prev, email: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('state')}</label>
                                        <select value={editData.state || ''} onChange={e => setEditData(prev => ({ ...prev, state: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                                            {data.shipping.map(s => <option key={s.state} value={s.state}>{s.state}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('city')}</label>
                                        <select value={editData.city || ''} onChange={e => setEditData(prev => ({ ...prev, city: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                                            {data.shipping.filter(s => s.state === editData.state).map(s => <option key={s.city} value={s.city}>{s.city}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('address')}</label>
                                        <input type="text" value={editData.address || ''} onChange={e => setEditData(prev => ({ ...prev, address: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                </div>
                            )}
                            {editingSingleItem.table === 'shipping' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('state')}</label>
                                        <select value={editData.state || ''} onChange={e => setEditData(prev => ({ ...prev, state: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`}>
                                            <option value="">{t('selectState')}</option>
                                            {[...new Set(data.shipping.map(s => s.state))].map(state => <option key={state} value={state}>{state}</option>)}
                                            <option value="__new__">{t('addNewState')}</option>
                                        </select>
                                        {editData.state === '__new__' && (
                                            <input type="text" placeholder={t('enterNewStateName')} onChange={e => setEditData(prev => ({ ...prev, state: e.target.value }))} className={`w-full p-2 border rounded-md mt-2 ${FOCUSED_INPUT_STYLE}`} />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('city')}</label>
                                        <input type="text" value={editData.city || ''} onChange={e => setEditData(prev => ({ ...prev, city: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('fees')} ({getCurrencySymbol(data.generalSettings.currency)})</label>
                                        <input type="number" value={editData.fees || ''} onChange={e => setEditData(prev => ({ ...prev, fees: parseFloat(e.target.value) || 0 }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                </div>
                            )}
                            {editingSingleItem.table === 'vendors' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('vendorName') || 'Vendor Name'}</label>
                                        <input type="text" value={editData.name || ''} onChange={e => setEditData(prev => ({ ...prev, name: e.target.value }))} className={`w-full p-2 border rounded-md ${FOCUSED_INPUT_STYLE}`} />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
                            <Button onClick={() => setEditingSingleItem(null)} color="gray">{t('cancel')}</Button>
                            <Button onClick={async () => {
                                const tableName = editingSingleItem.table;

                                console.log(`📝 [EDIT MODAL] Starting save for ${tableName}`, editData);

                                // Prepare the item to sync BEFORE setData
                                let preparedItem = null;
                                let modifiedProducts = [];
                                if (tableName === 'products') {
                                    preparedItem = { ...editData, costproduct: getProductCost(editData) };
                                } else {
                                    preparedItem = { ...editData };
                                    
                                    // If oil is updated, check if any product's qtyoilinbottle should change
                                    if (tableName === 'oils') {
                                        data.products.forEach(p => {
                                            if (p.oil === preparedItem.oil) {
                                                const bottleInfo = data.bottles.find(b => b.size === p.size && b.gender === p.gender);
                                                if (bottleInfo) {
                                                    const sizeStr = bottleInfo.size?.replace(/[^0-9]/g, '');
                                                    if (sizeStr) {
                                                        const defaultField = `oil_per_${sizeStr}ml`;
                                                        if (preparedItem[defaultField] !== undefined && p.qtyoilinbottle !== preparedItem[defaultField]) {
                                                            const updatedProduct = { ...p, qtyoilinbottle: preparedItem[defaultField] };
                                                            const oilCost = (parseFloat(preparedItem.price) || 0) * (parseFloat(updatedProduct.qtyoilinbottle) || 0);
                                                            const bottleCost = parseFloat(bottleInfo.totalcostbottle) || 0;
                                                            updatedProduct.costproduct = oilCost + bottleCost;
                                                            modifiedProducts.push(updatedProduct);
                                                        }
                                                    }
                                                }
                                            }
                                        });
                                    }
                                }

                                console.log(`? [EDIT MODAL] Prepared item:`, preparedItem);
                                if (modifiedProducts.length > 0) {
                                    console.log(`? [EDIT MODAL] Also updated ${modifiedProducts.length} related products`);
                                }

                                // Update local state - CREATE NEW ARRAYS to trigger React re-render
                                setData(prev => {
                                    if (tableName === 'products') {
                                        const index = prev.products.findIndex(p => p.product === editingSingleItem.data.product);
                                        if (index > -1) {
                                            const newProducts = [...prev.products];
                                            newProducts[index] = preparedItem;
                                            return { ...prev, products: newProducts };
                                        }
                                    } else if (tableName === 'oils') {
                                        const index = prev.oils.findIndex(o => o.oil === editingSingleItem.data.oil);
                                        if (index > -1) {
                                            const newOils = [...prev.oils];
                                            newOils[index] = preparedItem;
                                            let newProducts = prev.products;
                                            if (modifiedProducts.length > 0) {
                                                newProducts = prev.products.map(p => {
                                                    const updated = modifiedProducts.find(mp => mp.id === p.id);
                                                    return updated || p;
                                                });
                                            }
                                            return { ...prev, oils: newOils, products: newProducts };
                                        }
                                    } else if (tableName === 'bottles') {
                                        const index = prev.bottles.findIndex(b => b.id === editingSingleItem.data.id);
                                        if (index > -1) {
                                            const newBottles = [...prev.bottles];
                                            newBottles[index] = preparedItem;
                                            console.log(`🔄 [EDIT MODAL] Creating new bottles array, updated item at index ${index}`);
                                            return { ...prev, bottles: newBottles };
                                        }
                                    } else if (tableName === 'customers') {
                                        const index = prev.customers.findIndex(c => c.id === editingSingleItem.data.id);
                                        if (index > -1) {
                                            const newCustomers = [...prev.customers];
                                            newCustomers[index] = preparedItem;
                                            return { ...prev, customers: newCustomers };
                                        }
                                    } else if (tableName === 'shipping') {
                                        const index = prev.shipping.findIndex(s => s.id === editingSingleItem.data.id);
                                        if (index > -1) {
                                            const newShipping = [...prev.shipping];
                                            newShipping[index] = preparedItem;
                                            return { ...prev, shipping: newShipping };
                                        } else {
                                            return { ...prev, shipping: [...prev.shipping, preparedItem] };
                                        }
                                    } else if (tableName === 'vendors') {
                                        const index = prev.vendors?.findIndex(v => v.id === editingSingleItem.data.id) ?? -1;
                                        if (index > -1) {
                                            const newVendors = [...prev.vendors];
                                            newVendors[index] = preparedItem;
                                            return { ...prev, vendors: newVendors };
                                        } else {
                                            return { ...prev, vendors: [...(prev.vendors || []), preparedItem] };
                                        }
                                    }

                                    return prev;
                                });

                                console.log(`⏱️ [EDIT MODAL] State update queued, syncing to Supabase...`);

                                // Sync to Supabase - use the prepared item
                                if (preparedItem) {
                                    console.log(`🔄 [EDIT MODAL] Calling syncSingleItem for ${tableName}...`);
                                    try {
                                        await syncSingleItem(tableName, preparedItem);
                                        if (modifiedProducts && modifiedProducts.length > 0) {
                                            console.log(`?? [EDIT MODAL] Syncing ${modifiedProducts.length} related products...`);
                                            await syncMultipleItems('products', modifiedProducts);
                                        }
                                        console.log(`✅ [EDIT MODAL] Sync completed successfully for ${tableName}`);
                                    } catch (err) {
                                        console.error(`❌ [EDIT MODAL] Sync failed for ${tableName}:`, err);
                                        showAlert(`Error syncing ${tableName}: ${err.message}`);
                                        return; // Don't close modal if sync failed
                                    }
                                } else {
                                    console.warn(`⚠️ [EDIT MODAL] No preparedItem found for ${tableName}`);
                                }

                                setEditingSingleItem(null);
                                showAlert(t('itemUpdated'));
                            }} color="green">{t('save')}</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Low Stock Alert Modal */}
            {showLowStockAlert && (() => {
                const lowOils = data.oils.filter(o => safeCoerceNumber(o.actualstock) < data.stockSettings.lowStockThresholds.oils);
                const lowBottles = data.bottles.filter(b => safeCoerceNumber(b.actualstock) < data.stockSettings.lowStockThresholds.bottles);
                const primaryColor = data.settings?.primaryColor || 'indigo';

                return (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
                        <div className="bg-white rounded-[1.5rem] shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col border border-gray-100">
                            <div className={`p-6 border-b border-${primaryColor}-100 flex items-center justify-between bg-${primaryColor}-50`}>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-${primaryColor}-100 shadow-sm">
                                        <Icon path={ICONS_MAP['warning']} className={`w-5 h-5 text-${primaryColor}-500`} />
                                    </div>
                                    <h2 className={`text-xl font-bold text-${primaryColor}-800`}>{t('lowStockAlert')}</h2>
                                </div>
                                <button
                                    onClick={() => setShowLowStockAlert(false)}
                                    className={`text-${primaryColor}-400 hover:text-${primaryColor}-600 hover:bg-neutral-100 p-2 rounded-full transition-colors`}
                                >
                                    <Icon path={ICONS_MAP['close']} className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
                                {(lowOils.length === 0 && lowBottles.length === 0) && (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 font-medium">{t('noLowStockAlerts')}</p>
                                    </div>
                                )}

                                <div className="space-y-6">
                                    {lowOils.length > 0 && (
                                        <div>
                                            <h4 className="text-xs font-bold text-red-700 uppercase tracking-wider mb-3 px-3 py-1.5 rounded-lg bg-red-50">{t('oilsThreshold', { threshold: data.stockSettings.lowStockThresholds.oils })}</h4>
                                            <div className="space-y-3">
                                                {lowOils.map(o => {
                                                    const threshold = data.stockSettings.lowStockThresholds.oils;
                                                    const current = safeCoerceNumber(o.actualstock);
                                                    const percent = Math.min(100, Math.max(0, (current / threshold) * 80));
                                                    const isCritical = current <= (threshold * 0.5);

                                                    return (
                                                        <div key={o.oil} className="rounded-xl p-4 shadow-md" style={{ backgroundColor: '#fcf9f9', boxShadow: '0 4px 12px rgba(0,0,0,0.10)' }}>
                                                            <div className="flex justify-between items-center mb-2">
                                                                <span className="font-bold text-gray-900 text-sm">{o.oil}</span>
                                                                <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${isCritical ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                                                                    {isCritical ? t('critical') : t('low')}
                                                                </span>
                                                            </div>
                                                            <div className="w-full bg-gray-200 rounded-full h-2 mb-2 overflow-hidden">
                                                                <div className={`h-2 rounded-full ${isCritical ? 'bg-red-500' : 'bg-amber-500'}`} style={{ width: `${percent}%` }}></div>
                                                            </div>
                                                            <div className="flex justify-between text-xs font-medium text-gray-600">
                                                                <span className={isCritical ? 'text-red-600 font-bold' : ''}>{current} {t('ml')} {t('remaining')}</span>
                                                                <span>{t('threshold')}: {threshold} {t('ml')}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {lowBottles.length > 0 && (
                                        <div>
                                            <h4 className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-3 px-3 py-1.5 rounded-lg bg-amber-50">{t('bottlesThreshold', { threshold: data.stockSettings.lowStockThresholds.bottles })}</h4>
                                            <div className="space-y-3">
                                                {lowBottles.map(b => {
                                                    const threshold = data.stockSettings.lowStockThresholds.bottles;
                                                    const current = safeCoerceNumber(b.actualstock);
                                                    const percent = Math.min(100, Math.max(0, (current / threshold) * 80));
                                                    const isCritical = current <= (threshold * 0.5);

                                                    return (
                                                        <div key={b.id} className="rounded-xl p-4 shadow-md" style={{ backgroundColor: '#fcf9f9', boxShadow: '0 4px 12px rgba(0,0,0,0.10)' }}>
                                                            <div className="flex justify-between items-center mb-2">
                                                                <span className="font-bold text-gray-900 text-sm">{b.size} {b.gender}</span>
                                                                <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${isCritical ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                                                                    {isCritical ? t('critical') : t('low')}
                                                                </span>
                                                            </div>
                                                            <div className="w-full bg-gray-200 rounded-full h-2 mb-2 overflow-hidden">
                                                                <div className={`h-2 rounded-full ${isCritical ? 'bg-red-500' : 'bg-amber-500'}`} style={{ width: `${percent}%` }}></div>
                                                            </div>
                                                            <div className="flex justify-between text-xs font-medium text-gray-600">
                                                                <span className={isCritical ? 'text-red-600 font-bold' : ''}>{current} {t('units')} {t('remaining')}</span>
                                                                <span>{t('threshold')}: {threshold} {t('units')}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="p-4 border-t border-gray-100 flex justify-end gap-3 bg-white">
                                <Button onClick={() => setShowLowStockAlert(false)} color="gray" className="bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 shadow-sm">
                                    {t('close')}
                                </Button>
                                <Button onClick={() => {
                                    setShowLowStockAlert(false);
                                    setView('Stock');
                                }} color={primaryColor} className="shadow-md text-sm px-4">
                                    {t('goToStock')}
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            <aside ref={sidebarRef} className={`hidden lg:flex lg:flex-col lg:relative w-64 bg-${data.settings.primaryColor}-600 shadow-lg z-30 p-4 transition-transform duration-200 ease-in-out`}>
                <div className="flex items-center gap-3 mb-8">
                    <img src={data.settings.logoUrl} alt="Store Logo" className="w-16 h-16 rounded-full flex-shrink-0" onError={(e) => e.target.src = initialData.settings.logoUrl} />
                    <div className="flex-1 min-w-0">
                        <h1 className="text-xl font-bold text-white truncate">{data.generalSettings.storeName}</h1>
                        {isAdmin && <Badge color="bg-red-100 text-red-800 mt-1">Admin</Badge>}
                    </div>
                </div>
                <nav className="flex-1 space-y-2">
                    {((pendingWebsiteOrders || []).length + (pendingInstapayOrders || []).length) > 0 && (
                        <button onClick={() => setShowPendingWebsiteModal(true)} className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors bg-red-500 text-white font-bold hover:bg-red-600 rounded-md shadow-sm mb-2`}>
                            <span className="flex items-center flex-1 min-w-0 pr-2">
                                <span className="text-lg mr-3 flex-shrink-0">🌐</span>
                                <span className="truncate">New Website Orders</span>
                            </span>
                            <span className="bg-white text-red-600 py-0.5 px-2 rounded-full text-xs flex-shrink-0 animate-pulse">{(pendingWebsiteOrders || []).length + (pendingInstapayOrders || []).length}</span>
                        </button>
                    )}
                    <NavItem label="Dashboard" translationKey="dashboard" icon="dashboard" currentView={view} setView={setView} />
                    <NavItem label="Orders Board" translationKey="ordersBoard" icon="ordered" currentView={view} setView={setView} />
                    <NavItem label="Stock" translationKey="stockManagement" icon="stock" currentView={view} setView={setView} />
                    <NavItem label="Customers" translationKey="customersAndShipping" icon="customers" currentView={view} setView={setView} />
                    {isAdmin && <NavItem label="Settings" translationKey="settings" icon="settings" currentView={view} setView={setView} />}
                </nav>
                <div className="p-4 border-t space-y-2">
                    {isPushSupported && (
                        <div className="py-3 px-2 border-b border-white/10 mb-4 bg-black/5 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Icon path={ICONS_MAP['warning']} className="w-4 h-4 text-white/70" />
                                    <span className="text-xs font-bold text-white uppercase tracking-wider">{t('notifications') || 'Notifications'}</span>
                                </div>
                                <button
                                    onClick={isPushSubscribed ? unsubscribeFromPush : subscribeToPush}
                                    disabled={isPushLoading}
                                    className={`relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isPushSubscribed ? 'bg-green-500' : 'bg-white/20'}`}
                                    title={isPushSubscribed ? 'Disable Notifications' : 'Enable Notifications'}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isPushSubscribed ? (isRTL ? '-translate-x-5' : 'translate-x-5') : 'translate-x-0'}`} />
                                </button>
                            </div>
                            
                            {isPushSubscribed && (
                                <button 
                                    onClick={sendTestNotification}
                                    className="w-full py-1.5 px-2 bg-white/10 hover:bg-white/20 text-[10px] font-bold text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                                >
                                    <Icon path={ICONS_MAP['dashboard']} className="w-3 h-3" />
                                    {t('testNotification') || 'Send Test Notification'}
                                </button>
                            )}

                            {pushError && (
                                <div className="mt-2 p-2 bg-red-500/20 border border-red-500/30 rounded-lg text-[10px] text-red-100 flex flex-col gap-1">
                                    <p>{pushError}</p>
                                    <button onClick={clearPushError} className="underline text-left opacity-70 hover:opacity-100">Dismiss</button>
                                </div>
                            )}
                        </div>
                    )}
                    <div className={`text-xs text-${data.settings.primaryColor}-200 mb-2`}>
                        <div className="font-medium truncate">User: {user?.email || 'None'}</div>
                    </div>
                    <Button onClick={toggleLanguage} color="gray" className="w-full mb-2">
                        {language === 'en' ? 'English' : 'عربي'}
                    </Button>
                    <Button onClick={signOut} color="gray" className="w-full">
                        {t('logout')}
                    </Button>
                </div>
            </aside>
            <div className="flex-1 flex flex-col overflow-hidden">
                <header className={`flex justify-between items-center p-4 bg-white border-b lg:hidden sticky top-0 z-20 shadow-sm`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <img src={data.settings.logoUrl} alt="Store Logo" className="w-10 h-10 rounded-full flex-shrink-0" onError={(e) => e.target.src = initialData.settings.logoUrl} />
                        <h1 className={`text-xl font-extrabold text-${data.settings.primaryColor}-700 truncate tracking-tight`}>{data.generalSettings.storeName}</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        {((pendingWebsiteOrders || []).length + (pendingInstapayOrders || []).length) > 0 && (
                            <button onClick={() => setShowPendingWebsiteModal(true)} className={`relative text-${data.settings.primaryColor}-600 focus:outline-none bg-${data.settings.primaryColor}-50 p-2 rounded-full shadow-sm`}>
                                <span className="text-lg">🌐</span>
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm animate-pulse">{(pendingWebsiteOrders || []).length + (pendingInstapayOrders || []).length}</span>
                            </button>
                        )}

                        {/* Language Selector */}
                        <button onClick={toggleLanguage} className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-700 font-bold text-xs hover:bg-gray-200 transition-colors">
                            {language === 'en' ? 'EN' : 'ع'}
                        </button>

                        {/* Settings Dropdown */}
                        <div className="relative">
                            <button onClick={() => setShowTopSettings(!showTopSettings)} className={`text-gray-500 hover:text-${data.settings.primaryColor}-600 focus:outline-none transition-colors ${(view === 'Settings' || showTopSettings) ? `text-${data.settings.primaryColor}-600` : ''}`}>
                                <Icon path={ICONS_MAP['settings']} className="w-6 h-6" />
                            </button>

                            {showTopSettings && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setShowTopSettings(false)}></div>
                                    <div className={`absolute top-full ${isRTL ? 'left-0' : 'right-0'} mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden`}>
                                        <div className="p-2 space-y-1">
                                            {isAdmin && (
                                                <button onClick={() => { setView('Settings'); setShowTopSettings(false); }} className={`w-full text-${isRTL ? 'right' : 'left'} px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-2`}>
                                                    <Icon path={ICONS_MAP['settings']} className="w-4 h-4 text-gray-400" />
                                                    {t('appSettings') || 'App Settings'}
                                                </button>
                                            )}

                                            {isPushSupported && (
                                                <div className="px-3 py-3 hover:bg-gray-50 rounded-lg border-t border-gray-100 mt-1">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                                            <Icon path={ICONS_MAP['warning']} className="w-4 h-4 text-indigo-500" />
                                                            {t('notifications') || 'Notifications'}
                                                        </span>
                                                        <button
                                                            onClick={isPushSubscribed ? unsubscribeFromPush : subscribeToPush}
                                                            disabled={isPushLoading}
                                                            className={`relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isPushSubscribed ? `bg-green-500` : 'bg-gray-200'}`}
                                                        >
                                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isPushSubscribed ? (isRTL ? '-translate-x-5' : 'translate-x-5') : 'translate-x-0'}`} />
                                                        </button>
                                                    </div>
                                                    {isPushSubscribed && (
                                                        <button 
                                                            onClick={sendTestNotification}
                                                            className="w-full py-2 px-3 bg-indigo-50 hover:bg-indigo-100 text-xs font-bold text-indigo-600 rounded-lg transition-colors flex items-center justify-center gap-2"
                                                        >
                                                            <Icon path={ICONS_MAP['dashboard']} className="w-3.5 h-3.5" />
                                                            {t('testNotification') || 'Send Test Notification'}
                                                        </button>
                                                    )}
                                                    {pushError && (
                                                        <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded-lg text-[10px] text-red-600">
                                                            {pushError}
                                                            <button onClick={clearPushError} className="block mt-1 underline">Dismiss</button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Profile/Logout */}
                        <div className="relative">
                            <button onClick={signOut} className={`flex items-center justify-center w-8 h-8 rounded-full bg-${data.settings.primaryColor}-100 text-${data.settings.primaryColor}-700 hover:bg-${data.settings.primaryColor}-200 transition-colors`} title="Logout">
                                <Icon path={ICONS_MAP['customers']} className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </header>
                <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-4 md:p-6 pb-20 lg:pb-6">
                    <Routes>
                        <Route path="/login" element={<Login onLogin={() => navigate('/dashboard')} />} />
                        <Route path="/dashboard" element={renderView} />
                        <Route path="/inventory" element={renderView} />
                        <Route path="/clients" element={renderView} />
                        <Route path="/clients/new" element={renderView} />
                        <Route path="/leads" element={renderView} />
                        <Route path="/orders" element={renderView} />
                        <Route path="/orders/new" element={renderView} />
                        <Route path="/orders/deleted" element={renderView} />
                        <Route path="/settings" element={renderView} />
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                </main>
                {/* Mobile Bottom Navigation */}
                <nav className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-gray-200 flex justify-around items-center px-1 pb-safe z-30 shadow-[0_-2px_10px_rgba(0,0,0,0.1)] text-xs h-16">
                    <BottomNavItem label={t('dashboard')} icon="dashboard" currentView={view} setView={setView} target="Dashboard" primaryColor={data.settings.primaryColor} />
                    <BottomNavItem label={t('ordersBoard')} icon="ordered" currentView={view} setView={setView} target="Orders Board" primaryColor={data.settings.primaryColor} />
                    <BottomNavItem label={t('stockManagement')} icon="stock" currentView={view} setView={setView} target="Stock" primaryColor={data.settings.primaryColor} />
                    <BottomNavItem label={t('customersAndShipping')} icon="customers" currentView={view} setView={setView} target="Customers" primaryColor={data.settings.primaryColor} />
                </nav>
            </div>

            <PendingWooOrdersModal
                isOpen={showPendingWebsiteModal}
                onClose={() => {
                    setShowPendingWebsiteModal(false);
                    setSingleOrderToImport(null);
                }}
                processingOrders={singleOrderToImport ? [singleOrderToImport] : pendingWebsiteOrders}
                instapayOrders={pendingInstapayOrders}
                products={data.products}
                customers={data.customers}
                bottles={data.bottles}
                oils={data.oils}
                shippingFees={data.shipping}
                generalSettings={data.generalSettings}
                settings={data.settings}
                formatCurrency={formatCurrency}
                calculateBuildableStock={calculateBuildableStock}
                getProductCost={getProductCost}
                onSaveCustomer={handleSaveCustomer}
                onSaveOrder={handleSaveOrder}
                showAlert={showAlert}
                setView={setView}
                setWooImportData={setWooImportData}
                onOrderImported={(id) => {
                    setPendingWebsiteOrders(prev => prev.filter(o => o.id !== id));
                    setPendingInstapayOrders(prev => prev.filter(o => o.id !== id));
                }}
                onStatusUpdate={async (id, newStatus) => {
                    try {
                         const { lilacvalleyClient } = await import('./lib/api/lilacvalleyClient');
                         const { error } = await lilacvalleyClient.from('orders').update({ status: newStatus }).eq('id', id);
                         if (error) throw error;
                         showAlert(`Order #${id} marked as ${newStatus}`);
                    } catch (e) {
                         console.error('Failed to update status', e);
                         showAlert('Failed to update status');
                    }
                }}
            />
        </div>
    );
}
